import { useEffect, useRef, useState } from "react";
import axios from "axios";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import { FaChevronRight } from "react-icons/fa6";
import { FaChevronLeft } from "react-icons/fa6";
import { FcAddImage } from "react-icons/fc";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { Check } from "lucide-react";
import { MdDelete } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import { toast } from "react-toastify";
import Cropper from "react-easy-crop";
import { Eye } from "lucide-react";
import { API_URL } from "../../Config";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Converts a jpg/png/etc File into a webp File entirely in the browser
// (decode via createImageBitmap, encode via canvas.toBlob). If the file is
// already webp it's returned untouched. Nothing is uploaded or downloaded —
// this only happens in memory.
const WEBP_QUALITY = 0.85;

async function toWebpFile(file, quality = WEBP_QUALITY) {
  if (!file || file.type === "image/webp") return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch (err) {
    // Fallback for browsers without createImageBitmap support
    bitmap = await new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read image"));
      };
      img.src = url;
    });
  }

  const width = bitmap.width || bitmap.naturalWidth;
  const height = bitmap.height || bitmap.naturalHeight;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0);
  if (typeof bitmap.close === "function") bitmap.close();

  const webpBlob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("webp encode failed")),
      "image/webp",
      quality,
    );
  });

  const newName = file.name.replace(/\.[^./\\]+$/, "") + ".webp";
  return new File([webpBlob], newName, { type: "image/webp" });
}

function CroppedBanner({ imageurl, cropdata }) {
  if (!cropdata || !cropdata.width || !cropdata.height) {
    return <img src={imageurl} className="w-full h-full object-cover" />;
  }
  const { x, y, width, height } = cropdata;
  return (
    <div
      className="w-full h-full"
      style={{
        backgroundImage: `url(${imageurl})`,
        backgroundSize: `${10000 / width}% ${10000 / height}%`,
        backgroundPosition: `${x / (1 - width / 100) || 0}% ${y / (1 - height / 100) || 0}%`,
        backgroundRepeat: "no-repeat",
      }}
    />
  );
}

function SortableImage({
  img,
  index,
  isSelected,
  isGroupFollower,
  onToggle,
  onView,
  category,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: index.toString() });
  const routerLocation = useLocation();
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();

  const propertyid =
    routerLocation.state?.propertyid ?? searchParams.get("id") ?? paramId;

  const isViewMode =
    routerLocation.state?.mode === "view" ||
    searchParams.get("mode") === "view";

  const hasCategory = !!category; // NEW

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : isGroupFollower ? 0.3 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={() => {
        if (!isViewMode) {
          onToggle(index);
        }
      }}
      className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-colors group ${
        isSelected
          ? "border-green-900 ring-2 ring-green-900/40"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <img
        src={img.url}
        alt={`Photo ${index + 1}`}
        className="w-full h-full object-cover pointer-events-none"
      />

      {/* NEW: whitish overlay for already-tagged images */}
      {hasCategory && (
        <div className="absolute inset-0 bg-white/45 pointer-events-none" />
      )}

      {/* NEW: small "Tagged" badge so it's obvious why it's faded
      {hasCategory && (
        <div className="absolute top-1.5 right-8 z-10 bg-green-800/90 text-white text-[9px] font-medium px-1.5 py-0.5 rounded pointer-events-none">
          Tagged
        </div>
      )} */}

      {/* Position + Category at bottom */}
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 flex justify-between z-10">
        <span>#{index + 1}</span>
        {category && <span className="truncate ml-2">{category}</span>}
      </div>

      {/* Drag handle */}
      {!isViewMode && (
        <div
          {...listeners}
          className="absolute top-1.5 left-1/2 -translate-x-1/2 bg-white/80 rounded px-2 py-0.5 text-[10px] font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10"
          onClick={(e) => e.stopPropagation()}
        >
          ⠿ Drag
        </div>
      )}

      {/* View button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onView({ url: img.url, label: `Photo ${index + 1}` });
        }}
        className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-white/80 border border-gray-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
      >
        <Eye size={12} strokeWidth={2.5} />
      </button>

      {/* Selection checkbox */}
      {!isViewMode && (
        <div
          className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center border z-10 ${
            isSelected
              ? "bg-green-500 border-green-900 text-white"
              : "bg-white/80 border-gray-300"
          }`}
        >
          {isSelected && <Check size={12} strokeWidth={3} />}
        </div>
      )}
    </div>
  );
}
// function SortableImage({
//   img,
//   index,
//   isSelected,
//   isGroupFollower,
//   onToggle,
//   onView,
//   category,
// }) {
//   const {
//     attributes,
//     listeners,
//     setNodeRef,
//     transform,
//     transition,
//     isDragging,
//   } = useSortable({ id: index.toString() });
//   // const routerLocation = useLocation();
//   // const { id: paramId } = useParams();
//   // const propertyid = routerLocation.state?.propertyid ?? paramId;
//   // const isViewMode = routerLocation.state?.mode === "view";
//   const routerLocation = useLocation();
//   const { id: paramId } = useParams();
//   const [searchParams] = useSearchParams();

//   const propertyid =
//     routerLocation.state?.propertyid ?? searchParams.get("id") ?? paramId;

//   const isViewMode =
//     routerLocation.state?.mode === "view" ||
//     searchParams.get("mode") === "view";
//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//     opacity: isDragging ? 0.3 : isGroupFollower ? 0.3 : 1,
//     zIndex: isDragging ? 10 : 1,
//   };

//   return (
//     <div
//       ref={setNodeRef}
//       style={style}
//       {...attributes}
//       onClick={() => {
//         if (!isViewMode) {
//           onToggle(index);
//         }
//       }}
//       className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-colors group ${
//         isSelected
//           ? "border-green-900 ring-2 ring-green-900/40"
//           : "border-gray-200 hover:border-gray-300"
//       }`}
//     >
//       <img
//         src={img.url}
//         alt={`Photo ${index + 1}`}
//         className="w-full h-full object-cover pointer-events-none"
//       />

//       {/* Position + Category at bottom */}
//       <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 flex justify-between">
//         <span>#{index + 1}</span>
//         {category && <span className="truncate ml-2">{category}</span>}
//       </div>

//       {/* Drag handle */}
//       {!isViewMode && (
//         <div
//           {...listeners}
//           className="absolute top-1.5 left-1/2 -translate-x-1/2 bg-white/80 rounded px-2 py-0.5 text-[10px] font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
//           onClick={(e) => e.stopPropagation()}
//         >
//           ⠿ Drag
//         </div>
//       )}
//       {/* View button */}

//       <button
//         type="button"
//         onClick={(e) => {
//           e.stopPropagation();
//           onView({ url: img.url, label: `Photo ${index + 1}` });
//         }}
//         className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-white/80 border border-gray-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
//       >
//         <Eye size={12} strokeWidth={2.5} />
//       </button>

//       {/* Selection checkbox */}
//       {!isViewMode && (
//         <div
//           className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center border ${
//             isSelected
//               ? "bg-green-500 border-green-900 text-white"
//               : "bg-white/80 border-gray-300"
//           }`}
//         >
//           {isSelected && <Check size={12} strokeWidth={3} />}
//         </div>
//       )}
//     </div>
//   );
// }
function DragStack({ images, count }) {
  // images[0] is the photo being grabbed, so it sits on top
  const stack = images.slice(0, 3);

  return (
    <div className="relative w-full h-full cursor-grabbing">
      {[...stack].reverse().map((img, r) => {
        const depth = stack.length - 1 - r; // 0 = top card
        return (
          <div
            key={img.url + r}
            className="absolute inset-0 rounded-lg overflow-hidden border-2 border-white shadow-xl bg-white"
            style={{
              zIndex: 10 - depth,
              transform: `translate(${depth * 10}px, ${depth * 10}px) rotate(${depth * 4}deg)`,
              transformOrigin: "center",
            }}
          >
            <img
              src={img.url}
              alt=""
              className="w-full h-full object-cover pointer-events-none"
            />
          </div>
        );
      })}

      {count > 1 && (
        <div
          className="absolute -top-3 -right-3 min-w-[26px] h-[26px] px-1.5 rounded-full bg-green-900 text-white text-xs font-semibold flex items-center justify-center border-2 border-white shadow-md"
          style={{ zIndex: 20 }}
        >
          {count}
        </div>
      )}
    </div>
  );
}
const EditPropertyDetailElementor = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const navigate = useNavigate();
  // const routerLocation = useLocation();
  // const { id: paramId } = useParams();
  // const propertyid = routerLocation.state?.propertyid ?? paramId;
  // const isViewMode = routerLocation.state?.mode === "view";
  const routerLocation = useLocation();
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();

  const propertyid =
    routerLocation.state?.propertyid ?? searchParams.get("id") ?? paramId;

  const isViewMode =
    routerLocation.state?.mode === "view" ||
    searchParams.get("mode") === "view";
  const [isLoadingProperty, setIsLoadingProperty] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [viewImage, setViewImage] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    if (savedState !== null) {
      return JSON.parse(savedState);
    }

    return window.innerWidth >= 1024;
  });
  const [indiaStates, setIndiaStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingImages, setIsDeletingImages] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ done: 0, total: 0 });
  const [propertycategory, setpropertycategory] = useState("");
  const [propertytag, setpropertytag] = useState("");

  useEffect(() => {
    // Load world.json from public folder
    setLoadingStates(true);
    fetch("/world.json")
      .then((res) => res.json())
      .then((data) => {
        const india = data.find((country) => country.name === "India");
        if (india) {
          setIndiaStates(india.states);
        }
      })
      .catch((err) => console.error("Error loading world.json:", err))
      .finally(() => setLoadingStates(false));
  }, []);

  const handleStateChange = (e) => {
    const stateName = e.target.value;
    setSelectedState(stateName);
    setLoadingCities(true);
    setTimeout(() => {
      if (stateName.toLowerCase() === "goa") {
        const goaCities = [
          "Aldona",
          "Arambol",
          "Baga",
          "Bambolim",
          "Bandora",
          "Benaulim",
          "Calangute",
          "Candolim",
          "Carapur",
          "Cavelossim",
          "Chicalim",
          "Chinchinim",
          "Colovale",
          "Colva",
          "Cortalim",
          "Cuncolim",
          "Curchorem",
          "Curti",
          "Davorlim",
          "Dicholi",
          "Goa Velha",
          "Guirim",
          "Jua",
          "Kankon",
          "Madgaon",
          "Мариса",
          "Morjim",
          "Mormugao",
          "Navelim",
          "North Goa",
          "Palle",
          "Panaji",
          "Pernem",
          "Ponda",
          "Quepem",
          "Queula",
          "Raia",
          "Saligao",
          "Sancoale",
          "Sanguem",
          "Sanquelim",
          "Sanvordem",
          "Serula",
          "Solim",
          "South Goa",
          "Taleigao",
          "Vagator",
          "Valpoy",
          "Varca",
          "Vasco da Gama",
          // Extra villages
          "Anjuna",
          "Arpora",
          "Assagao",
          "Parra",
          "Siolim",
          "Reis Magos",
          "Nerul",
          "Porvorim",
          "Dabolim",
          "Mandrem",
          "Marra",
          "Bardez",
          "Moira",
        ];

        // Convert to {id, name} objects since your city dropdown expects that
        setCities(goaCities.map((city, idx) => ({ id: idx + 1, name: city })));
      } else if (stateName.toLowerCase() === "karnataka") {
        const karnatakaCities = [
          // Extra villages

          // Added cities
          "Afzalpur",
          "Ajjampur",
          "Aland",
          "Alnavar",
          "lark",
          "Alur",
          "Anekal",
          "Ankola",
          "Annigeri",
          "Arkalgud",
          "Arsikere",
          "Athni",
          "Aurad",
          "Badami",
          "Bagalkot",
          "Bagepalli",
          "Bail-Hongal",
          "Ballari",
          "Banavar",
          "Bangarapet",
          "Bannur",
          "Bantval",
          "Basavakalyan",
          "Basavana Bagevadi",
          "Belagavi",
          "Belluru",
          "Beltangadi",
          "Belur",
          "Bengaluru",
          "Bengaluru Rural",
          "Bengaluru Urban",
          "Bhadravati",
          "Bhalki",
          "Bhatkal",
          "Bidar",
          "Bilgi",
          "Birur",
          "Byadgi",
          "Byndoor",
          "Canacona",
          "Challakere",
          "Chamrajnagar",
          "Channagiri",
          "Channapatna",
          "Channarayapatna",
          "Chik Ballapur",
          "Chikkaballapur",
          "Chikkamagaluru",
          "Chiknayakanhalli",
          "Chikodi",
          "Chincholi",
          "Chintamani",
          "Chitapur",
          "Chitradurga",
          "Closepet",
          "Coondapoor",
          "Coorg",
          "Dakshina Kannada",
          "Dandeli",
          "Davanagere",
          "Devanhalli",
          "Dharwad",
          "Dod Ballapur",
          "French Rocks",
          "Gadag",
          "Gadag-Betageri",
          "Gajendragarh",
          "Gangawati",
          "Gangolli",
          "Gokak",
          "Gokarna",
          "Goribidnur",
          "Gorur",
          "Gubbi",
          "Gudibanda",
          "Guledagudda",
          "Gundlupet",
          "Gurmatkal",
          "Hadagalli",
          "Haliyal",
          "Hampi",
          "Hangal",
          "Harihar",
          "Harpanahalli",
          "Hassan",
          "Haveri",
          "Heggadadevankote",
          "Hirekerur",
          "Hiriyur",
          "Holalkere",
          "Hole Narsipur",
          "Homnabad",
          "Honavar",
          "Honnali",
          "Hosanagara",
          "Hosangadi",
          "Hosdurga",
          "Hoskote",
          "Hospet",
          "Hubballi",
          "Hukeri",
          "Hungund",
          "Hunsur",
          "Ilkal",
          "Indi",
          "Jagalur",
          "Jamkhandi",
          "Jevargi",
          "Kabini",
          "Kadur",
          "Kalaburgi",
          "Kalghatgi",
          "Kampli",
          "Kankanhalli",
          "Karkala",
          "Karwar",
          "Kavalur",
          "Kerur",
          "Khanapur",
          "Kodagu",
          "Kodigenahalli",
          "Kodlipet",
          "Kolar",
          "Kollegal",
          "Konanur",
          "Konnur",
          "Koppa",
          "Koppal",
          "Koratagere",
          "Kotturu",
          "Krishnarajpet",
          "Kudachi",
          "Kudligi",
          "Kumsi",
          "Kumta",
          "Kundgol",
          "Kunigal",
          "Kurgunta",
          "Kushalnagar",
          "Kushtagi",
          "Lakshmeshwar",
          "Lingsugur",
          "Londa",
          "Machare",
          "Maddagiri",
          "Maddur",
          "Madikeri",
          "Magadi",
          "Mahalingpur",
          "Malavalli",
          "Malpe",
          "Malur",
          "Mandya",
          "Mangaluru",
          "Manipal",
          "Manvi",
          "Mayakonda",
          "Melukote",
          "Mudbidri",
          "Muddebihal",
          "Mudgal",
          "Mudgere",
          "Mudhol",
          "Mulbagal",
          "Mulgund",
          "Mulki",
          "Mundargi",
          "Mundgod",
          "Munirabad",
          "Murudeshwara",
          "Mysuru",
          "Nagamangala",
          "Nanjangud",
          "Narasimharajapura",
          "Naregal",
          "Nargund",
          "Navalgund",
          "Nelamangala",
          "Nyamti",
          "Pangala",
          "Pavugada",
          "Piriyapatna",
          "Ponnampet",
          "Puttur",
          "Rabkavi",
          "Raichur",
          "Ramanagara",
          "Ranibennur",
          "Raybag",
          "Robertsonpet",
          "Ron",
          "Sadalgi",
          "Sagar",
          "Sakleshpur",
          "Sandur",
          "Sanivarsante",
          "Sankeshwar",
          "Sargur",
          "Saundatti",
          "Savanur",
          "Seram",
          "Shahabad",
          "Shahpur",
          "Shiggaon",
          "Shikarpur",
          "Shimoga",
          "Shirhatti",
          "Shorapur",
          "Shrirangapattana",
          "Siddapur",
          "Sidlaghatta",
          "Sindgi",
          "Sindhnur",
          "Sira",
          "Sirsi",
          "Siruguppa",
          "Someshwar",
          "Somvarpet",
          "Sorab",
          "Sravana Belgola",
          "Sringeri",
          "Srinivaspur",
          "Sulya",
          "Suntikoppa",
          "Talikota",
          "Tarikere",
          "Tekkalakote",
          "Terdal",
          "Tiptur",
          "Tirthahalli",
          "Tirumakudal Narsipur",
          "Tumakuru",
          "Turuvekere",
          "Udupi",
          "Ullal",
          "Uttar Kannada",
          "Vadigenhalli",
          "Vijayapura",
          "Virarajendrapet",
          "Wadi",
          "Yadgir",
          "Yelahanka",
          "Yelandur",
          "Yelbarga",
          "Yellapur",
        ];

        // Convert to {id, name} objects since your city dropdown expects that
        setCities(
          karnatakaCities.map((city, idx) => ({ id: idx + 1, name: city })),
        );
      } else if (stateName.toLowerCase() === "kerala") {
        const keralaCities = [
          "Adoor",
          "Alappuzha",
          "Alleppey",
          "Aluva",
          "Alwaye",
          "Angamali",
          "Aroor",
          "Arukutti",
          "Attingal",
          "Avanoor",
          "Azhikkal",
          "Beypore",
          "Changanacheri",
          "Chelakara",
          "Chengannur",
          "Cherpulassery",
          "Cherthala",
          "Chetwayi",
          "Chittur",
          "Cochin",
          "Dharmadom",
          "Edakkulam",
          "Elur",
          "Erattupetta",
          "Ernakulam",
          "Ferokh",
          "Guruvayur",
          "Idukki",
          "Iringal",
          "Irinjalakuda",
          "Kadakkavoor",
          "Kalamassery",
          "Kalavoor",
          "Kalpetta",
          "Kanhangad",
          "Kannavam",
          "Kannur",
          "Kasaragod",
          "Kattanam",
          "Kayankulam",
          "Kizhake Chalakudi",
          "Kochi",
          "Kodungallur",
          "Kollam",
          "Kotamangalam",
          "Kottayam",
          "Kovalam",
          "Kozhikode",
          "Kumbalam",
          "Kunnamangalam",
          "Kunnamkulam",
          "Kunnumma",
          "Kutiatodu",
          "Kuttampuzha",
          "Lalam",
          "Mahe",
          "Malappuram",
          "Manjeri",
          "Manjeshwaram",
          "Mannarakkat",
          "Marayur",
          "Mattanur",
          "Mavelikara",
          "Mavoor",
          "Muluppilagadu",
          "Munnar",
          "Muvattupula",
          "Muvattupuzha",
          "Nadapuram",
          "Naduvannur",
          "Nedumangad",
          "Neyyattinkara",
          "Nileshwar",
          "Ottappalam",
          "Palackattumala",
          "Palakkad district",
          "Palghat",
          "Panamaram",
          "Pappinissheri",
          "Paravur Tekkumbhagam",
          "Pariyapuram",
          "Pathanamthitta",
          "Pattanamtitta",
          "Payyanur",
          "Perinthalmanna",
          "Perumbavoor",
          "Perumpavur",
          "Perya",
          "Piravam",
          "Ponnani",
          "Ponmana",
          "Punalur",
          "Punnayur",
          "Ramamangalam",
          "Shertallai",
          "Shoranur",
          "Taliparamba",
          "Thalassery",
          "Thalikulam",
          "Thanniyam",
          "Thekkady",
          "Thiruvananthapuram",
          "Thrissur",
          "Tirur",
          "Tiruvalla",
          "Vaikam",
          "Varkala",
          "Vatakara",
          "Vayalar",
          "Vettur",
          "Wayanad",
        ];

        // Convert to {id, name} objects since your city dropdown expects that
        setCities(
          keralaCities.map((city, idx) => ({ id: idx + 1, name: city })),
        );
      } else if (stateName.toLowerCase() === "himachal pradesh") {
        const himachalCities = [
          // Existing Himachal Pradesh cities
          "Arki",
          "Baddi",
          "Banjar",
          "Bilaspur",
          "Chamba",
          "Chaupal",
          "Chowari",
          "Chuari Khas",
          "Dagshai",
          "Dalhousie",
          "Daulatpur",
          "Dera Gopipur",
          "Dharamsala",
          "Gagret",
          "Ghumarwin",
          "Hamirpur",
          "Jawala Mukhi",
          "Jogindarnagar",
          "Jubbal",
          "Jutogh",
          "Kalka",
          "Kangar",
          "Kangra",
          "Kasauli",
          "Kinnaur",
          "Kotkhai",
          "Kotla",
          "Kulu",
          "Kyelang",
          "Lahul and Spiti",
          "Manali",
          "Mandi",
          "Nadaun",
          "Nagar",
          "Nagrota",
          "Nahan",
          "Nalagarh",
          "Palampur",
          "Pandoh",
          "Paonta Sahib",
          "Parwanoo",
          "Rajgarh",
          "Rampur",
          "Rohru",
          "Sabathu",
          "Santokhgarh",
          "Sarahan",
          "Sarka Ghat",
          "Seoni",
          "Shimla",
          "Sirmaur",
          "Solan",
          "Sundarnagar",
          "Theog",
          "Tira Sujanpur",
          "Una",
          "Yol",

          // Newly added locations
          "Bir",
          "Billing",
          "Narkanda",
          "Jibhi",
          "Tirthan",
          "Kasol",
        ];

        setCities(
          himachalCities.map((city, idx) => ({
            id: idx + 1,
            name: city,
          })),
        );
      } else if (stateName.toLowerCase() === "tamil nadu") {
        const tamilnaduCities = [
          // Tamil Nadu cities
          "Aambur",
          "Ariyalur",
          "Arakkonam",
          "Arani",
          "Attur",
          "Avadi",
          "Batlagundu",
          "Chengalpattu",
          "Chennai",
          "Chidambaram",
          "Coimbatore",
          "Courtallam",
          "Cuddalore",
          "Coonoor",
          "Dharapuram",
          "Dharmapuri",
          "Dindigul",
          "Erode",
          "Gobichettipalayam",
          "Gudalur",
          "Gudiyatham",
          "Hosur",
          "Jayankondam",
          "Kallakurichi",
          "Kanchipuram",
          "Kanyakumari",
          "Karaikudi",
          "Karur",
          "Kodaikanal",
          "Kovilpatti",
          "Krishnagiri",
          "Kumbakonam",
          "Madurai",
          "Manapparai",
          "Mahabalipuram",
          "Mayiladuthurai",
          "Mettupalayam",
          "Namakkal",
          "Nagapattinam",
          "Nagercoil",
          "Ooty",
          "Palani",
          "Pallavaram",
          "Pattukkottai",
          "Perambalur",
          "Pollachi",
          "Poonamallee",
          "Pudukkottai",
          "Rajapalayam",
          "Ramanathapuram",
          "Ranipet",
          "Salem",
          "Sankagiri",
          "Sivaganga",
          "Sivakasi",
          "Tambaram",
          "Tenkasi",
          "Thanjavur",
          "Theni",
          "Thiruvallur",
          "Thiruvarur",
          "Thoothukudi",
          "Tindivanam",
          "Tiruchendur",
          "Tiruchengode",
          "Tiruchirappalli",
          "Tirunelveli",
          "Tirupathur",
          "Tiruppur",
          "Tiruvannamalai",
          "Udagamandalam",
          "Udumalpet",
          "Usilampatti",
          "Vaniyambadi",
          "Vellore",
          "Villupuram",
          "Virudhunagar",
        ];

        // Convert to {id, name} objects since your city dropdown expects that
        setCities(
          tamilnaduCities.map((city, idx) => ({ id: idx + 1, name: city })),
        );
      } else if (stateName.toLowerCase() === "uttarakhand") {
        const uttarakhandCities = [
          // ... existing Uttarakhand cities from the JSON ...
          "Almora",
          "Bageshwar",
          "Barkot",
          "Bazpur",
          "Bhim Tal",
          "Bhowali",
          "Birbhaddar",
          "Chakrata",
          "Chamoli",
          "Champawat",
          "Clement Town",
          "Dehradun",
          "Devaprayag",
          "Dharchula",
          "Doiwala",
          "Dugadda",
          "Dwarahat",
          "Garhwal",
          "Haldwani",
          "Harbatpur",
          "Haridwar",
          "Jaspur",
          "Joshimath",
          "Kaladhungi",
          "Kalagarh Project Colony",
          "Kashipur",
          "Khatima",
          "Kichha",
          "Kotdwara",
          "Laksar",
          "Lansdowne",
          "Lohaghat",
          "Manglaur",
          "Mussoorie",
          "Naini Tal",
          "Narendranagar",
          "Pauri",
          "Pipalkoti",
          "Pithoragarh",
          "Raipur",
          "Raiwala Bara",
          "Ramnagar",
          "Ranikhet",
          "Rishikesh",
          "Roorkee",
          "Rudraprayag",
          "Sitarganj",
          "Srinagar",
          "Sultanpur",
          "Tanakpur",
          "Tehri",
          "Tehri-Garhwal",
          "Udham Singh Nagar",
          "Uttarkashi",
          "Vikasnagar",
          // Added
          "Satoli",
          "Kumaon",
          "Mukteshwar",
          "Jilling",
          "Jim Corbett",
        ];

        setCities(
          uttarakhandCities.map((city, idx) => ({ id: idx + 1, name: city })),
        );

        setCities(
          uttarakhandCities.map((city, idx) => ({
            id: idx + 1,
            name: city,
          })),
        );
      } else if (stateName.toLowerCase() === "maharashtra") {
        const maharashtraCities = [
          // ... existing Maharashtra cities from the JSON ...
          "Achalpur",
          "Adawad",
          "Agar Panchaitan",
          "Aheri",
          "Ahmadpur",
          "Ahmednagar",
          "Airoli",
          "Ajara",
          "Akalkot",
          "Akluj",
          "Akola",
          "Akolner",
          "Akot",
          "Akrani",
          "Alandi",
          "Ale",
          "Alibaug",
          "Alkuti",
          "Allapalli",
          "Amalner",
          "Amarnath",
          "Ambad",
          "Ambajogai",
          "Ambegaon",
          "Ambernath",
          "Amgaon",
          "Amravati",
          "Andheri",
          "Andura",
          "Anjangaon",
          "Anjarle",
          "Anshing",
          "Arag",
          "Arangaon",
          "Ardhapur",
          "Argaon",
          "Artist Village",
          "Arvi",
          "Ashta",
          "Ashti",
          "Asoda",
          "Assaye",
          "Astagaon",
          "Aundh Satara",
          "Aurangabad",
          "Ausa",
          "Badlapur",
          "Badnapur",
          "Badnera",
          "Bagewadi",
          "Balapur",
          "Balapur Akola district",
          "Ballalpur",
          "Ballard Estate",
          "Ballarpur",
          "Banda Maharashtra",
          "Bandra",
          "Baner",
          "Bankot",
          "Baramati",
          "Barsi",
          "Basmat",
          "Bavdhan",
          "Bawanbir",
          "Beed",
          "Bhadgaon Maharashtra",
          "Bhandara",
          "Bhandardara",
          "Bhandup",
          "Bhayandar",
          "Bhigvan",
          "Bhiwandi",
          "Bhiwapur",
          "Bhokar",
          "Bhokardan",
          "Bhoom",
          "Bhor",
          "Bhudgaon",
          "Bhugaon",
          "Bhusaval",
          "Bijur",
          "Bilashi",
          "Biloli",
          "Boisar",
          "Borgaon Manju",
          "Borivali",
          "Brahmapuri",
          "Breach Candy",
          "Buldana",
          "Byculla",
          "Chakan",
          "Chakur",
          "Chalisgaon",
          "Chanda",
          "Chandgad",
          "Chandor",
          "Chandrapur",
          "Chandur",
          "Chandur Bazar",
          "Chausala",
          "Chembur",
          "Chicholi",
          "Chichondi Patil",
          "Chikhli (Buldhana)",
          "Chikhli (Jalna)",
          "Chimur",
          "Chinchani",
          "Chinchpokli",
          "Chiplun",
          "Chopda",
          "Colaba",
          "Dabhol",
          "Daddi",
          "Dahanu",
          "Dahivel",
          "Dapoli",
          "Darwha",
          "Daryapur",
          "Dattapur",
          "Daulatabad",
          "Daund",
          "Deccan Gymkhana",
          "Deglur",
          "Dehu",
          "Deolali",
          "Deolapar",
          "Deoli",
          "Deoni",
          "Deulgaon Raja",
          "Devrukh",
          "Dharangaon",
          "Dharavi",
          "Dharmabad",
          "Dharur",
          "Dhawalpuri",
          "Dhule",
          "Dhulia",
          "Dighori",
          "Diglur",
          "Digras",
          "Dindori Maharashtra",
          "Diveagar",
          "Dombivli",
          "Dondaicha",
          "Dongri",
          "Dudhani",
          "Durgapur",
          "Durgapur Chandrapur",
          "Erandol",
          "Faizpur",
          "Fort",
          "Gadchiroli",
          "Gadhinglaj",
          "Gangakher",
          "Gangapur",
          "Ganpatipule",
          "Gevrai",
          "Ghargaon",
          "Ghatanji",
          "Ghatkopar",
          "Ghoti Budrukh",
          "Ghugus",
          "Girgaon",
          "Gondia",
          "Gorai",
          "Goregaon",
          "Guhagar",
          "Hadapsar Pune",
          "Hadgaon",
          "Halkarni",
          "Harangul",
          "Harnai",
          "Helwak",
          "Hinganghat",
          "Hingoli",
          "Hirapur",
          "Hirapur Hamesha",
          "Hotgi",
          "Ichalkaranji",
          "Igatpuri",
          "Indapur",
          "Jaisingpur",
          "Jaitapur",
          "Jakhangaon",
          "Jalgaon",
          "Jalgaon Jamod",
          "Jalkot",
          "Jalna",
          "Jamkhed",
          "Jamod",
          "Janephal",
          "Jaoli",
          "Jat Sangli",
          "Jategaon",
          "Jawhar",
          "Jaysingpur",
          "Jejuri",
          "Jintur",
          "Jogeshwari",
          "Juhu",
          "Junnar",
          "Kachurwahi",
          "Kadegaon",
          "Kadus",
          "Kagal",
          "Kaij",
          "Kalamb",
          "Kalamb Osmanabad",
          "Kalamboli",
          "Kalamnuri",
          "Kalas",
          "Kali(DK)",
          "Kalmeshwar",
          "Kalundri",
          "Kalyan",
          "Kalyani Nagar",
          "Kamargaon",
          "Kamatgi",
          "Kamptee",
          "Kandri",
          "Kankauli",
          "Kankavli",
          "Kannad",
          "Karad",
          "Karajagi",
          "Karanja",
          "Karanja Lad",
          "Karjat",
          "Karkamb",
          "Karmala",
          "Kasara",
          "Kasoda",
          "Kati",
          "Katol",
          "Katral",
          "Khadki",
          "Khalapur",
          "Khallar",
          "Khamgaon",
          "Khanapur",
          "Khandala",
          "Khangaon",
          "Khapa",
          "Kharakvasla",
          "Kharda",
          "Kharghar",
          "Kharsundi",
          "Khed",
          "Khetia",
          "Khoni",
          "Khopoli",
          "Khuldabad",
          "Kinwat",
          "Kodoli",
          "Kolhapur",
          "Kondalwadi",
          "Kondhali",
          "Kopar Khairane",
          "Kopargaon",
          "Kopela",
          "Koradi",
          "Koregaon",
          "Koynanagar",
          "Kudal",
          "Kuhi",
          "Kurandvad",
          "Kurankhed",
          "Kurduvadi",
          "Kusumba",
          "Lakhandur",
          "Lanja",
          "Lasalgaon",
          "Latur",
          "Lavasa",
          "Lohogaon",
          "Lonar",
          "Lonavala",
          "Mahabaleshwar",
          "Mahad",
          "Mahape",
          "Mahim",
          "Maindargi",
          "Majalgaon",
          "Makhjan",
          "Malabar Hill",
          "Malad",
          "Malegaon",
          "Malkapur",
          "Malvan",
          "Manchar",
          "Mandangad",
          "Mandhal",
          "Mandwa",
          "Mangaon",
          "Mangrul Pir",
          "Manjlegaon",
          "Mankeshwar",
          "Mankhurd",
          "Manmad",
          "Manor",
          "Mansar",
          "Manwat",
          "Maregaon",
          "Mastiholi",
          "Masur India",
          "Matheran",
          "Matunga",
          "Mazagaon",
          "Mehekar",
          "Mehergaon",
          "Mehkar",
          "Mhasla",
          "Mhasvad",
          "Miraj",
          "Mohadi",
          "Mohol",
          "Mohpa",
          "Mokhada taluka",
          "Mora Maharashtra",
          "Moram",
          "Morsi",
          "Mowad",
          "Mudkhed",
          "Mukher",
          "Mul",
          "Mulher",
          "Mulund",
          "Mumbai",
          "Mumbai Suburban",
          "Murbad",
          "Murgud",
          "Murtajapur",
          "Murud (Raigad)",
          "Murud (Ratnagiri)",
          "Murum",
          "Nadgaon",
          "Nagapur",
          "Nagothana",
          "Nagpur",
          "Nagpur Division",
          "Nala Sopara",
          "Naldurg",
          "Nalegaon",
          "Nampur",
          "Nanded",
          "Nandgaon",
          "Nandnee",
          "Nandura",
          "Nandura Buzurg",
          "Nandurbar",
          "Narayangaon",
          "Nardana",
          "Nariman Point",
          "Narkhed",
          "Nashik",
          "Nashik Division",
          "Navapur",
          "Navi Mumbai",
          "Neral",
          "Nerur",
          "Nevasa",
          "Nighoj",
          "Nilanga",
          "Nipani",
          "Niphad",
          "Nira Narsingpur",
          "Osmanabad",
          "Ozar",
          "Pabal",
          "Pachora",
          "Pahur Maharashtra",
          "Paithan",
          "Palghar",
          "Pali Raigad",
          "Palso",
          "Panchgani",
          "Pandharpur",
          "Pandhurli",
          "Panhala",
          "Panvel",
          "Parbhani",
          "Parel",
          "Parli Vaijnath",
          "Parner",
          "Parola",
          "Parseoni",
          "Partur",
          "Patan",
          "Pathardi",
          "Pathri",
          "Patur",
          "Paturda",
          "Paud",
          "Pauni",
          "Pawna",
          "Pedgaon",
          "Peint",
          "Pen",
          "Phaltan",
          "Phulambri",
          "Piliv",
          "Pimpalgaon Baswant",
          "Pimpalgaon Raja",
          "Pimpri",
          "Pimpri-Chinchwad",
          "Pipri",
          "Powai",
          "Prabhadevi",
          "Prakasha",
          "Pulgaon",
          "Pune",
          "Pune Division",
          "Puntamba",
          "Pural",
          "Purna",
          "Pusad",
          "Radhanagari",
          "Rahata",
          "Rahimatpur",
          "Rahuri",
          "Raigarh",
          "Raireshwar",
          "Rajapur",
          "Rajgurunagar",
          "Rajur",
          "Rajura",
          "Ralegaon",
          "Ramewadi",
          "Ramtek",
          "Ratnagiri",
          "Raver",
          "Renapur",
          "Renavi",
          "Revadanda",
          "Revdanda",
          "Risod",
          "Roha",
          "Sailu",
          "Sakol",
          "Sakoli",
          "Sakri",
          "Samudrapur",
          "Sangameshwar",
          "Sangamner",
          "Sangli",
          "Sangola",
          "Sangrampur Maharashtra",
          "Saoli",
          "Saoner",
          "Sarangkheda",
          "Saswad",
          "Satana",
          "Satara",
          "Satara Division",
          "Satpati",
          "Savantvadi",
          "Savda",
          "Savlaj",
          "Sawantvadi",
          "Selu",
          "Sevagram",
          "Sewri",
          "Shahada",
          "Shahapur",
          "Shedbal",
          "Shegaon",
          "Shevgaon",
          "Shikrapur",
          "Shiraguppi",
          "Shirala",
          "Shirdi",
          "Shirgaon",
          "Shirol",
          "Shirpur",
          "Shirud",
          "Shirwal",
          "Shivaji Nagar",
          "Shrigonda",
          "Sillod",
          "Sindewahi",
          "Sindhudurg",
          "Sindi",
          "Sindkheda",
          "Sinnar",
          "Sion Mumbai",
          "Sironcha",
          "Sirur",
          "Sivala East Godavari district",
          "Solapur",
          "Sonala",
          "Sonegaon",
          "Songir",
          "Sonvad",
          "Soygaon",
          "Srivardhan",
          "Surgana",
          "Taklibhan",
          "Talbid",
          "Talegaon Dabhade",
          "Talegaon Dhamdhere",
          "Taloda",
          "Talode",
          "Tarapur",
          "Tardeo",
          "Tasgaon",
          "Telhara",
          "Thalner",
          "Thane",
          "Trimbak",
          "Trombay",
          "Tuljapur",
          "Tumsar",
          "Udgir",
          "Ulhasnagar",
          "Umarga",
          "Umarkhed",
          "Umred",
          "Uran",
          "Uruli Kanchan",
          "Vada",
          "Vadgaon",
          "Vadner",
          "Vaijapur",
          "Vairag",
          "Valsang",
          "Vangaon",
          "Varangaon",
          "Vashi",
          "Vasind",
          "Vatul",
          "Velas Maharashtra",
          "Velneshwar",
          "Vengurla",
          "Vijaydurg",
          "Vikhroli",
          "Vile Parle",
          "Vinchur",
          "Virar",
          "Vita Maharashtra",
          "Vite",
          "Wadala",
          "Wadgaon",
          "Wadner",
          "Wadwani",
          "Wagholi",
          "Wai",
          "Wakad",
          "Walgaon",
          "Walki",
          "Wani",
          "Wardha",
          "Warora",
          "Warud",
          "Washim",
          "Worli",
          "Yaval",
          "Yavatmal",
          "Yeola",
          // Added
          "Mulshi",
        ];

        setCities(
          maharashtraCities.map((city, idx) => ({ id: idx + 1, name: city })),
        );

        setCities(
          maharashtraCities.map((city, idx) => ({
            id: idx + 1,
            name: city,
          })),
        );
      } else {
        const stateObj = indiaStates.find((s) => s.name === stateName);
        setCities(stateObj ? stateObj.cities : []);
      }
      setLoadingCities(false);
    }, 0);
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const MAX_CARDS = 100;

  const [selectedImages, setSelectedImages] = useState(
    Array(MAX_CARDS).fill(null),
  );

  // Category assigned to each banner slide/image (parallel array to selectedImages)
  const [selectedCategories, setSelectedCategories] = useState(
    Array(MAX_CARDS).fill(""),
  );
  // crop logic
  const [selectedCrops, setSelectedCrops] = useState(
    Array(MAX_CARDS).fill(null),
  );
  const [cropModalIndex, setCropModalIndex] = useState(null);
  const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
  const [cropZoom, setCropZoom] = useState(1);
  const [pendingCroppedArea, setPendingCroppedArea] = useState(null);
  const bannerWrapperRef = useRef(null);
  const [bannerAspect, setBannerAspect] = useState(16 / 9);
  const [cropBoxSize, setCropBoxSize] = useState({ width: 600, height: 338 });

  const openCropModal = (index) => {
    const existing = selectedCrops[index];
    setCropPosition({ x: 0, y: 0 });
    setCropZoom(existing?.zoom || 1);
    setPendingCroppedArea(existing || null);

    const wrapperWidth =
      bannerWrapperRef.current?.getBoundingClientRect()?.width;
    const bannerHeightPx = window.innerHeight * 0.58; // matches h-[58vh]
    const aspect =
      wrapperWidth && bannerHeightPx ? wrapperWidth / bannerHeightPx : 16 / 9;
    setBannerAspect(aspect);

    // Fit that exact aspect ratio into the available modal space, in real px
    // (avoids the aspect-ratio + max-height CSS conflict that shrinks the box).
    const maxAvailableWidth = window.innerWidth * 0.85;
    const maxAvailableHeight = window.innerHeight * 0.6;

    let boxWidth = maxAvailableHeight * aspect;
    let boxHeight = maxAvailableHeight;
    if (boxWidth > maxAvailableWidth) {
      boxWidth = maxAvailableWidth;
      boxHeight = maxAvailableWidth / aspect;
    }
    setCropBoxSize({ width: boxWidth, height: boxHeight });

    setCropModalIndex(index);
  };

  const handleCropComplete = (croppedAreaPercentages, _croppedAreaPixels) => {
    setPendingCroppedArea({
      x: croppedAreaPercentages.x,
      y: croppedAreaPercentages.y,
      width: croppedAreaPercentages.width,
      height: croppedAreaPercentages.height,
    });
  };

  const saveCrop = () => {
    if (cropModalIndex === null || !pendingCroppedArea) return;
    setSelectedCrops((prev) => {
      const u = [...prev];
      const { x, y, width, height } = pendingCroppedArea;
      u[cropModalIndex] = { x, y, width, height };
      return u;
    });
    setCropModalIndex(null);
  };
  //crop logic over
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [bannerCategoryError, setBannerCategoryError] = useState("");
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const bannerCarouselRef = useRef(null);

  // "All Photos" multi-select + bulk category assignment, used at the top of
  // the View All gallery modal (separate from the per-slide category picker
  // on the main banner carousel).
  const [selectedGalleryIndices, setSelectedGalleryIndices] = useState([]);
  const [bulkCategoryValue, setBulkCategoryValue] = useState("");

  // Reset the gallery selection whenever the modal is closed, so it starts
  // fresh next time it's opened.
  useEffect(() => {
    if (!showGalleryModal) {
      setSelectedGalleryIndices([]);
      setBulkCategoryValue("");
    }
  }, [showGalleryModal]);

  const toggleGallerySelection = (index) => {
    setSelectedGalleryIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const allGalleryIndices = selectedImages
    .map((img, i) => (img ? i : null))
    .filter((i) => i !== null);

  const isAllGallerySelected =
    allGalleryIndices.length > 0 &&
    selectedGalleryIndices.length === allGalleryIndices.length;

  const toggleSelectAllGallery = () => {
    setSelectedGalleryIndices(isAllGallerySelected ? [] : allGalleryIndices);
  };

  // Applies `bulkCategoryValue` to every currently-selected image at once.
  const applyBulkCategory = () => {
    if (!bulkCategoryValue || selectedGalleryIndices.length === 0) return;
    setSelectedCategories((prev) => {
      const updated = [...prev];
      selectedGalleryIndices.forEach((idx) => {
        updated[idx] = bulkCategoryValue;
      });
      return updated;
    });
    setSelectedGalleryIndices([]);
    setBulkCategoryValue("");
    setBannerCategoryError("");
  };

  // Checks whether the slide at `index` is blocking navigation.
  // Only a NEWLY uploaded file (has `.file`) without a category blocks —
  // images that were already loaded from the server (no `.file`, e.g.
  // existing carousel images or converted legacy images) never block, since
  // legacy images predate the category concept and shouldn't be forced to
  // pick one just to view/save the rest of the form.
  const isBannerSlideBlocked = (index) => {
    return !!selectedImages[index]?.file && !selectedCategories[index];
  };

  const CustomLeftArrowBannerCarousel = ({ onClick }) => {
    return (
      <button
        onClick={onClick}
        className="hidden md:block absolute left-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
        name="previous_btn"
      >
        <FaChevronLeft />
      </button>
    );
  };

  const CustomRightArrowBannerCarousel = ({ onClick }) => {
    const handleNextClick = () => {
      // Block moving to the next slide until the current slide's image has a category
      // if (isBannerSlideBlocked(activeBannerIndex)) {
      //   setBannerCategoryError(
      //     "Please select a category for this image before moving to the next slide.",
      //   );
      //   return;
      // }
      setBannerCategoryError("");
      onClick();
    };

    return (
      <button
        onClick={handleNextClick}
        className="hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
        name="next_btn"
      >
        <FaChevronRight />
      </button>
    );
  };

  // Plain arrows (no category validation) — used by carousels other than the
  // main banner (e.g. the per-bedroom image carousels below), so they don't
  // accidentally inherit the banner's category-gate logic.
  const CustomLeftArrowPlain = ({ onClick }) => {
    return (
      <button
        onClick={onClick}
        className="hidden md:block absolute left-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
        name="previous_btn"
      >
        <FaChevronLeft />
      </button>
    );
  };

  const CustomRightArrowPlain = ({ onClick }) => {
    return (
      <button
        onClick={onClick}
        className="hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
        name="next_btn"
      >
        <FaChevronRight />
      </button>
    );
  };

  // Guards against the corrective goToSlide (below) re-triggering this same
  // handler and causing an oscillation/"stuck" loop.
  const isCorrectingBannerSlideRef = useRef(false);

  // Safety net for navigation that bypasses the arrow buttons (swipe, keyboard, dots):
  // if the previous slide had an image without a category, snap back to it —
  // but ONLY when moving forward. Moving backward must always be allowed,
  // otherwise a later incomplete slide permanently blocks the user from ever
  // navigating back to fix it.
  const handleBannerAfterChange = (previousSlide, { currentSlide }) => {
    if (isCorrectingBannerSlideRef.current) {
      isCorrectingBannerSlideRef.current = false;
      setActiveBannerIndex(currentSlide);
      return;
    }

    const movingForward = currentSlide > previousSlide;

    // if (movingForward && isBannerSlideBlocked(previousSlide)) {
    //   setBannerCategoryError(
    //     "Please select a category for this image before moving to the next slide.",
    //   );
    //   isCorrectingBannerSlideRef.current = true;
    //   if (bannerCarouselRef.current) {
    //     bannerCarouselRef.current.goToSlide(previousSlide);
    //   }
    //   setActiveBannerIndex(previousSlide);
    //   return;
    // }
    setBannerCategoryError("");
    setActiveBannerIndex(currentSlide);
  };

  const handleImageUpload = async (event, index) => {
    if (isViewMode) return;
    const file = event.target.files[0];
    event.target.value = ""; // allow re-selecting the same file later
    if (!file) return;

    let webpFile;
    try {
      webpFile = await toWebpFile(file);
    } catch (err) {
      setBannerCategoryError(
        "That image could not be converted. Try a different file.",
      );
      return;
    }

    const imageurl = URL.createObjectURL(webpFile);
    const updatedImages = [...selectedImages];
    updatedImages[index] = { url: imageurl, file: webpFile };
    setSelectedImages(updatedImages);

    setSelectedCrops((prev) => {
      const u = [...prev];
      u[index] = null;
      return u;
    });
  };
  const handleBulkImageUpload = async (event) => {
    if (isViewMode) return;
    const files = Array.from(event.target.files || []);
    event.target.value = ""; // allow re-selecting the same files again later
    if (files.length === 0) return;

    const emptySlotCount = selectedImages.filter((img) => !img).length;

    let webpFiles;
    try {
      webpFiles = await Promise.all(files.map((f) => toWebpFile(f)));
    } catch (err) {
      setBannerCategoryError(
        "One or more images could not be converted. Try again.",
      );
      return;
    }

    setSelectedImages((prev) => {
      const updated = [...prev];
      let fileIdx = 0;

      for (let i = 0; i < updated.length && fileIdx < webpFiles.length; i++) {
        if (!updated[i]) {
          const file = webpFiles[fileIdx];
          updated[i] = { url: URL.createObjectURL(file), file };
          fileIdx++;
        }
      }
      return updated;
    });

    if (files.length > emptySlotCount) {
      setBannerCategoryError(
        `Only ${emptySlotCount} empty slot(s) available — ${
          files.length - emptySlotCount
        } photo(s) couldn't be added. Delete some images or reduce your selection.`,
      );
    } else {
      setBannerCategoryError("");
    }
  };
  const handleBannerImageDelete = async (index) => {
    if (isViewMode) return;
    const img = selectedImages[index];

    // Clear locally right away for a snappy UI.
    setSelectedImages((prev) => {
      const u = [...prev];
      u[index] = null;
      return u;
    });
    setSelectedCategories((prev) => {
      const u = [...prev];
      u[index] = "";
      return u;
    });
    setSelectedCrops((prev) => {
      const u = [...prev];
      u[index] = null;
      return u;
    });
    setBannerCategoryError("");

    // If this was already a persisted image (loaded from the server, no
    // pending `.file`), delete it from the DB + Cloudflare right now instead
    // of waiting for the next full-form Save. Freshly-picked-but-not-yet-saved
    // uploads (has `.file`) were never written anywhere, so nothing to call.
    if (img?.url && !img?.file && propertyid) {
      try {
        const res = await fetch(`${API_URL}/property/${propertyid}/image`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ imageurl: img.url }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to delete image");
        }
      } catch (err) {
        console.error("Error deleting image:", err);
        setSubmitError(
          "That image was removed here but couldn't be deleted on the server. It may reappear if you don't save.",
        );
      }
    }
  };
  const handleDeleteSelectedImages = async () => {
    if (isViewMode) return;
    const indices = [...selectedGalleryIndices];
    if (indices.length === 0) return;

    setShowDeleteConfirm(false);
    setIsDeletingImages(true);
    setDeleteProgress({ done: 0, total: indices.length });

    const deleteSet = new Set(indices);
    const failedDeletes = [];

    for (let i = 0; i < indices.length; i++) {
      const idx = indices[i];
      const img = selectedImages[idx];

      if (img?.url && img?.file) {
        // Fresh, not-yet-saved upload — nothing on the server to clean up,
        // just release the local blob URL.
        URL.revokeObjectURL(img.url);
      } else if (img?.url && !img?.file && propertyid) {
        // Already-persisted image (came from the server on load) — delete it
        // from the DB + Cloudflare right now, same call as the single-image
        // delete in handleBannerImageDelete.
        try {
          const res = await fetch(`${API_URL}/property/${propertyid}/image`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageurl: img.url }),
          });
          const data = await res.json();
          if (!res.ok || !data.success) {
            throw new Error(data.error || "Failed to delete image");
          }
        } catch (err) {
          console.error("Error deleting image:", err);
          failedDeletes.push(idx);
          // Server delete failed — keep this slide, don't compact it away.
          deleteSet.delete(idx);
        }
      }

      setDeleteProgress({ done: i + 1, total: indices.length });
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    // Compact: drop the slots that were actually deleted and shift everything
    // left so there are no gaps — same approach as handleDragEnd.
    const entries = selectedImages
      .map((img, i) => ({
        img,
        cat: selectedCategories[i],
        crop: selectedCrops[i],
        i,
      }))
      .filter((e) => e.img !== null && !deleteSet.has(e.i));

    const newImages = Array(MAX_CARDS).fill(null);
    const newCategories = Array(MAX_CARDS).fill("");
    const newCrops = Array(MAX_CARDS).fill(null);

    entries.forEach((e, idx) => {
      newImages[idx] = e.img;
      newCategories[idx] = e.cat;
      newCrops[idx] = e.crop;
    });

    setSelectedImages(newImages);
    setSelectedCategories(newCategories);
    setSelectedCrops(newCrops);

    setSelectedGalleryIndices([]);
    setBannerCategoryError("");
    setIsDeletingImages(false);

    if (failedDeletes.length > 0) {
      setSubmitError(
        `${failedDeletes.length} image(s) couldn't be deleted from the server and were kept — try again.`,
      );
    }
  };
  // Deletes a Tailored Experience grid image (imagegrid1-4 on propertyImages).
  // Same idea as handleBannerImageDelete: clear locally right away, and if
  // this was already a persisted image (a real saved URL, not a fresh
  // not-yet-saved file), delete it from Cloudflare + null the column now
  // instead of waiting for the next form Save — Save alone never sends this
  // field once it's cleared, so without this call the old value would just
  // sit in the DB untouched.
  const handleTailoredexpimageDelete = async (index) => {
    if (isViewMode) return;

    const exp = tailoredExpData[index];
    const wasPersisted = !!exp?.img && !exp?.file;

    setTailoredExpData((prev) => {
      const u = [...prev];
      u[index] = { ...u[index], img: null, file: null };
      return u;
    });

    if (wasPersisted && propertyid) {
      try {
        const res = await fetch(`${API_URL}/property/${propertyid}/image`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fieldName: `imagegrid${index + 1}`,
            table: "propertyImages",
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.error || "Failed to delete image");
        }
      } catch (err) {
        console.error("Error deleting tailored experience image:", err);
        setSubmitError(
          "That image was removed here but couldn't be deleted on the server. It may reappear if you don't save.",
        );
      }
    }
  };

  const handleBannerCategoryChange = (index, categoryValue) => {
    setSelectedCategories((prev) => {
      const u = [...prev];
      u[index] = categoryValue;
      return u;
    });
    if (categoryValue) {
      setBannerCategoryError("");
    }
  };

  // Builds the ordered category list, with Bedroom/Bathroom expanded dynamically
  // based on the "No. of Rooms" (bookingRooms/maxbed) and "Max Bathroom" (maxbath) inputs.
  const getBannerCategoryOptions = () => {
    const bedroomCount = parseInt(bookingRooms) || 0;
    const bathroomCount = parseInt(maxbath) || 0;

    const bedroomOptions =
      bedroomCount > 0
        ? Array.from({ length: bedroomCount }, (_, i) => `Bedroom ${i + 1}`)
        : ["Bedroom"];

    const bathroomOptions =
      bathroomCount > 0
        ? Array.from({ length: bathroomCount }, (_, i) => `Bathroom ${i + 1}`)
        : ["Bathroom"];

    return [
      "Living Room",
      "Dining Area",
      ...bedroomOptions,
      ...bathroomOptions,
      "Pool",
      "Courtyard",
      "Games & Activity Area",
      "Exterior",
      "Jacuzzi",
      "Theatre Room",
      "Gym",
      "Garden",
      "Kitchen",
      "Additional Photos",
      "Cover Photo",
    ];
  };

  // Groups every selected banner image by its assigned category, for the "View All" gallery.
  // Categories with no images are omitted.
  const getGroupedBannerGallery = () => {
    const grouped = {};
    getBannerCategoryOptions().forEach((cat) => {
      grouped[cat] = [];
    });
    selectedImages.forEach((img, index) => {
      const cat = selectedCategories[index];
      if (img && cat) {
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push({ ...img, index });
      }
    });
    return Object.entries(grouped).filter(([, imgs]) => imgs.length > 0);
  };

  // Pulls the images assigned to a given bedroom directly from the banner's
  // selectedImages/selectedCategories (category === "Bedroom {roomIndex + 1}").
  // This keeps the Bedroom Carousel section below in sync with the banner —
  // there is no separate upload step here anymore.
  const getBedroomImages = (roomIndex) => {
    const categoryLabel = `Bedroom ${roomIndex + 1}`;
    const images = [];
    selectedImages.forEach((img, idx) => {
      if (img && selectedCategories[idx] === categoryLabel) {
        images.push({ ...img, bannerIndex: idx });
      }
    });
    return images;
  };

  const TailoredExpCarousel = [
    {
      img: "https://mpvstorage.blob.core.windows.net/villapics-new/1592/77613/villa-barbados-caribbean-luxury-the-great-house-boat_FullL.jpg?v=302558110212",
      title: "For Adventure Seekers",
      content:
        "Experience the thrill of Goa with our adventure package! Enjoy cozy stays, a hearty breakfast, and activities like water sports, trekking, and more.",
    },
    {
      img: "https://mpvstorage.blob.core.windows.net/villapics-new/1592/66699/villa-barbados-caribbean-luxury-the-great-house-ext-9_FullL.jpg?v=302558110212",
      title: "For Leisure Lovers",
      content:
        "Unwind with our leisure package in Goa. Relax in serene stays, savor a delicious breakfast, and enjoy peaceful beaches, spa sessions, and local attractions.",
    },
  ];

  const responsive_TailoredExpCarousel = {
    xl: {
      breakpoint: { max: 4000, min: 1280 },
      items: 2,
      partialVisibilityGutter: 20,
    },
    lg: {
      breakpoint: { max: 1280, min: 1024 },
      items: 2,
      partialVisibilityGutter: 20,
    },
    md: {
      breakpoint: { max: 1024, min: 768 },
      items: 2,
      partialVisibilityGutter: 15,
    },
    sm: {
      breakpoint: { max: 768, min: 640 },
      items: 1,
      partialVisibilityGutter: 15,
    },
    xs: {
      breakpoint: { max: 640, min: 0 },
      items: 1,
      partialVisibilityGutter: 10,
    },
  };

  const responsive_OuterCarousel = {
    xl: {
      breakpoint: { max: 4000, min: 1280 },
      items: 3,
      partialVisibilityGutter: 20,
    },
    lg: {
      breakpoint: { max: 1280, min: 1024 },
      items: 3,
      partialVisibilityGutter: 20,
    },
    md: {
      breakpoint: { max: 1024, min: 768 },
      items: 2,
      partialVisibilityGutter: 15,
    },
    sm: {
      breakpoint: { max: 768, min: 640 },
      items: 2,
      partialVisibilityGutter: 15,
    },
    xs: {
      breakpoint: { max: 640, min: 0 },
      items: 1,
      partialVisibilityGutter: 10,
    },
  };

  const responsive_BannerCarousel = {
    xl: {
      breakpoint: { max: 4000, min: 1280 },
      items: 1,
      partialVisibilityGutter: 20,
    },
    lg: {
      breakpoint: { max: 1280, min: 1024 },
      items: 1,
      partialVisibilityGutter: 20,
    },
    md: {
      breakpoint: { max: 1024, min: 768 },
      items: 1,
      partialVisibilityGutter: 15,
    },
    sm: {
      breakpoint: { max: 768, min: 640 },
      items: 1,
      partialVisibilityGutter: 15,
    },
    xs: {
      breakpoint: { max: 640, min: 0 },
      items: 1,
      partialVisibilityGutter: 10,
    },
  };

  const [showDots, setShowDots] = useState(false);

  useEffect(() => {
    const updateDotsVisibility = () => {
      setShowDots(window.innerWidth < 768);
    };

    updateDotsVisibility();
    window.addEventListener("resize", updateDotsVisibility);
    return () => window.removeEventListener("resize", updateDotsVisibility);
  }, []);

  const responsive_ImageCarouselOne = {
    xl: {
      breakpoint: { max: 4000, min: 1280 },
      items: 1,
      partialVisibilityGutter: 20,
    },
    lg: {
      breakpoint: { max: 1280, min: 1024 },
      items: 1,
      partialVisibilityGutter: 20,
    },
    md: {
      breakpoint: { max: 1024, min: 768 },
      items: 1,
      partialVisibilityGutter: 15,
    },
    sm: {
      breakpoint: { max: 768, min: 640 },
      items: 1,
      partialVisibilityGutter: 15,
    },
    xs: {
      breakpoint: { max: 640, min: 0 },
      items: 1,
      partialVisibilityGutter: 10,
    },
  };

  const CustomLeftArrowTailoredExpCarousel = ({ onClick }) => {
    return (
      <button
        onClick={onClick}
        className="hidden md:block absolute left-3 top-44 transform -translate-y-1/2 shadow-custom bg-white bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
        name="previous_btn"
      >
        <FaChevronLeft />
      </button>
    );
  };

  const CustomRightArrowTailoredExpCarousel = ({ onClick }) => {
    return (
      <button
        onClick={onClick}
        className="hidden md:block absolute right-3 top-44 transform -translate-y-1/2 shadow-custom bg-white bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
        name="next_btn"
      >
        <FaChevronRight />
      </button>
    );
  };

  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const carouselRef = useRef(null);

  const handlePrevSlide = () => {
    if (carouselRef.current) {
      carouselRef.current.previous();
    }
  };

  const handleNextSlide = () => {
    if (carouselRef.current) {
      carouselRef.current.next();
    }
  };

  const [, setIsOpen_dropdownExp] = useState(false);
  const dropdownRef_dropdownExp = useRef(null);

  useEffect(() => {
    const handleClickOutside_dropdownExp = (event) => {
      if (
        dropdownRef_dropdownExp.current &&
        !dropdownRef_dropdownExp.current.contains(event.target)
      ) {
        setIsOpen_dropdownExp(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside_dropdownExp);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside_dropdownExp);
  }, []);
  const [tailoredExpData, setTailoredExpData] = useState([
    { img: null, title: "", content: "" },
    { img: null, title: "", content: "" },
    { img: null, title: "", content: "" },
    { img: null, title: "", content: "" },
  ]);
  const MAX_CARDS_Exp = tailoredExpData.length;

  // Booking Widget States
  const [bookingPrice, setBookingPrice] = useState("");
  const [bookingPriceDetails, setBookingPriceDetails] = useState(
    "(for 10 rooms) Per Night + Taxes",
  );
  const [bookingcheckinDate, setBookingcheckinDate] = useState("");
  const [bookingcheckintime, setBookingcheckintime] = useState("");
  const [bookingcheckoutDate, setBookingcheckoutDate] = useState("");
  const [bookingcheckouttime, setBookingcheckouttime] = useState("");
  const [bookingAdults, setBookingAdults] = useState("2");
  const [bookingChildren, setBookingChildren] = useState("0");
  const [bookingRooms, setBookingRooms] = useState("3");
  const [bookingTotal, setBookingTotal] = useState("");

  const [propertyname, setpropertyname] = useState("");
  const [description, setDescription] = useState("");
  const [heading, setHeading] = useState("");
  const [content, setContent] = useState("");
  const [catchyQuote, setCatchyQuote] = useState("");
  const [maptitle, setmaptitle] = useState("");
  const [mapcontent, setmapcontent] = useState("");
  const [tailoredExperience, setTailoredExperience] = useState("");
  const [tailoredExperienceContent, setTailoredExperienceContent] =
    useState("");

  const [maplang, setmaplang] = useState("");
  const [maplong, setmaplong] = useState("");

  // Additional required data (not shown on website)
  const [propertycode, setpropertycode] = useState("");
  const [propertyowner, setpropertyowner] = useState("");
  const [airid, setairid] = useState("");
  const [instagramreellink, setinstagramreellink] = useState("");
  const [propertybrochure, setPropertyBrochure] = useState("");
  const [defaultprice, setdefaultprice] = useState("");
  const [deposit, setDeposit] = useState("");
  const [maxbath, setmaxbath] = useState("");
  const [maplink, setMaplink] = useState("");
  const [youtubelink, setYoutubelink] = useState("");
  const [conserlink, setconserlink] = useState("");
  const [seasontype, setseasontype] = useState([]);
  const [isOpen_seasontype, setIsOpen_seasontype] = useState(false);
  const containerRef_seasontype = useRef(null);
  const [propertytype, setpropertytype] = useState("Villa");
  const [location, setLocation] = useState("");
  const [rating, setRating] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Upload progress for the saving overlay. "uploading" = bytes still
  // leaving the browser (tracked via axios onUploadProgress); "finalizing"
  // = browser has sent everything, server is now uploading to Cloudflare
  // and writing to the DB — no byte-level signal exists for that part.
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState("idle"); // idle | uploading | finalizing

  const [amenitiesList, setAmenitiesList] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]); // stores ids: [1, 4, 5]

  const MAX_CHARS = 1500;

  const quillRef = useRef(null);
  const [textLength, setTextLength] = useState(0);

  const handleContentChange = (html, delta, source, editor) => {
    const len = editor.getLength() - 1;

    if (len > MAX_CHARS) {
      const quill = quillRef.current.getEditor();
      quill.deleteText(MAX_CHARS, quill.getLength());
      return;
    }

    setContent(html);
    setTextLength(len);
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen_amenities, setIsOpen_amenities] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/getAmenities`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setAmenitiesList(data.data);
      })
      .catch((err) => console.error("Failed to load amenities", err));
  }, []);

  // Toggle stores/removes the numeric id
  const toggleAmenity = (id) => {
    if (isViewMode) return;
    setSelectedAmenities((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };
  const toggleseasontype = (season) => {
    setseasontype((prev) =>
      prev.includes(season)
        ? prev.filter((s) => s !== season)
        : [...prev, season],
    );
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef_seasontype.current &&
        !containerRef_seasontype.current.contains(event.target)
      ) {
        setIsOpen_seasontype(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const filteredAmenities = amenitiesList.filter((amenity) =>
    amenity.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const containerRef_amenities = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef_amenities.current &&
        !containerRef_amenities.current.contains(event.target)
      ) {
        setIsOpen_amenities(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ─── Fetch existing property + autofill the form ───
  useEffect(() => {
    if (!propertyid) {
      setIsLoadingProperty(false);
      setLoadError(
        "No property selected to edit — missing propertyid (check the navigate() call that opened this page).",
      );
      return;
    }

    const loadProperty = async () => {
      setIsLoadingProperty(true);
      setLoadError("");
      try {
        const res = await fetch(`${API_URL}/admin/properties/${propertyid}`);
        if (!res.ok) throw new Error("Failed to load property");
        const p = await res.json();

        // ── Simple fields ──
        setpropertyname(p.propertyname || "");
        setDescription(p.description || "");
        setHeading(p.heading || "");
        setContent(p.content || "");
        setCatchyQuote(p.catchquote || "");
        setmaptitle(p.maptitle || "");
        setmapcontent(p.mapcontent || "");
        setmaplang(p.maplang ?? "");
        setmaplong(p.maplong ?? "");
        setpropertycode(p.propertycode || "");
        setpropertyowner(p.propertyowner || "");
        setairid(p.airid || "");
        setinstagramreellink(p.instagramreellink || "");
        setPropertyBrochure(p.propertybrochure || "");
        setBookingPrice(p.currentprice ?? "");
        setdefaultprice(p.defaultprice ?? "");
        setDeposit(p.deposit ?? "");
        setBookingAdults(String(p.maxadult ?? "2"));
        setBookingChildren(String(p.maxchild ?? "0"));
        setBookingRooms(String(p.maxbed ?? "3"));
        setmaxbath(String(p.maxbath ?? ""));
        setMaplink(p.maplink || "");
        setYoutubelink(p.youtubelink || "");
        setseasontype(
          p.seasontype
            ? String(p.seasontype)
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
            : [],
        );
        setpropertytype(p.propertytype || "Villa");
        setSelectedState(p.destination || "");
        setLocation(p.location || "");
        setRating(p.rating ?? "");
        setconserlink(p.conserlink || "");
        setpropertycategory(p.propertycategory || "");
        setpropertytag(p.propertytag || "");

        // ── Amenities (stored as comma-joined ids string) ──
        if (p.addons) {
          setSelectedAmenities(
            String(p.addons)
              .split(",")
              .map((v) => parseInt(v, 10))
              .filter((v) => !isNaN(v)),
          );
        }

        // ── Tailored experience ──
        setTailoredExperience(p.expnamemain || "");
        setTailoredExperienceContent(p.expcontentmain || "");
        setTailoredExpData([
          { img: p.imagegrid1 || null, title: "", content: "" },
          { img: p.imagegrid2 || null, title: "", content: "" },
          { img: p.imagegrid3 || null, title: "", content: "" },
          { img: p.imagegrid4 || null, title: "", content: "" },
        ]);

        // ── Merged top carousel ──
        // Every property's images now live in allPropertyImages, so this is
        // the same simple fill-in for every property, old or new.
        const images = Array.isArray(p.images) ? p.images : [];
        const filledImages = Array(MAX_CARDS).fill(null);
        const filledCategories = Array(MAX_CARDS).fill("");
        const filledCrops = Array(MAX_CARDS).fill(null); // ADD THIS

        images.forEach((img, i) => {
          if (i >= MAX_CARDS) return;
          filledImages[i] = { url: img.imageurl, file: null };
          filledCategories[i] = img.category || "";

          if (img.cropdata) {
            try {
              filledCrops[i] =
                typeof img.cropdata === "string"
                  ? JSON.parse(img.cropdata)
                  : img.cropdata;
            } catch (_) {}
          }
        });

        setSelectedImages(filledImages);
        setSelectedCategories(filledCategories);
        setSelectedCrops(filledCrops);
      } catch (err) {
        console.error("Error loading property:", err);
        setLoadError(err.message || "Failed to load property");
      } finally {
        setIsLoadingProperty(false);
      }
    };

    loadProperty();
  }, [propertyid]);
  // Auto-populate cities when state is set (for edit mode)
  // Auto-populate cities when state is set (for edit mode)
  useEffect(() => {
    if (selectedState && indiaStates.length > 0) {
      handleStateChange({ target: { value: selectedState } });
    }
  }, [selectedState, indiaStates.length]);
  const handleSubmit = async () => {
    if (isViewMode) return;
    setSubmitError("");

    const missingFields = [];
    if (!propertyname) missingFields.push("Property Name");
    if (!propertycode) missingFields.push("Property Code");
    if (!propertytype) missingFields.push("Property Type");
    if (!selectedState) missingFields.push("destination (State)");
    if (!location) missingFields.push("Location (City)");

    if (missingFields.length > 0) {
      const fieldList = missingFields.join(", ");
      toast.error(`Please fill: ${fieldList}`, {
        // position: "top-center",
        autoClose: 4000,
      });
      return;
    }

    const uncategorizedBannerIndex = selectedImages.findIndex(
      (img, index) => img?.file && !selectedCategories[index],
    );
    // if (uncategorizedBannerIndex !== -1) {
    //   setSubmitError(
    //     `Please select a category for banner slide ${uncategorizedBannerIndex + 1} before saving.`,
    //   );
    //   return;
    // }

    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadPhase("uploading");
    try {
      const formData = new FormData();

      // ── Simple text fields -> `properties` table ──
      formData.append("propertyname", propertyname);
      formData.append("description", description);
      formData.append("heading", heading);
      formData.append("content", content);
      formData.append("catchquote", catchyQuote);
      formData.append("maplang", maplang);
      formData.append("maplong", maplong);
      formData.append("propertycode", propertycode);
      formData.append("propertyowner", propertyowner);
      formData.append("airid", airid);
      formData.append("instagramreellink", instagramreellink);
      formData.append("propertybrochure", propertybrochure);
      formData.append("currentprice", bookingPrice);
      formData.append("defaultprice", defaultprice);
      formData.append("deposit", deposit);
      formData.append("maxadult", bookingAdults);
      formData.append("maxchild", bookingChildren);
      formData.append("maxbed", bookingRooms);
      formData.append("maxbath", maxbath);
      formData.append("maplink", maplink);
      formData.append("youtubelink", youtubelink);
      formData.append("conserlink", conserlink);
      formData.append("seasontype", seasontype.join(","));
      formData.append("propertytype", propertytype);
      formData.append("destination", selectedState);
      formData.append("location", location);
      formData.append("rating", rating);
      formData.append("propertycategory", propertycategory);
      formData.append("propertytag", propertytag);
      // ── Map / propertyImages text fields ──
      formData.append("maptitle", maptitle);
      formData.append("mapcontent", mapcontent);

      // ── Amenities (ids) ──
      formData.append("amenities", JSON.stringify(selectedAmenities));

      // ── Tailored experience ──
      formData.append("expnamemain", tailoredExperience);
      formData.append("expcontentmain", tailoredExperienceContent);

      // ── Merged top carousel ──
      // allPropertyImages is the source of truth for every property now.
      // Slides with a fresh upload go through banner_<position>(+_category);
      // slides that are unchanged (already-saved image, no new file) are
      // passed back as `existingimages` JSON so the backend keeps them
      // instead of deleting them when it rebuilds the table.
      const existingimages = [];
      selectedImages.forEach((img, index) => {
        if (!img) return;
        const position = index + 1;
        if (img.file) {
          formData.append(`banner_${position}`, img.file);
          formData.append(
            `banner_${position}_category`,
            selectedCategories[index] || "",
          );
          formData.append(
            `banner_${position}_crop`,
            selectedCrops[index] ? JSON.stringify(selectedCrops[index]) : "",
          ); // ADD THIS
        } else {
          existingimages.push({
            imageurl: img.url,
            position,
            category: selectedCategories[index] || "",
            cropdata: selectedCrops[index]
              ? JSON.stringify(selectedCrops[index])
              : null,
          });
        }
      });
      formData.append("existingimages", JSON.stringify(existingimages));

      // ── Tailored experience images -> `experiences` (imagegrid1..4) ──
      // New uploads go as files; unchanged ones are passed back as plain URL
      // strings; cleared ones are sent as an explicit empty string so the
      // backend can tell "untouched" apart from "deleted" and null the
      // column on save too (belt-and-suspenders alongside the instant
      // delete call in handleTailoredexpimageDelete).
      tailoredExpData.forEach((exp, index) => {
        if (exp?.file) {
          formData.append(`expimage${index + 1}`, exp.file);
        } else {
          formData.append(`imagegrid${index + 1}`, exp?.img || "");
        }
      });

      const { data } = await axios.put(`${API_URL}/${propertyid}`, formData, {
        onUploadProgress: (e) => {
          if (!e.total) return;
          const pct = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(pct);
          // Browser has finished sending bytes; server is now uploading
          // to Cloudflare and writing to the DB, with no progress signal.
          if (pct >= 100) setUploadPhase("finalizing");
        },
      });

      if (!data.success) {
        throw new Error(data.error || "Failed to update property");
      }

      setIsSubmitting(false);
      setUploadPhase("idle");
      navigate("/Properties");
    } catch (err) {
      console.error("Error saving property:", err);
      const message =
        err.response?.data?.error ||
        err.message ||
        "Something went wrong while saving.";
      setSubmitError(message);
      setIsSubmitting(false);
      setUploadPhase("idle");
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const [activeDragIndex, setActiveDragIndex] = useState(null);

  const handleDragStart = (event) => {
    setActiveDragIndex(parseInt(event.active.id));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragIndex(null);
    if (!over || active.id === over.id) return;

    const activeIdx = parseInt(active.id);
    const overIdx = parseInt(over.id);

    // Current order of filled slots, as original indices
    const order = selectedImages
      .map((img, i) => (img ? i : null))
      .filter((i) => i !== null);

    // Group drag if the dragged photo is one of several selected photos
    const isGroupDrag =
      selectedGalleryIndices.includes(activeIdx) &&
      selectedGalleryIndices.length > 1;

    const moving = isGroupDrag
      ? order.filter((i) => selectedGalleryIndices.includes(i)) // keeps their relative order
      : [activeIdx];
    const movingSet = new Set(moving);

    // Dropping onto one of the photos being moved = no-op
    if (movingSet.has(overIdx)) return;

    const rest = order.filter((i) => !movingSet.has(i));
    const movingForward = order.indexOf(overIdx) > order.indexOf(activeIdx);
    const insertAt = rest.indexOf(overIdx) + (movingForward ? 1 : 0);

    const newOrder = [
      ...rest.slice(0, insertAt),
      ...moving,
      ...rest.slice(insertAt),
    ];

    const rebuild = (arr, emptyValue) => {
      const result = Array(MAX_CARDS).fill(emptyValue);
      newOrder.forEach((orig, i) => {
        result[i] = arr[orig];
      });
      return result;
    };

    setSelectedImages((prev) => rebuild(prev, null));
    setSelectedCategories((prev) => rebuild(prev, ""));
    setSelectedCrops((prev) => rebuild(prev, null));

    // Indices shifted, so remap the selection to the new positions
    setSelectedGalleryIndices(
      newOrder
        .map((orig, pos) =>
          selectedGalleryIndices.includes(orig) ? pos : null,
        )
        .filter((p) => p !== null),
    );
  };

  const handleDragCancel = () => setActiveDragIndex(null);
  if (isLoadingProperty) {
    return (
      <div className="flex items-center justify-center w-full h-screen text-gray-500">
        Loading property…
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-screen text-red-600 gap-3">
        <p>{loadError}</p>
        <button
          onClick={() => navigate("/Properties")}
          className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm"
        >
          Back to Properties
        </button>
      </div>
    );
  }

  // Every image with a `.file` is a fresh upload (new or replaced) that
  // was converted to webp at selection time (see toWebpFile) — unchanged
  // images only carry a `.url` and aren't re-uploaded on save.
  const totalImagesToUpload =
    selectedImages.filter((img) => img?.file).length +
    tailoredExpData.filter((exp) => exp?.file).length;
  const isGroupDragging =
    activeDragIndex !== null &&
    selectedGalleryIndices.length > 1 &&
    selectedGalleryIndices.includes(activeDragIndex);

  const dragStackImages =
    activeDragIndex === null
      ? []
      : [
          activeDragIndex,
          ...(isGroupDragging
            ? selectedGalleryIndices.filter((i) => i !== activeDragIndex)
            : []),
        ]
          .map((i) => selectedImages[i])
          .filter(Boolean);

  return (
    <div
      className={`flex bg-[#F8F9FA] transition-all duration-500 ${
        isMobile ? "ml-0" : isSidebarOpen ? "w-[82vw]" : "w-[95vw]"
      }`}
    >
      {isSubmitting && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg w-80 p-6 text-center">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-green-800"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>

            <h3 className="mt-4 font-semibold text-gray-800">
              {uploadPhase === "finalizing"
                ? "Finalizing on server..."
                : "Saving property..."}
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              {totalImagesToUpload === 0
                ? "No new images to upload"
                : `${totalImagesToUpload} image${totalImagesToUpload === 1 ? "" : "s"} ready (converted to webp)`}
            </p>

            <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-green-800 h-2.5 rounded-full transition-all duration-200"
                style={{
                  width: `${uploadPhase === "finalizing" ? 100 : uploadProgress}%`,
                }}
              />
            </div>

            <p className="text-xs text-gray-500 mt-2">
              {uploadPhase === "finalizing"
                ? "Uploading to Cloudflare & saving..."
                : `Uploading... ${uploadProgress}%`}
            </p>
          </div>
        </div>
      )}

      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`p-5 lg:p-6 transition-all duration-500 w-full ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />

        {/* Header */}
        <div className="flex justify-between gap-5 items-start lg:items-center">
          <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center">
            <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
              Add New Property
            </p>
          </div>
        </div>

        {/* Data not to be shown in website */}
        <div className="mt-10">
          <p className="text-lg text-center mb-6">
            Required Data <i>(not to be displayed on the website)</i>
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 items-center gap-5">
            <div className="flex flex-col w-full">
              {/* goes to table properties as propertycode */}
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Property Code <sub className="text-red-500 text-base">*</sub>
              </label>
              <input
                placeholder="Enter property code"
                name="propertycode"
                required
                value={propertycode}
                disabled={isViewMode}
                onChange={(e) => setpropertycode(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as propertyowner */}
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Property's Owner name
              </label>
              <input
                placeholder="Enter Owner's name"
                name="propertyowner"
                value={propertyowner}
                disabled={isViewMode}
                onChange={(e) => setpropertyowner(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>
            <div className="flex flex-col w-full">
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Property ID
              </label>
              <input
                placeholder="Enter Airbnb ID"
                name="airid"
                value={airid}
                disabled={isViewMode}
                onChange={(e) => setairid(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>
            <div className="flex flex-col w-full">
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Instagram Reel Link
              </label>
              <input
                placeholder="Enter Instagram Reel Link"
                name="instagramreellink"
                value={instagramreellink}
                disabled={isViewMode}
                onChange={(e) => setinstagramreellink(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>
            <div className="flex flex-col w-full">
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Property Brochure
              </label>
              <input
                placeholder="Enter Drive Link"
                name="propertybrochure"
                value={propertybrochure}
                disabled={isViewMode}
                onChange={(e) => setPropertyBrochure(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as defaultprice */}
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Default Price
              </label>
              <input
                placeholder="Enter default price"
                name="defaultprice"
                value={defaultprice}
                disabled={isViewMode}
                onChange={(e) => setdefaultprice(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as deposit */}

              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Security Deposit
              </label>
              <input
                placeholder="Enter security deposit"
                name="deposit"
                value={deposit}
                disabled={isViewMode}
                onChange={(e) => setDeposit(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as maxadult
                  Synced with "Guests" (Adults) in the Booking Widget below —
                  both read/write the same `bookingAdults` state. */}

              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Max Adults
              </label>
              <input
                type="number"
                placeholder="Enter max adults"
                name="maxadult"
                value={bookingAdults}
                disabled={isViewMode}
                onChange={(e) => setBookingAdults(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as maxchild
                  Synced with "Guests" (Children) in the Booking Widget below —
                  both read/write the same `bookingChildren` state. */}

              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Max Children
              </label>
              <input
                type="number"
                placeholder="Enter max children"
                name="maxchild"
                value={bookingChildren}
                disabled={isViewMode}
                onChange={(e) => setBookingChildren(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as maxbed
                  Synced with "No. of Rooms" in the Booking Widget below —
                  both read/write the same `bookingRooms` state, so changing
                  either one updates the other automatically. */}

              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Max Bedroom
              </label>
              <input
                type="number"
                placeholder="Enter max bedroom"
                name="maxbed"
                value={bookingRooms}
                disabled={isViewMode}
                onChange={(e) => setBookingRooms(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as maxbath */}

              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Max Bathroom
              </label>
              <input
                placeholder="Enter max bathroom"
                name="maxbath"
                value={maxbath}
                disabled={isViewMode}
                onChange={(e) => setmaxbath(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as maplink */}
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Map Location
              </label>
              <input
                placeholder="Enter map location"
                name="maplink"
                value={maplink}
                disabled={isViewMode}
                onChange={(e) => setMaplink(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div className="flex flex-col w-full">
              {/* goes to table properties as youtubelink */}
              <label
                for="input"
                className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
              >
                Youtube Link
              </label>
              <input
                placeholder="Enter youtube link"
                name="youtubelink"
                value={youtubelink}
                disabled={isViewMode}
                onChange={(e) => setYoutubelink(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              />
            </div>

            <div
              className="flex flex-col w-full relative"
              ref={containerRef_seasontype}
            >
              {/* goes to table properties as seasontype */}
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Season Type
              </label>

              <button
                type="button"
                onClick={() => {
                  if (!isViewMode) {
                    setIsOpen_seasontype((prev) => !prev);
                  }
                }}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full text-left focus:outline-none focus:ring-0 focus:border-black focus:shadow-md flex justify-between items-center"
              >
                <span
                  className={seasontype.length ? "text-black" : "text-black/25"}
                >
                  {seasontype.length
                    ? seasontype.join(", ")
                    : "Select season type"}
                </span>
                <span className="text-gray-800">
                  <IoIosArrowDown />
                </span>
              </button>

              {isOpen_seasontype && (
                <div className="absolute top-full mt-1 left-0 w-full bg-white border border-gray-300 rounded-lg shadow-md z-30 p-2">
                  {["Summer", "Winter", "Monsoon"].map((season) => (
                    <label
                      key={season}
                      className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer select-none text-sm"
                    >
                      <input
                        type="checkbox"
                        disabled={isViewMode}
                        checked={seasontype.includes(season)}
                        onChange={() => toggleseasontype(season)}
                        className="accent-black w-4 h-4 cursor-pointer"
                      />
                      {season}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* destination (State) */}
            {/* <div className="flex flex-col w-full">
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Destination<sub className="text-red-500 text-base">*</sub>
              </label>
              <select
                name="destination"
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                value={selectedState}
                onChange={handleStateChange}
                disabled={isViewMode}
                required
              >
                <option value="">Select your destination</option>
                {indiaStates.map((state) => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div> */}
            <div className="flex flex-col w-full">
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit flex items-center gap-2">
                Destination<sub className="text-red-500 text-base">*</sub>
                {loadingStates && (
                  <span className="inline-block h-3 w-3 border-2 border-gray-300 border-t-green-800 rounded-full animate-spin" />
                )}
              </label>
              <select
                name="destination"
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md disabled:opacity-60 disabled:cursor-wait"
                value={selectedState}
                onChange={handleStateChange}
                disabled={isViewMode || loadingStates}
                required
              >
                <option value="">
                  {loadingStates
                    ? "Loading destinations..."
                    : "Select your destination"}
                </option>
                {indiaStates.map((state) => (
                  <option key={state.id} value={state.name}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Location (City) */}
            {/* <div className="flex flex-col w-full">
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Select Location <sub className="text-red-500 text-base">*</sub>
              </label>
              <select
                name="location"
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
                disabled={isViewMode}
              >
                <option value="">Select your location</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div> */}
            <div className="flex flex-col w-full">
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit flex items-center gap-2">
                Select Location <sub className="text-red-500 text-base">*</sub>
                {loadingCities && (
                  <span className="inline-block h-3 w-3 border-2 border-gray-300 border-t-green-800 rounded-full animate-spin" />
                )}
              </label>
              <select
                name="location"
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md disabled:opacity-60 disabled:cursor-wait"
                disabled={isViewMode || loadingCities || !selectedState}
              >
                <option value="">
                  {loadingCities
                    ? "Loading locations..."
                    : !selectedState
                      ? "Select a destination first"
                      : "Select your location"}
                </option>
                {cities.map((city) => (
                  <option key={city.id} value={city.name}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Property Type */}
            <div className="flex flex-col w-full">
              {/* goes to table properties as propertytype */}
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Property Type<sub className="text-red-500 text-base">*</sub>
              </label>

              <select
                name="propertytype"
                required
                value={propertytype}
                disabled={isViewMode}
                onChange={(e) => setpropertytype(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              >
                <option value="Villa">Villa</option>
                <option value="Apartment">Apartment</option>
              </select>
            </div>

            {/* Property Category */}
            <div className="flex flex-col w-full">
              {/* goes to table properties as propertytype */}
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Property Category
              </label>

              <select
                name="propertycategory"
                value={propertycategory}
                disabled={isViewMode}
                onChange={(e) => setpropertycategory(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              >
                <option value="">Select Category</option>
                <option value="Budget Friendly">Budget Friendly</option>
                <option value="Luxury">Luxury</option>
                <option value="Zorba Select">Zorba Select</option>
                {/* <option value="Apartments">Apartments</option> */}
              </select>
            </div>

            {/* Tags Property */}
            <div className="flex flex-col w-full">
              {/* goes to table properties as propertytype */}
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Property Tag
              </label>

              <select
                name="propertytag"
                value={propertytag}
                disabled={isViewMode}
                onChange={(e) => setpropertytag(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              >
                <option value="">Select Tag</option>
                <option value="Guest Favourite">Guest Favourite</option>
              </select>
            </div>

            {/* Rating */}
            <div className="flex flex-col w-full">
              <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
                Property Rating
              </label>

              <select
                name="rating"
                required
                value={rating}
                disabled={isViewMode}
                onChange={(e) => setRating(e.target.value)}
                className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
              >
                <option value="0">Enter Rating</option>
                <option value="5">5</option>
                <option value="4">4</option>
                <option value="3">3</option>
                <option value="2">2</option>
                <option value="1">1</option>
                <option value="0">0</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-3 items-center mx-[10%] md:mx-[25%] my-10 md:my-16 justify-center">
          <div className="h-[1px] w-full bg-gray-300" />
          <img
            src="../assets/zorba_logo.webp"
            alt="Img"
            className="h-4 w-auto"
          />
          <div className="h-[1px] w-full bg-gray-300" />
        </div>

        <div className="my-10">
          <div className="">
            <div className="flex flex-col items-center justify-center w-full max-w-3xl mx-auto text-center mt-8">
              <div className="w-full">
                {/* goes to table "properties" as "propertyname" */}
                <input
                  type="text"
                  name="propertyname"
                  required
                  maxLength={50}
                  value={propertyname}
                  onChange={(e) => setpropertyname(e.target.value)}
                  disabled={isViewMode}
                  className="w-full text-center text-2xl sm:text-3xl font-bold border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                  placeholder="Enter Property Name"
                />
                <p className="text-xs mt-1 text-center">
                  {propertyname.length.toString().padStart(2, "0")}/50
                </p>
                {/* goes to table "properties" as "description" */}
                <div className="my-4">
                  <textarea
                    rows="2"
                    name="description"
                    maxLength={250}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={isViewMode}
                    className="w-full text-center text-gray-700 text-lg border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                    placeholder="Enter Property Description..."
                  />
                  <p className="text-xs text-center">
                    {description.length.toString().padStart(2, "0")}/250
                  </p>
                </div>
              </div>
            </div>

            {/* Banner carousel */}
            <div className="mt-5 -mx-3 relative" ref={bannerWrapperRef}>
              {/* Images are stored in the `allPropertyImages` table with:
    propertyid, imageurl, and position (1, 2, 3, ...)
*/}

              <Carousel
                ref={bannerCarouselRef}
                responsive={responsive_BannerCarousel}
                autoPlay={false}
                autoPlaySpeed={3000}
                infinite={false}
                keyBoardControl
                showDots={false}
                customLeftArrow={<CustomLeftArrowBannerCarousel />}
                customRightArrow={<CustomRightArrowBannerCarousel />}
                afterChange={handleBannerAfterChange}
              >
                {[...Array(MAX_CARDS)].map((_, index) => (
                  <div key={index} className="mx-3">
                    <div className="flex flex-col items-center justify-between w-full h-[58vh] bg-white border-dashed border-2 border-gray-400 relative overflow-hidden">
                      {selectedImages[index] ? (
                        <>
                          <div className="absolute inset-0">
                            <CroppedBanner
                              imageurl={selectedImages[index]?.url}
                              cropdata={selectedCrops[index]}
                            />
                          </div>
                          {!isViewMode && (
                            <div
                              onClick={() => handleBannerImageDelete(index)}
                              className="absolute top-1 right-1 bg-white/60 rounded-full p-1 cursor-pointer hover:bg-white text-gray-600 hover:text-red-600 transition"
                            >
                              <MdDelete size={18} />
                            </div>
                          )}

                          {!isViewMode && (
                            <label className="absolute bottom-1 left-2 text-xs font-medium text-gray-600 bg-white/70 px-2 py-0.5 rounded cursor-pointer hover:underline">
                              <div className="flex gap-1 items-center">
                                Replace <FcAddImage size={20} />
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, index)}
                                disabled={isViewMode}
                                className="hidden"
                              />
                            </label>
                          )}
                          {!isViewMode && (
                            <button
                              type="button"
                              onClick={() => openCropModal(index)}
                              className="absolute bottom-1 left-[6.5rem] flex items-center h-6 px-2 text-xs font-medium text-gray-600 bg-white/70 px-2 py-0.5 rounded hover:underline"
                            >
                              Adjust
                            </button>
                          )}
                          {/* Per-slide category dropdown (mandatory once this slide has an image) */}
                          <div className="absolute bottom-14 right-3 z-10 flex flex-col items-end">
                            <select
                              name={`bannerCategory_${index}`}
                              value={selectedCategories[index] || ""}
                              onChange={(e) =>
                                handleBannerCategoryChange(
                                  index,
                                  e.target.value,
                                )
                              }
                              disabled={isViewMode}
                              className={`text-xs font-medium rounded-md px-2 py-1.5 max-w-[150px] focus:outline-none focus:ring-0 shadow-sm ${
                                selectedCategories[index]
                                  ? "bg-white/90 border border-gray-300 text-gray-800"
                                  : "bg-red-50/95 border border-red-500 text-red-700"
                              }`}
                            >
                              <option value="">Select category*</option>
                              {getBannerCategoryOptions().map((cat) => (
                                <option key={cat} value={cat}>
                                  {cat}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
                          <FcAddImage size={50} />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, index)}
                            disabled={isViewMode}
                            className="hidden"
                          />
                        </label>
                      )}

                      <div className="absolute top-1 left-2 text-sm font-semibold text-black/60 bg-white/70 rounded px-2 py-0.5">
                        {index + 1}
                      </div>
                    </div>
                  </div>
                ))}
              </Carousel>

              <div className="absolute z-20 bottom-2.5 right-6 flex gap-2">
                {!isViewMode && (
                  <label className="px-4 py-[7.5px] bg-green-800 hover:bg-green-700 rounded-md text-white text-xs font-medium shadow-sm transition-colors cursor-pointer whitespace-nowrap">
                    Upload Multiple
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleBulkImageUpload}
                      disabled={isViewMode}
                      className="hidden"
                    />
                  </label>
                )}
                <button
                  type="button"
                  onClick={() => setShowGalleryModal(true)}
                  className="px-4 py-[7.5px] bg-green-800 hover:bg-green-700 rounded-md text-white text-xs font-medium shadow-sm transition-colors whitespace-nowrap"
                >
                  View All
                </button>
              </div>

              {bannerCategoryError && (
                <p className="absolute z-20 bottom-14 right-3 bg-red-600 text-white text-xs px-3 py-1.5 rounded shadow-sm max-w-xs text-right">
                  {bannerCategoryError}
                </p>
              )}
            </div>
          </div>

          {/* View All Gallery Modal */}
          {/* {showGalleryModal && (
            <div
              className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
              onClick={() => setShowGalleryModal(false)}
            >
              <div
                className="bg-white rounded-xl w-full max-w-5xl max-h-[85vh] overflow-y-auto pt-0 p-6 relative"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setShowGalleryModal(false)}
                  className="sticky top-3 ml-auto block z-50 text-gray-500 hover:text-black text-xl font-semibold px-2"
                >
                  ✕
                </button>

              
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    All Photos{" "}
                    <span className="text-sm font-normal text-gray-400">
                      ({selectedImages.filter(Boolean).length})
                    </span>
                  </h3>
                  <p className="text-xs text-gray-400 mb-3">
                    Select photos below, choose a category, then click Apply.
                  </p>

                  {selectedImages.filter(Boolean).length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      No photos added to the carousel yet.
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                        {selectedImages.map((img, index) =>
                          img ? (
                            <div
                              key={index}
                              onClick={() => toggleGallerySelection(index)}
                              className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                                selectedGalleryIndices.includes(index)
                                  ? "border-green-900 ring-2 ring-green-900/40"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <img
                                src={img.url}
                                alt={`Photo ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                              {selectedCategories[index] && (
                                <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">
                                  {selectedCategories[index]}
                                </span>
                              )}
                              <div
                                className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center border ${
                                  selectedGalleryIndices.includes(index)
                                    ? "bg-green-500 border-green-900 text-white"
                                    : "bg-white/80 border-gray-300"
                                }`}
                              >
                                {selectedGalleryIndices.includes(index) && (
                                  <Check size={12} strokeWidth={3} />
                                )}
                              </div>
                            </div>
                          ) : null,
                        )}
                      </div>

                      {selectedGalleryIndices.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <span className="text-sm text-gray-600">
                            {selectedGalleryIndices.length} selected
                          </span>
                          <select
                            value={bulkCategoryValue}
                            onChange={(e) =>
                              setBulkCategoryValue(e.target.value)
                            }
                            className="text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:border-black"
                          >
                            <option value="">Select category</option>
                            {getBannerCategoryOptions().map((cat) => (
                              <option key={cat} value={cat}>
                                {cat}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={applyBulkCategory}
                            disabled={!bulkCategoryValue}
                            className="text-sm bg-green-900 text-white rounded-md px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-700"
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedGalleryIndices([])}
                            className="text-sm text-gray-500 hover:text-black px-2"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <hr className="border-gray-200 mb-6" />

             
                {getGroupedBannerGallery().length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No images have been assigned a category yet.
                  </p>
                ) : (
                  getGroupedBannerGallery().map(([category, images]) => (
                    <div key={category} className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
                        {category}{" "}
                        <span className="text-sm font-normal text-gray-400">
                          ({images.length})
                        </span>
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {images.map((img) => (
                          <div
                            key={img.index}
                            className="aspect-square rounded-lg overflow-hidden border border-gray-200"
                          >
                            <img
                              src={img.url}
                              alt={category}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )} */}
          {/* View All Gallery Modal */}
          {showGalleryModal && (
            <div
              className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
              onClick={() => setShowGalleryModal(false)}
            >
              <div
                className="bg-white rounded-xl w-full max-w-5xl max-h-[85vh] flex flex-col relative overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Scrollable content area */}
                <div className="overflow-y-auto p-6 pt-0 flex-1">
                  <button
                    type="button"
                    onClick={() => setShowGalleryModal(false)}
                    className="sticky top-3 ml-auto block z-50 text-gray-500 hover:text-black text-xl font-semibold px-2"
                  >
                    ✕
                  </button>

                  {/* ── All Photos: select + bulk-assign category ── */}
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-lg font-semibold text-gray-800">
                        All Photos{" "}
                        <span className="text-sm font-normal text-gray-400">
                          ({selectedImages.filter(Boolean).length})
                        </span>
                      </h3>
                    </div>
                    <p className="text-xs text-gray-400 mb-3">
                      Select photos below, choose a category, then click Apply.
                    </p>

                    {/* Sticky bulk-action footer — only takes up space when something is selected */}
                    {selectedGalleryIndices.length > 0 && (
                      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 bg-gray-50 border-t border-gray-200 w-full px-5 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)] flex-shrink-0">
                        <span className="text-sm text-gray-600">
                          {selectedGalleryIndices.length} selected
                        </span>
                        <select
                          value={bulkCategoryValue}
                          disabled={isViewMode}
                          onChange={(e) => setBulkCategoryValue(e.target.value)}
                          className="text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:border-black"
                        >
                          <option value="">Select category</option>
                          {getBannerCategoryOptions().map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={applyBulkCategory}
                          disabled={!bulkCategoryValue}
                          className="text-sm bg-green-900 text-white rounded-md px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-700"
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeleteConfirm(true)}
                          className="text-sm bg-red-600 text-white rounded-md px-3 py-2 hover:bg-red-700 flex items-center gap-1"
                        >
                          <MdDelete size={14} /> Delete Selected
                        </button>

                        {allGalleryIndices.length > 0 && (
                          <button
                            type="button"
                            onClick={toggleSelectAllGallery}
                            className="ml-auto text-sm text-gray-500 hover:text-black px-2"
                          >
                            {isAllGallerySelected
                              ? "Deselect All"
                              : "Select All"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setSelectedGalleryIndices([])}
                          className="text-sm text-gray-500 hover:text-black px-2 mr-3"
                        >
                          Clear
                        </button>
                      </div>
                    )}

                    {selectedImages.filter(Boolean).length === 0 ? (
                      <p className="text-gray-500 text-sm">
                        No photos added to the carousel yet.
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          onDragCancel={handleDragCancel}
                        >
                          <SortableContext
                            items={selectedImages
                              .map((_, idx) => idx.toString())
                              .filter((_, i) => selectedImages[i] !== null)}
                            strategy={rectSortingStrategy}
                          >
                            {selectedImages.map((img, index) =>
                              img ? (
                                <SortableImage
                                  key={index}
                                  img={img}
                                  index={index}
                                  isSelected={selectedGalleryIndices.includes(
                                    index,
                                  )}
                                  isGroupFollower={
                                    activeDragIndex !== null &&
                                    activeDragIndex !== index &&
                                    selectedGalleryIndices.length > 1 &&
                                    selectedGalleryIndices.includes(
                                      activeDragIndex,
                                    ) &&
                                    selectedGalleryIndices.includes(index)
                                  }
                                  onToggle={toggleGallerySelection}
                                  onView={setViewImage}
                                  category={selectedCategories[index]}
                                />
                              ) : null,
                            )}
                          </SortableContext>
                          <DragOverlay
                            dropAnimation={{ duration: 200, easing: "ease" }}
                          >
                            {activeDragIndex !== null &&
                            dragStackImages.length > 0 ? (
                              <DragStack
                                images={dragStackImages}
                                count={
                                  isGroupDragging
                                    ? selectedGalleryIndices.length
                                    : 1
                                }
                              />
                            ) : null}
                          </DragOverlay>
                        </DndContext>
                      </div>
                    )}
                  </div>

                  <hr className="border-gray-200 mb-6" />

                  {/* ── Existing grouped-by-category view ── */}
                  {getGroupedBannerGallery().length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      No images have been assigned a category yet.
                    </p>
                  ) : (
                    getGroupedBannerGallery().map(([category, images]) => (
                      <div key={category} className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
                          {category}{" "}
                          <span className="text-sm font-normal text-gray-400">
                            ({images.length})
                          </span>
                        </h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {images.map((img) => (
                            <div
                              key={img.index}
                              className="aspect-square rounded-lg overflow-hidden border border-gray-200 relative group"
                            >
                              <img
                                src={img.url}
                                alt={category}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setViewImage({
                                    url: img.url,
                                    label: category,
                                  })
                                }
                                className="absolute inset-0 bg-black/0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-black/30 transition-all"
                              >
                                <Eye size={20} className="text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* delete-selected confirmation modal */}
          {showDeleteConfirm && (
            <div
              className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
              onClick={() => setShowDeleteConfirm(false)}
            >
              <div
                className="bg-white rounded-xl w-full max-w-sm p-6 text-center"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Delete {selectedGalleryIndices.length} image
                  {selectedGalleryIndices.length === 1 ? "" : "s"}?
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Any of these already saved to this property will be removed
                  from Cloudflare and the database right away — this can't be
                  undone. Freshly-added photos that haven't been saved yet are
                  just cleared from this form.
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-5 py-2 rounded-md bg-gray-800 text-white text-sm hover:bg-gray-700"
                  >
                    Keep them
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteSelectedImages}
                    className="px-5 py-2 flex items-center gap-2 rounded-md bg-red-600 text-white text-sm hover:bg-black transition"
                  >
                    <MdDelete size={14} /> Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* delete-in-progress overlay */}
          {isDeletingImages && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
              <div className="bg-white rounded-xl shadow-lg w-80 p-6 text-center">
                <svg
                  className="animate-spin h-8 w-8 mx-auto text-red-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <h3 className="mt-4 font-semibold text-gray-800">
                  Deleting images...
                </h3>
                <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className="bg-red-600 h-2.5 rounded-full transition-all duration-150"
                    style={{
                      width: `${
                        deleteProgress.total
                          ? (deleteProgress.done / deleteProgress.total) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {deleteProgress.done} of {deleteProgress.total} deleted
                </p>
              </div>
            </div>
          )}

          {/* crop modal */}
          {cropModalIndex !== null && (
            <div
              className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
              onClick={() => setCropModalIndex(null)}
            >
              <div
                className="bg-white rounded-xl p-5"
                style={{ width: cropBoxSize.width + 40 }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-sm font-semibold text-gray-800 mb-3">
                  Adjust image — slide {cropModalIndex + 1}
                </h3>

                <div
                  className="relative bg-gray-100 rounded-lg overflow-hidden mx-auto"
                  style={{
                    width: cropBoxSize.width,
                    height: cropBoxSize.height,
                  }}
                >
                  <Cropper
                    image={selectedImages[cropModalIndex]?.url}
                    crop={cropPosition}
                    zoom={cropZoom}
                    aspect={bannerAspect}
                    onCropChange={setCropPosition}
                    onZoomChange={setCropZoom}
                    onCropComplete={handleCropComplete}
                  />
                </div>

                <div className="flex items-center gap-3 mt-4">
                  <span className="text-xs text-gray-500">Zoom</span>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.05}
                    value={cropZoom}
                    onChange={(e) => setCropZoom(parseFloat(e.target.value))}
                    disabled={isViewMode}
                    className="flex-1"
                  />
                </div>

                <div className="flex justify-end gap-2 mt-5">
                  <button
                    type="button"
                    onClick={() => setCropModalIndex(null)}
                    className="text-sm px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveCrop}
                    className="text-sm px-4 py-2 rounded-md bg-green-900 text-white hover:bg-green-700"
                  >
                    Save crop position
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* View Image Modal */}
          {viewImage && (
            <div
              className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
              onClick={() => setViewImage(null)}
            >
              <div
                className="relative max-w-4xl max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setViewImage(null)}
                  className="absolute -top-9 right-0 text-white/80 hover:text-white text-xl font-semibold px-2"
                >
                  ✕
                </button>
                <img
                  src={viewImage.url}
                  alt={viewImage.label}
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                />
                {viewImage.label && (
                  <p className="text-white/70 text-xs text-center mt-2">
                    {viewImage.label}
                  </p>
                )}
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1">
                <div className="order-1 lg:order-2 mt-5 lg:mt-10 text-left">
                  {/* goes to table "properties" as "heading" */}
                  <input
                    type="text"
                    maxLength={100}
                    value={heading}
                    onChange={(e) => setHeading(e.target.value)}
                    className="fontPD flex w-full text-left text-2xl md:text-3xl lg:text-4xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                    style={{
                      letterSpacing: "1px",
                      wordSpacing: "2px",
                      lineHeight: 1.3,
                    }}
                    disabled={isViewMode}
                    placeholder="Heading"
                  />
                  <p className="text-xs text-left block mt-1">
                    {heading.length.toString().padStart(2, "0")}/100
                  </p>

                  {/* goes to table "properties" as "content" */}
                  <div className="my-5 lg:my-10">
                    <div
                      className={`text-base text-left border border-gray-100 bg-white overflow-hidden ${
                        isViewMode ? "pointer-events-none opacity-70" : ""
                      }`}
                    >
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={content}
                        onChange={handleContentChange}
                        // onChange={setContent}
                        readOnly={isViewMode}
                        placeholder="Content..."
                        modules={{
                          toolbar: isViewMode
                            ? false
                            : [["bold", "italic", "underline"], ["clean"]],
                        }}
                      />
                    </div>

                    <span
                      className={`text-xs block mt-1 text-right ${
                        textLength >= MAX_CHARS
                          ? "text-red-500"
                          : "text-gray-500"
                      }`}
                    >
                      {textLength}/{MAX_CHARS}
                    </span>

                    {/* <span className="text-xs block mt-1 text-right text-gray-500">
                      {content.replace(/<[^>]*>/g, "").length}/1500
                    </span> */}
                  </div>

                  {/* <div className="my-5 lg:my-10">
                    <textarea
                      type="text"
                      rows="3"
                      maxLength={1500}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      disabled={isViewMode}
                      className="flex w-full text-left border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                      placeholder="Content..."
                    />
                    <span className="text-x block mt-1">
                      {content.length.toString().padStart(2, "0")}/1500
                    </span>
                  </div> */}
                </div>
                {/* goes to table "properties" as "catchquote" */}

                <div className="mt-5 lg:mt-0 order-3">
                  <input
                    type="text"
                    maxLength={100}
                    value={catchyQuote}
                    onChange={(e) => setCatchyQuote(e.target.value)}
                    disabled={isViewMode}
                    className="flex w-full md:text-xl text-left border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                    placeholder="Catchy Quote"
                  />
                  <p className="text-xs text-left block mt-1">
                    {catchyQuote.length.toString().padStart(2, "0")}/100
                  </p>

                  <div className="flex gap-3 items-center mx-[10%] md:mx-[25%] my-5 justify-center">
                    <div className="h-[1px] w-full bg-gray-300" />
                    <img
                      src="../assets/zorba_logo.webp"
                      alt="Img"
                      className="h-4 w-auto"
                    />
                    <div className="h-[1px] w-full bg-gray-300" />
                  </div>
                </div>
              </div>
              {/* Carousel of Amenities Experience */}
              <div className="mt-5 mb-10" ref={containerRef_amenities}>
                <div className="text-center mb-4">
                  <h2 className="text-2xl font-semibold">Amenities</h2>
                  <p className="text-gray-500 mt-1">
                    {selectedAmenities.length} selected
                  </p>
                </div>

                <input
                  type="text"
                  placeholder="Search amenities..."
                  className="w-full mb-4 px-4 py-3 border rounded-lg focus:outline-none focus:ring-0 focus:border-[1.5px] focus:border-black"
                  value={searchTerm}
                  onFocus={() => setIsOpen_amenities(true)}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {isOpen_amenities && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 overflow-y-auto max-h-96">
                    {filteredAmenities.map((amenity, index) => {
                      const isSelected = selectedAmenities.includes(amenity.id);

                      return (
                        <div
                          key={index}
                          onClick={() => {
                            if (!isViewMode) {
                              toggleAmenity(amenity.id);
                            }
                          }}
                          className={`relative rounded-xl border px-3 py-5 transition-all duration-200 ${
                            isViewMode ? "cursor-default" : "cursor-pointer"
                          } ${
                            isSelected
                              ? "border-black shadow-md"
                              : "border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          {/* Tick */}
                          {isSelected && (
                            <div className="absolute top-3 right-3 bg-green-800 text-white rounded-full p-1">
                              <Check size={16} />
                            </div>
                          )}

                          {/* Icon */}
                          <img
                            src={amenity.url}
                            alt={amenity.name}
                            className="w-auto h-20 mb-3 object-contain"
                          />

                          {/* Text */}
                          <p className="text-base font-medium">
                            {amenity.name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Wrapping Carousel for all 5 image categories */}
              {/* Bedroom Carousel — sourced from the banner images assigned to
                  "Bedroom 1", "Bedroom 2", ... "Bedroom N" categories above. */}
              <div className="mt-8 mb-5 border border-gray-200 p-4 rounded-xl shadow-sm bg-gray-50 -mx-3 md:mx-0">
                {/* 
  Bedroom structure:
  - One row per bedroom in `allPropertyBedrooms`
  • propertyid
  • position = roomIndex + 1

  - One row per image in `allPropertyBedroomImages`, sourced from the banner
    images tagged with category "Bedroom {position}"
  • bedroomID (FK -> allPropertyBedrooms.id)
  • imageurl
  • position = imgIndex + 1
  */}
                <Carousel
                  key={`bedroom-outer-carousel-${parseInt(bookingRooms) || 1}`}
                  responsive={responsive_OuterCarousel}
                  autoPlay={false}
                  infinite={true}
                  showDots={true}
                  keyBoardControl={true}
                  arrows={false}
                >
                  {Array.from({ length: parseInt(bookingRooms) || 1 }).map(
                    (_, roomIndex) => {
                      const images = getBedroomImages(roomIndex);
                      return (
                        <div key={roomIndex} className="px-4 pb-8">
                          <h3 className="text-center font-bold text-lg mb-4">
                            Bedroom {roomIndex + 1} Images{" "}
                            <span className="text-sm font-normal text-gray-400">
                              ({images.length})
                            </span>
                          </h3>

                          {images.length === 0 ? (
                            <div className="flex flex-col items-center justify-center w-full aspect-[4/3] max-h-[350px] bg-white border-dashed border-2 border-gray-300 rounded-lg text-center px-4">
                              <p className="text-sm text-gray-400">
                                No images assigned yet. Add images in the banner
                                above and set their category to "Bedroom{" "}
                                {roomIndex + 1}".
                              </p>
                            </div>
                          ) : (
                            <Carousel
                              key={`bedroom-inner-carousel-${roomIndex}-${images.length}`}
                              responsive={responsive_ImageCarouselOne}
                              autoPlay={false}
                              autoPlaySpeed={3000}
                              infinite
                              keyBoardControl
                              showDots={showDots}
                              customLeftArrow={<CustomLeftArrowPlain />}
                              customRightArrow={<CustomRightArrowPlain />}
                            >
                              {images.map((img, imgIndex) => (
                                <div key={img.bannerIndex} className="mx-3">
                                  <div className="flex flex-col items-center justify-between w-full aspect-[4/3] max-h-[350px] bg-white border-dashed border-2 border-gray-400 relative overflow-hidden">
                                    <img
                                      src={img.url}
                                      alt={`Bedroom ${roomIndex + 1} - ${imgIndex + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                    {!isViewMode && (
                                      <div
                                        onClick={() =>
                                          handleBannerImageDelete(
                                            img.bannerIndex,
                                          )
                                        }
                                        title="Remove this image from the banner"
                                        className="absolute top-1 right-1 bg-white/60 rounded-full p-1 cursor-pointer hover:bg-white text-gray-600 hover:text-red-600 transition"
                                      >
                                        <MdDelete size={18} />
                                      </div>
                                    )}
                                    <div className="absolute bottom-1 right-2 text-sm font-semibold text-black/60 bg-white/70 rounded px-2 py-0.5">
                                      {imgIndex + 1}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </Carousel>
                          )}
                        </div>
                      );
                    },
                  )}
                </Carousel>
              </div>
              {/* Map */}
              <div className="mt-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-5">
                  <div>
                    {/* goes to table "propertyImages" as maptitle */}
                    <textarea
                      type="text"
                      maxLength={50}
                      value={maptitle}
                      onChange={(e) => setmaptitle(e.target.value)}
                      rows={2}
                      className="w-full text-2xl md:text-3xl lg:text-4xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                      style={{
                        letterSpacing: "1px",
                        wordSpacing: "2px",
                        lineHeight: 1.3,
                      }}
                      disabled={isViewMode}
                      placeholder="Map Title"
                    />
                    <p className="text-xs block">
                      {maptitle.length.toString().padStart(2, "0")}/50
                    </p>
                  </div>

                  <div>
                    {/* goes to table "propertyImages" as mapcontent */}
                    <textarea
                      type="text"
                      maxLength={150}
                      value={mapcontent}
                      onChange={(e) => setmapcontent(e.target.value)}
                      rows={4}
                      className="w-full border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                      placeholder="Map Content..."
                      disabled={isViewMode}
                    />
                    <p className="text-xs block">
                      {mapcontent.length.toString().padStart(2, "0")}/150
                    </p>
                  </div>
                </div>
                {/* goes to table "properties" as maplang  */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Map Latitude (maplang)
                    </label>
                    <input
                      type="number"
                      step="0.00000001"
                      name="maplang"
                      value={maplang}
                      onChange={(e) => setmaplang(e.target.value)}
                      disabled={isViewMode}
                      placeholder="Enter Latitude"
                      className="w-full text-2xl md:text-3xl lg:text-xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                    />
                  </div>
                  {/* goes to table "properties" as  maplong */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Map Longitude (maplong)
                    </label>
                    <input
                      type="number"
                      step="0.00000001"
                      name="maplong"
                      value={maplong}
                      onChange={(e) => setmaplong(e.target.value)}
                      disabled={isViewMode}
                      placeholder="Enter Longitude"
                      className="w-full text-2xl md:text-3xl lg:text-xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                    />
                  </div>
                </div>
              </div>
            </div>
            {/* Side StickyCard */}
            <div className="lg:col-span-1 relative">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 pb-4 sticky top-10">
                <div className="mb-4">
                  <div className="flex items-center text-4xl font-bold text-[#A23321]">
                    ₹ {/* goes to table properties as currentprice */}
                    <input
                      type="text"
                      value={bookingPrice}
                      placeholder="Enter Current Price"
                      onChange={(e) => setBookingPrice(e.target.value)}
                      disabled={isViewMode}
                      className="text-xl w-full bg-transparent border-b border-gray-300 focus:border-black focus:ring-0 px-1 py-0"
                    />
                  </div>
                  {/* <input
                    type="text"
                    value={bookingPriceDetails}
                    onChange={(e) => setBookingPriceDetails(e.target.value)}
                    className="text-base text-gray-400 w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 px-1 py-0 mt-1"
                  /> */}
                </div>

                {/* <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-sm text-gray-500 font-medium block mb-1">
                      Check-in ▾
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                      <input
                        type="date"
                        value={bookingcheckinDate}
                        onChange={(e) => setBookingcheckinDate(e.target.value)}
                        className="text-base text-gray-800 font-medium w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0"
                      />
                      <input
                        type="time"
                        value={bookingcheckintime}
                        onChange={(e) => setBookingcheckintime(e.target.value)}
                        className="text-sm text-gray-500 w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0 mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 font-medium block mb-1">
                      Check-out ▾
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                      <input
                        type="date"
                        value={bookingcheckoutDate}
                        onChange={(e) => setBookingcheckoutDate(e.target.value)}
                        className="text-base text-gray-800 font-medium w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0"
                      />
                      <input
                        type="time"
                        value={bookingcheckouttime}
                        onChange={(e) => setBookingcheckouttime(e.target.value)}
                        className="text-sm text-gray-500 w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0 mt-1"
                      />
                    </div>
                  </div>
                </div> */}

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-sm text-gray-500 font-medium block mb-1">
                      Guests ▾
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
                      <div className="flex items-center border-b border-gray-200 focus-within:border-black">
                        {/* goes to table properties as maxadult */}
                        <input
                          type="number"
                          value={bookingAdults}
                          disabled={isViewMode}
                          onChange={(e) => setBookingAdults(e.target.value)}
                          className="text-base text-gray-800 font-medium w-12 bg-transparent border-none focus:ring-0 p-0"
                        />
                        <span className="text-base text-gray-800 font-medium ml-1">
                          Adults,
                        </span>
                      </div>
                      <div className="flex items-center border-b border-gray-200 mt-1 focus-within:border-black">
                        {/* goes to table properties as maxchild */}
                        <input
                          type="number"
                          value={bookingChildren}
                          disabled={isViewMode}
                          onChange={(e) => setBookingChildren(e.target.value)}
                          className="text-base text-gray-800 font-medium w-12 bg-transparent border-none focus:ring-0 p-0"
                        />
                        <span className="text-base text-gray-800 font-medium ml-1">
                          Children
                        </span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-500 font-medium block mb-1">
                      No. of Rooms ▾
                    </label>
                    <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
                      <div className="flex items-center justify-center border-b border-gray-200 focus-within:border-black">
                        {/* goes to table properties as maxbed
                            Synced with "Max Bedroom" in the Required Data
                            section above — same `bookingRooms` state. */}
                        <input
                          type="number"
                          value={bookingRooms}
                          disabled={isViewMode}
                          onChange={(e) => setBookingRooms(e.target.value)}
                          className="text-base text-gray-800 font-medium w-12 text-right bg-transparent border-none focus:ring-0 p-0"
                        />
                        <span className="text-base text-gray-800 font-medium ml-2">
                          Rooms
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="border-gray-200 mb-4" />

                <div className="flex justify-between items-center mb-0">
                  <p className="text-2xl font-bold text-gray-800">Total</p>
                  <div className="text-right">
                    <div className="flex items-center justify-end text-2xl font-bold text-[#A23321]">
                      {bookingPrice && !isNaN(bookingPrice) ? (
                        <>
                          ₹{" "}
                          {(parseFloat(bookingPrice) * 1.18).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )}
                        </>
                      ) : (
                        "N/A"
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-1">(Incl. taxes)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Break */}
          <div className="flex gap-3 items-center mx-5 md:mx-[5%] my-8 md:my-14 justify-center">
            <div className="h-[1px] w-full bg-gray-300" />
            <img
              src="../assets/zorba_logo.webp"
              alt="Img"
              className="h-4 w-auto"
            />
            <div className="h-[1px] w-full bg-gray-300" />
          </div>
          {/* Break over  */}

          {/* Tailored Experience */}
          <div className="">
            <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-5">
              <div>
                {/* goes to table "experiences" as "expnamemain" */}
                <input
                  type="text"
                  placeholder="Experience"
                  maxLength={50}
                  value={tailoredExperience}
                  disabled={isViewMode}
                  onChange={(e) => setTailoredExperience(e.target.value)}
                  className="w-full text-2xl md:text-3xl lg:text-4xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                  style={{
                    letterSpacing: "1px",
                    wordSpacing: "2px",
                    lineHeight: 1.3,
                  }}
                />
                <p className="text-xs block mt-1">
                  {tailoredExperience.length.toString().padStart(2, "0")}/50
                </p>

                {/* goes to table "experiences" as "expcontentmain" */}
              </div>

              <div className="space-y-5">
                <div>
                  <textarea
                    type="text"
                    rows={3}
                    placeholder="Content..."
                    value={tailoredExperienceContent}
                    onChange={(e) =>
                      setTailoredExperienceContent(e.target.value)
                    }
                    maxLength={150}
                    disabled={isViewMode}
                    className="w-full border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                  />
                  <p className="text-xs block">
                    {tailoredExperienceContent.length
                      .toString()
                      .padStart(2, "0")}
                    /150
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Concierge Brochure
                  </label>
                  <input
                    type="text"
                    name="conserlink"
                    placeholder="Enter PDF Link"
                    value={conserlink}
                    disabled={isViewMode}
                    onChange={(e) => setconserlink(e.target.value)}
                    className="w-full border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
                  />
                </div>
              </div>
            </div>

            {/* Carousel of Tailored Experience */}
            <div className="mt-5 -mx-3">
              {/* goes to table "experiences" as "imagegrid1	, imagegrid2, imagegrid3 and imagegrid4" */}
              <Carousel
                ref={carouselRef}
                responsive={responsive_TailoredExpCarousel}
                autoPlay={false}
                autoPlaySpeed={3000}
                infinite
                keyBoardControl
                showDots={showDots}
                customLeftArrow={<CustomLeftArrowTailoredExpCarousel />}
                customRightArrow={<CustomRightArrowTailoredExpCarousel />}
                beforeChange={(nextSlide) => setActiveCardIndex(nextSlide)}
              >
                {[...Array(MAX_CARDS_Exp)].map((_, index) => (
                  <div key={index} className="mx-3">
                    <div className="relative w-full h-[350px] bg-white border-2 border-dashed border-gray-400 overflow-hidden">
                      {tailoredExpData[index]?.img ? (
                        <>
                          <img
                            src={tailoredExpData[index].img}
                            alt="Img"
                            className="w-full h-full object-cover"
                          />

                          {!isViewMode && (
                            <div
                              onClick={() =>
                                handleTailoredexpimageDelete(index)
                              }
                              className="absolute top-1 right-1 bg-white/60 rounded-full p-1 cursor-pointer hover:bg-white text-gray-600 hover:text-red-600 transition"
                            >
                              <MdDelete size={18} />
                            </div>
                          )}
                          {!isViewMode && (
                            <label className="absolute bottom-1 left-2 text-xs font-medium text-gray-600 bg-white/70 px-2 py-0.5 rounded cursor-pointer hover:underline">
                              <div className="flex gap-1 items-center">
                                Replace <FcAddImage size={20} />
                              </div>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  e.target.value = "";
                                  if (!file) return;
                                  let webpFile;
                                  try {
                                    webpFile = await toWebpFile(file);
                                  } catch (err) {
                                    return;
                                  }
                                  const newData = [...tailoredExpData];
                                  newData[index].img =
                                    URL.createObjectURL(webpFile); // preview
                                  newData[index].file = webpFile; // actual File for backend
                                  setTailoredExpData(newData);
                                }}
                                disabled={isViewMode}
                                className="hidden"
                              />
                            </label>
                          )}
                        </>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
                          <FcAddImage size={50} />
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              e.target.value = "";
                              if (!file) return;
                              let webpFile;
                              try {
                                webpFile = await toWebpFile(file);
                              } catch (err) {
                                return;
                              }
                              const newData = [...tailoredExpData];
                              newData[index].img =
                                URL.createObjectURL(webpFile);
                              newData[index].file = webpFile;
                              setTailoredExpData(newData);
                            }}
                            disabled={isViewMode}
                            className="hidden"
                          />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </Carousel>

              <div className="flex gap-5 items-center justify-center md:hidden text-sm text-gray-400 mt-3">
                <FaChevronLeft
                  onClick={handlePrevSlide}
                  className="cursor-pointer"
                />
                Showing {(activeCardIndex % TailoredExpCarousel.length) + 1} /
                {TailoredExpCarousel.length}
                <FaChevronRight
                  onClick={handleNextSlide}
                  className="cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 mb-5">
          {submitError && (
            <p className="text-red-600 text-sm mt-4">{submitError}</p>
          )}
          {!isViewMode && (
            <div className="grid grid-cols-2 gap-3 mt-10 w-72 mx-auto">
              <button
                type="button"
                onClick={() => navigate("/Properties")}
                className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EditPropertyDetailElementor;

//working
// import { useEffect, useRef, useState } from "react";
// import axios from "axios";
// import Menubar from "../Menubar/Menubar";
// import { Outlet } from "react-router-dom";
// import Carousel from "react-multi-carousel";
// import "react-multi-carousel/lib/styles.css";
// import { FaChevronRight } from "react-icons/fa6";
// import { FaChevronLeft } from "react-icons/fa6";
// import { FcAddImage } from "react-icons/fc";
// import {
//   useNavigate,
//   useParams,
//   useLocation,
//   useSearchParams,
// } from "react-router-dom";
// import { Check } from "lucide-react";
// import { MdDelete } from "react-icons/md";
// import { IoIosArrowDown } from "react-icons/io";
// import { toast } from "react-toastify";
// import Cropper from "react-easy-crop";
// import { Eye } from "lucide-react";
// import { API_URL } from "../../Config";

// import {
//   DndContext,
//   closestCenter,
//   PointerSensor,
//   useSensor,
//   useSensors,
// } from "@dnd-kit/core";
// import {
//   arrayMove,
//   SortableContext,
//   useSortable,
//   rectSortingStrategy,
// } from "@dnd-kit/sortable";
// import { CSS } from "@dnd-kit/utilities";

// // Converts a jpg/png/etc File into a webp File entirely in the browser
// // (decode via createImageBitmap, encode via canvas.toBlob). If the file is
// // already webp it's returned untouched. Nothing is uploaded or downloaded —
// // this only happens in memory.
// const WEBP_QUALITY = 0.85;

// async function toWebpFile(file, quality = WEBP_QUALITY) {
//   if (!file || file.type === "image/webp") return file;

//   let bitmap;
//   try {
//     bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
//   } catch (err) {
//     // Fallback for browsers without createImageBitmap support
//     bitmap = await new Promise((resolve, reject) => {
//       const img = new Image();
//       const url = URL.createObjectURL(file);
//       img.onload = () => {
//         URL.revokeObjectURL(url);
//         resolve(img);
//       };
//       img.onerror = () => {
//         URL.revokeObjectURL(url);
//         reject(new Error("Could not read image"));
//       };
//       img.src = url;
//     });
//   }

//   const width = bitmap.width || bitmap.naturalWidth;
//   const height = bitmap.height || bitmap.naturalHeight;
//   const canvas = document.createElement("canvas");
//   canvas.width = width;
//   canvas.height = height;
//   const ctx = canvas.getContext("2d");
//   ctx.drawImage(bitmap, 0, 0);
//   if (typeof bitmap.close === "function") bitmap.close();

//   const webpBlob = await new Promise((resolve, reject) => {
//     canvas.toBlob(
//       (blob) =>
//         blob ? resolve(blob) : reject(new Error("webp encode failed")),
//       "image/webp",
//       quality,
//     );
//   });

//   const newName = file.name.replace(/\.[^./\\]+$/, "") + ".webp";
//   return new File([webpBlob], newName, { type: "image/webp" });
// }

// function CroppedBanner({ imageurl, cropdata }) {
//   if (!cropdata || !cropdata.width || !cropdata.height) {
//     return <img src={imageurl} className="w-full h-full object-cover" />;
//   }
//   const { x, y, width, height } = cropdata;
//   return (
//     <div
//       className="w-full h-full"
//       style={{
//         backgroundImage: `url(${imageurl})`,
//         backgroundSize: `${10000 / width}% ${10000 / height}%`,
//         backgroundPosition: `${x / (1 - width / 100) || 0}% ${y / (1 - height / 100) || 0}%`,
//         backgroundRepeat: "no-repeat",
//       }}
//     />
//   );
// }

// function SortableImage({ img, index, isSelected, onToggle, onView, category }) {
//   const {
//     attributes,
//     listeners,
//     setNodeRef,
//     transform,
//     transition,
//     isDragging,
//   } = useSortable({ id: index.toString() });
//   // const routerLocation = useLocation();
//   // const { id: paramId } = useParams();
//   // const propertyid = routerLocation.state?.propertyid ?? paramId;
//   // const isViewMode = routerLocation.state?.mode === "view";
//   const routerLocation = useLocation();
//   const { id: paramId } = useParams();
//   const [searchParams] = useSearchParams();

//   const propertyid =
//     routerLocation.state?.propertyid ?? searchParams.get("id") ?? paramId;

//   const isViewMode =
//     routerLocation.state?.mode === "view" ||
//     searchParams.get("mode") === "view";
//   const style = {
//     transform: CSS.Transform.toString(transform),
//     transition,
//     opacity: isDragging ? 0.5 : 1,
//     zIndex: isDragging ? 10 : 1,
//   };

//   return (
//     <div
//       ref={setNodeRef}
//       style={style}
//       {...attributes}
//       onClick={() => {
//         if (!isViewMode) {
//           onToggle(index);
//         }
//       }}
//       className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-colors group ${
//         isSelected
//           ? "border-green-900 ring-2 ring-green-900/40"
//           : "border-gray-200 hover:border-gray-300"
//       }`}
//     >
//       <img
//         src={img.url}
//         alt={`Photo ${index + 1}`}
//         className="w-full h-full object-cover pointer-events-none"
//       />

//       {/* Position + Category at bottom */}
//       <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 flex justify-between">
//         <span>#{index + 1}</span>
//         {category && <span className="truncate ml-2">{category}</span>}
//       </div>

//       {/* Drag handle */}
//       {!isViewMode && (
//         <div
//           {...listeners}
//           className="absolute top-1.5 left-1/2 -translate-x-1/2 bg-white/80 rounded px-2 py-0.5 text-[10px] font-medium text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
//           onClick={(e) => e.stopPropagation()}
//         >
//           ⠿ Drag
//         </div>
//       )}
//       {/* View button */}

//       <button
//         type="button"
//         onClick={(e) => {
//           e.stopPropagation();
//           onView({ url: img.url, label: `Photo ${index + 1}` });
//         }}
//         className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full bg-white/80 border border-gray-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
//       >
//         <Eye size={12} strokeWidth={2.5} />
//       </button>

//       {/* Selection checkbox */}
//       {!isViewMode && (
//         <div
//           className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center border ${
//             isSelected
//               ? "bg-green-500 border-green-900 text-white"
//               : "bg-white/80 border-gray-300"
//           }`}
//         >
//           {isSelected && <Check size={12} strokeWidth={3} />}
//         </div>
//       )}
//     </div>
//   );
// }

// const EditPropertyDetailElementor = () => {
//   useEffect(() => {
//     window.scrollTo(0, 0);
//   }, []);
//   const navigate = useNavigate();
//   // const routerLocation = useLocation();
//   // const { id: paramId } = useParams();
//   // const propertyid = routerLocation.state?.propertyid ?? paramId;
//   // const isViewMode = routerLocation.state?.mode === "view";
//   const routerLocation = useLocation();
//   const { id: paramId } = useParams();
//   const [searchParams] = useSearchParams();

//   const propertyid =
//     routerLocation.state?.propertyid ?? searchParams.get("id") ?? paramId;

//   const isViewMode =
//     routerLocation.state?.mode === "view" ||
//     searchParams.get("mode") === "view";
//   const [isLoadingProperty, setIsLoadingProperty] = useState(true);
//   const [loadError, setLoadError] = useState("");
//   const [viewImage, setViewImage] = useState(null);
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const savedState = localStorage.getItem("menubarOpen");

//     if (savedState !== null) {
//       return JSON.parse(savedState);
//     }

//     return window.innerWidth >= 1024;
//   });
//   const [indiaStates, setIndiaStates] = useState([]);
//   const [selectedState, setSelectedState] = useState("");
//   const [cities, setCities] = useState([]);
//   const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
//   const [isDeletingImages, setIsDeletingImages] = useState(false);
//   const [deleteProgress, setDeleteProgress] = useState({ done: 0, total: 0 });
//   const [propertycategory, setpropertycategory] = useState("");
//   const [propertytag, setpropertytag] = useState("");

//   useEffect(() => {
//     // Load world.json from public folder
//     fetch("/world.json")
//       .then((res) => res.json())
//       .then((data) => {
//         const india = data.find((country) => country.name === "India");
//         if (india) {
//           setIndiaStates(india.states);
//         }
//       })
//       .catch((err) => console.error("Error loading world.json:", err));
//   }, []);

//   const handleStateChange = (e) => {
//     const stateName = e.target.value;
//     setSelectedState(stateName);

//     if (stateName.toLowerCase() === "goa") {
//       const goaCities = [
//         "Aldona",
//         "Arambol",
//         "Baga",
//         "Bambolim",
//         "Bandora",
//         "Benaulim",
//         "Calangute",
//         "Candolim",
//         "Carapur",
//         "Cavelossim",
//         "Chicalim",
//         "Chinchinim",
//         "Colovale",
//         "Colva",
//         "Cortalim",
//         "Cuncolim",
//         "Curchorem",
//         "Curti",
//         "Davorlim",
//         "Dicholi",
//         "Goa Velha",
//         "Guirim",
//         "Jua",
//         "Kankon",
//         "Madgaon",
//         "Мариса",
//         "Morjim",
//         "Mormugao",
//         "Navelim",
//         "North Goa",
//         "Palle",
//         "Panaji",
//         "Pernem",
//         "Ponda",
//         "Quepem",
//         "Queula",
//         "Raia",
//         "Saligao",
//         "Sancoale",
//         "Sanguem",
//         "Sanquelim",
//         "Sanvordem",
//         "Serula",
//         "Solim",
//         "South Goa",
//         "Taleigao",
//         "Vagator",
//         "Valpoy",
//         "Varca",
//         "Vasco da Gama",
//         // Extra villages
//         "Anjuna",
//         "Arpora",
//         "Assagao",
//         "Parra",
//         "Siolim",
//         "Reis Magos",
//         "Nerul",
//       ];

//       // Convert to {id, name} objects since your city dropdown expects that
//       setCities(goaCities.map((city, idx) => ({ id: idx + 1, name: city })));
//     } else if (stateName.toLowerCase() === "karnataka") {
//       const karnatakaCities = [
//         // Extra villages

//         // Added cities
//         "Afzalpur",
//         "Ajjampur",
//         "Aland",
//         "Alnavar",
//         "lark",
//         "Alur",
//         "Anekal",
//         "Ankola",
//         "Annigeri",
//         "Arkalgud",
//         "Arsikere",
//         "Athni",
//         "Aurad",
//         "Badami",
//         "Bagalkot",
//         "Bagepalli",
//         "Bail-Hongal",
//         "Ballari",
//         "Banavar",
//         "Bangarapet",
//         "Bannur",
//         "Bantval",
//         "Basavakalyan",
//         "Basavana Bagevadi",
//         "Belagavi",
//         "Belluru",
//         "Beltangadi",
//         "Belur",
//         "Bengaluru",
//         "Bengaluru Rural",
//         "Bengaluru Urban",
//         "Bhadravati",
//         "Bhalki",
//         "Bhatkal",
//         "Bidar",
//         "Bilgi",
//         "Birur",
//         "Byadgi",
//         "Byndoor",
//         "Canacona",
//         "Challakere",
//         "Chamrajnagar",
//         "Channagiri",
//         "Channapatna",
//         "Channarayapatna",
//         "Chik Ballapur",
//         "Chikkaballapur",
//         "Chikkamagaluru",
//         "Chiknayakanhalli",
//         "Chikodi",
//         "Chincholi",
//         "Chintamani",
//         "Chitapur",
//         "Chitradurga",
//         "Closepet",
//         "Coondapoor",
//         "Coorg",
//         "Dakshina Kannada",
//         "Dandeli",
//         "Davanagere",
//         "Devanhalli",
//         "Dharwad",
//         "Dod Ballapur",
//         "French Rocks",
//         "Gadag",
//         "Gadag-Betageri",
//         "Gajendragarh",
//         "Gangawati",
//         "Gangolli",
//         "Gokak",
//         "Gokarna",
//         "Goribidnur",
//         "Gorur",
//         "Gubbi",
//         "Gudibanda",
//         "Guledagudda",
//         "Gundlupet",
//         "Gurmatkal",
//         "Hadagalli",
//         "Haliyal",
//         "Hampi",
//         "Hangal",
//         "Harihar",
//         "Harpanahalli",
//         "Hassan",
//         "Haveri",
//         "Heggadadevankote",
//         "Hirekerur",
//         "Hiriyur",
//         "Holalkere",
//         "Hole Narsipur",
//         "Homnabad",
//         "Honavar",
//         "Honnali",
//         "Hosanagara",
//         "Hosangadi",
//         "Hosdurga",
//         "Hoskote",
//         "Hospet",
//         "Hubballi",
//         "Hukeri",
//         "Hungund",
//         "Hunsur",
//         "Ilkal",
//         "Indi",
//         "Jagalur",
//         "Jamkhandi",
//         "Jevargi",
//         "Kabini",
//         "Kadur",
//         "Kalaburgi",
//         "Kalghatgi",
//         "Kampli",
//         "Kankanhalli",
//         "Karkala",
//         "Karwar",
//         "Kavalur",
//         "Kerur",
//         "Khanapur",
//         "Kodagu",
//         "Kodigenahalli",
//         "Kodlipet",
//         "Kolar",
//         "Kollegal",
//         "Konanur",
//         "Konnur",
//         "Koppa",
//         "Koppal",
//         "Koratagere",
//         "Kotturu",
//         "Krishnarajpet",
//         "Kudachi",
//         "Kudligi",
//         "Kumsi",
//         "Kumta",
//         "Kundgol",
//         "Kunigal",
//         "Kurgunta",
//         "Kushalnagar",
//         "Kushtagi",
//         "Lakshmeshwar",
//         "Lingsugur",
//         "Londa",
//         "Machare",
//         "Maddagiri",
//         "Maddur",
//         "Madikeri",
//         "Magadi",
//         "Mahalingpur",
//         "Malavalli",
//         "Malpe",
//         "Malur",
//         "Mandya",
//         "Mangaluru",
//         "Manipal",
//         "Manvi",
//         "Mayakonda",
//         "Melukote",
//         "Mudbidri",
//         "Muddebihal",
//         "Mudgal",
//         "Mudgere",
//         "Mudhol",
//         "Mulbagal",
//         "Mulgund",
//         "Mulki",
//         "Mundargi",
//         "Mundgod",
//         "Munirabad",
//         "Murudeshwara",
//         "Mysuru",
//         "Nagamangala",
//         "Nanjangud",
//         "Narasimharajapura",
//         "Naregal",
//         "Nargund",
//         "Navalgund",
//         "Nelamangala",
//         "Nyamti",
//         "Pangala",
//         "Pavugada",
//         "Piriyapatna",
//         "Ponnampet",
//         "Puttur",
//         "Rabkavi",
//         "Raichur",
//         "Ramanagara",
//         "Ranibennur",
//         "Raybag",
//         "Robertsonpet",
//         "Ron",
//         "Sadalgi",
//         "Sagar",
//         "Sakleshpur",
//         "Sandur",
//         "Sanivarsante",
//         "Sankeshwar",
//         "Sargur",
//         "Saundatti",
//         "Savanur",
//         "Seram",
//         "Shahabad",
//         "Shahpur",
//         "Shiggaon",
//         "Shikarpur",
//         "Shimoga",
//         "Shirhatti",
//         "Shorapur",
//         "Shrirangapattana",
//         "Siddapur",
//         "Sidlaghatta",
//         "Sindgi",
//         "Sindhnur",
//         "Sira",
//         "Sirsi",
//         "Siruguppa",
//         "Someshwar",
//         "Somvarpet",
//         "Sorab",
//         "Sravana Belgola",
//         "Sringeri",
//         "Srinivaspur",
//         "Sulya",
//         "Suntikoppa",
//         "Talikota",
//         "Tarikere",
//         "Tekkalakote",
//         "Terdal",
//         "Tiptur",
//         "Tirthahalli",
//         "Tirumakudal Narsipur",
//         "Tumakuru",
//         "Turuvekere",
//         "Udupi",
//         "Ullal",
//         "Uttar Kannada",
//         "Vadigenhalli",
//         "Vijayapura",
//         "Virarajendrapet",
//         "Wadi",
//         "Yadgir",
//         "Yelahanka",
//         "Yelandur",
//         "Yelbarga",
//         "Yellapur",
//       ];

//       // Convert to {id, name} objects since your city dropdown expects that
//       setCities(
//         karnatakaCities.map((city, idx) => ({ id: idx + 1, name: city })),
//       );
//     } else if (stateName.toLowerCase() === "kerala") {
//       const keralaCities = [
//         "Adoor",
//         "Alappuzha",
//         "Alleppey",
//         "Aluva",
//         "Alwaye",
//         "Angamali",
//         "Aroor",
//         "Arukutti",
//         "Attingal",
//         "Avanoor",
//         "Azhikkal",
//         "Beypore",
//         "Changanacheri",
//         "Chelakara",
//         "Chengannur",
//         "Cherpulassery",
//         "Cherthala",
//         "Chetwayi",
//         "Chittur",
//         "Cochin",
//         "Dharmadom",
//         "Edakkulam",
//         "Elur",
//         "Erattupetta",
//         "Ernakulam",
//         "Ferokh",
//         "Guruvayur",
//         "Idukki",
//         "Iringal",
//         "Irinjalakuda",
//         "Kadakkavoor",
//         "Kalamassery",
//         "Kalavoor",
//         "Kalpetta",
//         "Kanhangad",
//         "Kannavam",
//         "Kannur",
//         "Kasaragod",
//         "Kattanam",
//         "Kayankulam",
//         "Kizhake Chalakudi",
//         "Kochi",
//         "Kodungallur",
//         "Kollam",
//         "Kotamangalam",
//         "Kottayam",
//         "Kovalam",
//         "Kozhikode",
//         "Kumbalam",
//         "Kunnamangalam",
//         "Kunnamkulam",
//         "Kunnumma",
//         "Kutiatodu",
//         "Kuttampuzha",
//         "Lalam",
//         "Mahe",
//         "Malappuram",
//         "Manjeri",
//         "Manjeshwaram",
//         "Mannarakkat",
//         "Marayur",
//         "Mattanur",
//         "Mavelikara",
//         "Mavoor",
//         "Muluppilagadu",
//         "Munnar",
//         "Muvattupula",
//         "Muvattupuzha",
//         "Nadapuram",
//         "Naduvannur",
//         "Nedumangad",
//         "Neyyattinkara",
//         "Nileshwar",
//         "Ottappalam",
//         "Palackattumala",
//         "Palakkad district",
//         "Palghat",
//         "Panamaram",
//         "Pappinissheri",
//         "Paravur Tekkumbhagam",
//         "Pariyapuram",
//         "Pathanamthitta",
//         "Pattanamtitta",
//         "Payyanur",
//         "Perinthalmanna",
//         "Perumbavoor",
//         "Perumpavur",
//         "Perya",
//         "Piravam",
//         "Ponnani",
//         "Ponmana",
//         "Punalur",
//         "Punnayur",
//         "Ramamangalam",
//         "Shertallai",
//         "Shoranur",
//         "Taliparamba",
//         "Thalassery",
//         "Thalikulam",
//         "Thanniyam",
//         "Thekkady",
//         "Thiruvananthapuram",
//         "Thrissur",
//         "Tirur",
//         "Tiruvalla",
//         "Vaikam",
//         "Varkala",
//         "Vatakara",
//         "Vayalar",
//         "Vettur",
//         "Wayanad",
//       ];

//       // Convert to {id, name} objects since your city dropdown expects that
//       setCities(keralaCities.map((city, idx) => ({ id: idx + 1, name: city })));
//     } else if (stateName.toLowerCase() === "tamil nadu") {
//       const tamilnaduCities = [
//         // Tamil Nadu cities
//         "Aambur",
//         "Ariyalur",
//         "Arakkonam",
//         "Arani",
//         "Attur",
//         "Avadi",
//         "Batlagundu",
//         "Chengalpattu",
//         "Chennai",
//         "Chidambaram",
//         "Coimbatore",
//         "Courtallam",
//         "Cuddalore",
//         "Coonoor",
//         "Dharapuram",
//         "Dharmapuri",
//         "Dindigul",
//         "Erode",
//         "Gobichettipalayam",
//         "Gudalur",
//         "Gudiyatham",
//         "Hosur",
//         "Jayankondam",
//         "Kallakurichi",
//         "Kanchipuram",
//         "Kanyakumari",
//         "Karaikudi",
//         "Karur",
//         "Kodaikanal",
//         "Kovilpatti",
//         "Krishnagiri",
//         "Kumbakonam",
//         "Madurai",
//         "Manapparai",
//         "Mahabalipuram",
//         "Mayiladuthurai",
//         "Mettupalayam",
//         "Namakkal",
//         "Nagapattinam",
//         "Nagercoil",
//         "Ooty",
//         "Palani",
//         "Pallavaram",
//         "Pattukkottai",
//         "Perambalur",
//         "Pollachi",
//         "Poonamallee",
//         "Pudukkottai",
//         "Rajapalayam",
//         "Ramanathapuram",
//         "Ranipet",
//         "Salem",
//         "Sankagiri",
//         "Sivaganga",
//         "Sivakasi",
//         "Tambaram",
//         "Tenkasi",
//         "Thanjavur",
//         "Theni",
//         "Thiruvallur",
//         "Thiruvarur",
//         "Thoothukudi",
//         "Tindivanam",
//         "Tiruchendur",
//         "Tiruchengode",
//         "Tiruchirappalli",
//         "Tirunelveli",
//         "Tirupathur",
//         "Tiruppur",
//         "Tiruvannamalai",
//         "Udagamandalam",
//         "Udumalpet",
//         "Usilampatti",
//         "Vaniyambadi",
//         "Vellore",
//         "Villupuram",
//         "Virudhunagar",
//       ];

//       // Convert to {id, name} objects since your city dropdown expects that
//       setCities(
//         tamilnaduCities.map((city, idx) => ({ id: idx + 1, name: city })),
//       );
//     } else {
//       const stateObj = indiaStates.find((s) => s.name === stateName);
//       setCities(stateObj ? stateObj.cities : []);
//     }
//   };

//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);

//       if (mobile) {
//         setIsSidebarOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const MAX_CARDS = 100;

//   const [selectedImages, setSelectedImages] = useState(
//     Array(MAX_CARDS).fill(null),
//   );

//   // Category assigned to each banner slide/image (parallel array to selectedImages)
//   const [selectedCategories, setSelectedCategories] = useState(
//     Array(MAX_CARDS).fill(""),
//   );
//   // crop logic
//   const [selectedCrops, setSelectedCrops] = useState(
//     Array(MAX_CARDS).fill(null),
//   );
//   const [cropModalIndex, setCropModalIndex] = useState(null);
//   const [cropPosition, setCropPosition] = useState({ x: 0, y: 0 });
//   const [cropZoom, setCropZoom] = useState(1);
//   const [pendingCroppedArea, setPendingCroppedArea] = useState(null);
//   const bannerWrapperRef = useRef(null);
//   const [bannerAspect, setBannerAspect] = useState(16 / 9);
//   const [cropBoxSize, setCropBoxSize] = useState({ width: 600, height: 338 });

//   const openCropModal = (index) => {
//     const existing = selectedCrops[index];
//     setCropPosition({ x: 0, y: 0 });
//     setCropZoom(existing?.zoom || 1);
//     setPendingCroppedArea(existing || null);

//     const wrapperWidth =
//       bannerWrapperRef.current?.getBoundingClientRect()?.width;
//     const bannerHeightPx = window.innerHeight * 0.58; // matches h-[58vh]
//     const aspect =
//       wrapperWidth && bannerHeightPx ? wrapperWidth / bannerHeightPx : 16 / 9;
//     setBannerAspect(aspect);

//     // Fit that exact aspect ratio into the available modal space, in real px
//     // (avoids the aspect-ratio + max-height CSS conflict that shrinks the box).
//     const maxAvailableWidth = window.innerWidth * 0.85;
//     const maxAvailableHeight = window.innerHeight * 0.6;

//     let boxWidth = maxAvailableHeight * aspect;
//     let boxHeight = maxAvailableHeight;
//     if (boxWidth > maxAvailableWidth) {
//       boxWidth = maxAvailableWidth;
//       boxHeight = maxAvailableWidth / aspect;
//     }
//     setCropBoxSize({ width: boxWidth, height: boxHeight });

//     setCropModalIndex(index);
//   };

//   const handleCropComplete = (croppedAreaPercentages, _croppedAreaPixels) => {
//     setPendingCroppedArea({
//       x: croppedAreaPercentages.x,
//       y: croppedAreaPercentages.y,
//       width: croppedAreaPercentages.width,
//       height: croppedAreaPercentages.height,
//     });
//   };

//   const saveCrop = () => {
//     if (cropModalIndex === null || !pendingCroppedArea) return;
//     setSelectedCrops((prev) => {
//       const u = [...prev];
//       const { x, y, width, height } = pendingCroppedArea;
//       u[cropModalIndex] = { x, y, width, height };
//       return u;
//     });
//     setCropModalIndex(null);
//   };
//   //crop logic over
//   const [activeBannerIndex, setActiveBannerIndex] = useState(0);
//   const [bannerCategoryError, setBannerCategoryError] = useState("");
//   const [showGalleryModal, setShowGalleryModal] = useState(false);
//   const bannerCarouselRef = useRef(null);

//   // "All Photos" multi-select + bulk category assignment, used at the top of
//   // the View All gallery modal (separate from the per-slide category picker
//   // on the main banner carousel).
//   const [selectedGalleryIndices, setSelectedGalleryIndices] = useState([]);
//   const [bulkCategoryValue, setBulkCategoryValue] = useState("");

//   // Reset the gallery selection whenever the modal is closed, so it starts
//   // fresh next time it's opened.
//   useEffect(() => {
//     if (!showGalleryModal) {
//       setSelectedGalleryIndices([]);
//       setBulkCategoryValue("");
//     }
//   }, [showGalleryModal]);

//   const toggleGallerySelection = (index) => {
//     setSelectedGalleryIndices((prev) =>
//       prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
//     );
//   };

//   const allGalleryIndices = selectedImages
//     .map((img, i) => (img ? i : null))
//     .filter((i) => i !== null);

//   const isAllGallerySelected =
//     allGalleryIndices.length > 0 &&
//     selectedGalleryIndices.length === allGalleryIndices.length;

//   const toggleSelectAllGallery = () => {
//     setSelectedGalleryIndices(isAllGallerySelected ? [] : allGalleryIndices);
//   };

//   // Applies `bulkCategoryValue` to every currently-selected image at once.
//   const applyBulkCategory = () => {
//     if (!bulkCategoryValue || selectedGalleryIndices.length === 0) return;
//     setSelectedCategories((prev) => {
//       const updated = [...prev];
//       selectedGalleryIndices.forEach((idx) => {
//         updated[idx] = bulkCategoryValue;
//       });
//       return updated;
//     });
//     setSelectedGalleryIndices([]);
//     setBulkCategoryValue("");
//     setBannerCategoryError("");
//   };

//   // Checks whether the slide at `index` is blocking navigation.
//   // Only a NEWLY uploaded file (has `.file`) without a category blocks —
//   // images that were already loaded from the server (no `.file`, e.g.
//   // existing carousel images or converted legacy images) never block, since
//   // legacy images predate the category concept and shouldn't be forced to
//   // pick one just to view/save the rest of the form.
//   const isBannerSlideBlocked = (index) => {
//     return !!selectedImages[index]?.file && !selectedCategories[index];
//   };

//   const CustomLeftArrowBannerCarousel = ({ onClick }) => {
//     return (
//       <button
//         onClick={onClick}
//         className="hidden md:block absolute left-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
//         name="previous_btn"
//       >
//         <FaChevronLeft />
//       </button>
//     );
//   };

//   const CustomRightArrowBannerCarousel = ({ onClick }) => {
//     const handleNextClick = () => {
//       // Block moving to the next slide until the current slide's image has a category
//       // if (isBannerSlideBlocked(activeBannerIndex)) {
//       //   setBannerCategoryError(
//       //     "Please select a category for this image before moving to the next slide.",
//       //   );
//       //   return;
//       // }
//       setBannerCategoryError("");
//       onClick();
//     };

//     return (
//       <button
//         onClick={handleNextClick}
//         className="hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
//         name="next_btn"
//       >
//         <FaChevronRight />
//       </button>
//     );
//   };

//   // Plain arrows (no category validation) — used by carousels other than the
//   // main banner (e.g. the per-bedroom image carousels below), so they don't
//   // accidentally inherit the banner's category-gate logic.
//   const CustomLeftArrowPlain = ({ onClick }) => {
//     return (
//       <button
//         onClick={onClick}
//         className="hidden md:block absolute left-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
//         name="previous_btn"
//       >
//         <FaChevronLeft />
//       </button>
//     );
//   };

//   const CustomRightArrowPlain = ({ onClick }) => {
//     return (
//       <button
//         onClick={onClick}
//         className="hidden md:block absolute right-3 top-1/2 transform -translate-y-1/2 bg-white border shadow-md bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
//         name="next_btn"
//       >
//         <FaChevronRight />
//       </button>
//     );
//   };

//   // Guards against the corrective goToSlide (below) re-triggering this same
//   // handler and causing an oscillation/"stuck" loop.
//   const isCorrectingBannerSlideRef = useRef(false);

//   // Safety net for navigation that bypasses the arrow buttons (swipe, keyboard, dots):
//   // if the previous slide had an image without a category, snap back to it —
//   // but ONLY when moving forward. Moving backward must always be allowed,
//   // otherwise a later incomplete slide permanently blocks the user from ever
//   // navigating back to fix it.
//   const handleBannerAfterChange = (previousSlide, { currentSlide }) => {
//     if (isCorrectingBannerSlideRef.current) {
//       isCorrectingBannerSlideRef.current = false;
//       setActiveBannerIndex(currentSlide);
//       return;
//     }

//     const movingForward = currentSlide > previousSlide;

//     // if (movingForward && isBannerSlideBlocked(previousSlide)) {
//     //   setBannerCategoryError(
//     //     "Please select a category for this image before moving to the next slide.",
//     //   );
//     //   isCorrectingBannerSlideRef.current = true;
//     //   if (bannerCarouselRef.current) {
//     //     bannerCarouselRef.current.goToSlide(previousSlide);
//     //   }
//     //   setActiveBannerIndex(previousSlide);
//     //   return;
//     // }
//     setBannerCategoryError("");
//     setActiveBannerIndex(currentSlide);
//   };

//   const handleImageUpload = async (event, index) => {
//     if (isViewMode) return;
//     const file = event.target.files[0];
//     event.target.value = ""; // allow re-selecting the same file later
//     if (!file) return;

//     let webpFile;
//     try {
//       webpFile = await toWebpFile(file);
//     } catch (err) {
//       setBannerCategoryError(
//         "That image could not be converted. Try a different file.",
//       );
//       return;
//     }

//     const imageurl = URL.createObjectURL(webpFile);
//     const updatedImages = [...selectedImages];
//     updatedImages[index] = { url: imageurl, file: webpFile };
//     setSelectedImages(updatedImages);

//     setSelectedCrops((prev) => {
//       const u = [...prev];
//       u[index] = null;
//       return u;
//     });
//   };
//   const handleBulkImageUpload = async (event) => {
//     if (isViewMode) return;
//     const files = Array.from(event.target.files || []);
//     event.target.value = ""; // allow re-selecting the same files again later
//     if (files.length === 0) return;

//     const emptySlotCount = selectedImages.filter((img) => !img).length;

//     let webpFiles;
//     try {
//       webpFiles = await Promise.all(files.map((f) => toWebpFile(f)));
//     } catch (err) {
//       setBannerCategoryError(
//         "One or more images could not be converted. Try again.",
//       );
//       return;
//     }

//     setSelectedImages((prev) => {
//       const updated = [...prev];
//       let fileIdx = 0;

//       for (let i = 0; i < updated.length && fileIdx < webpFiles.length; i++) {
//         if (!updated[i]) {
//           const file = webpFiles[fileIdx];
//           updated[i] = { url: URL.createObjectURL(file), file };
//           fileIdx++;
//         }
//       }
//       return updated;
//     });

//     if (files.length > emptySlotCount) {
//       setBannerCategoryError(
//         `Only ${emptySlotCount} empty slot(s) available — ${
//           files.length - emptySlotCount
//         } photo(s) couldn't be added. Delete some images or reduce your selection.`,
//       );
//     } else {
//       setBannerCategoryError("");
//     }
//   };
//   const handleBannerImageDelete = async (index) => {
//     if (isViewMode) return;
//     const img = selectedImages[index];

//     // Clear locally right away for a snappy UI.
//     setSelectedImages((prev) => {
//       const u = [...prev];
//       u[index] = null;
//       return u;
//     });
//     setSelectedCategories((prev) => {
//       const u = [...prev];
//       u[index] = "";
//       return u;
//     });
//     setSelectedCrops((prev) => {
//       const u = [...prev];
//       u[index] = null;
//       return u;
//     });
//     setBannerCategoryError("");

//     // If this was already a persisted image (loaded from the server, no
//     // pending `.file`), delete it from the DB + Cloudflare right now instead
//     // of waiting for the next full-form Save. Freshly-picked-but-not-yet-saved
//     // uploads (has `.file`) were never written anywhere, so nothing to call.
//     if (img?.url && !img?.file && propertyid) {
//       try {
//         const res = await fetch(`${API_URL}/property/${propertyid}/image`, {
//           method: "DELETE",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({ imageurl: img.url }),
//         });
//         const data = await res.json();
//         if (!res.ok || !data.success) {
//           throw new Error(data.error || "Failed to delete image");
//         }
//       } catch (err) {
//         console.error("Error deleting image:", err);
//         setSubmitError(
//           "That image was removed here but couldn't be deleted on the server. It may reappear if you don't save.",
//         );
//       }
//     }
//   };
//   const handleDeleteSelectedImages = async () => {
//     if (isViewMode) return;
//     const indices = [...selectedGalleryIndices];
//     if (indices.length === 0) return;

//     setShowDeleteConfirm(false);
//     setIsDeletingImages(true);
//     setDeleteProgress({ done: 0, total: indices.length });

//     const deleteSet = new Set(indices);
//     const failedDeletes = [];

//     for (let i = 0; i < indices.length; i++) {
//       const idx = indices[i];
//       const img = selectedImages[idx];

//       if (img?.url && img?.file) {
//         // Fresh, not-yet-saved upload — nothing on the server to clean up,
//         // just release the local blob URL.
//         URL.revokeObjectURL(img.url);
//       } else if (img?.url && !img?.file && propertyid) {
//         // Already-persisted image (came from the server on load) — delete it
//         // from the DB + Cloudflare right now, same call as the single-image
//         // delete in handleBannerImageDelete.
//         try {
//           const res = await fetch(`${API_URL}/property/${propertyid}/image`, {
//             method: "DELETE",
//             headers: { "Content-Type": "application/json" },
//             body: JSON.stringify({ imageurl: img.url }),
//           });
//           const data = await res.json();
//           if (!res.ok || !data.success) {
//             throw new Error(data.error || "Failed to delete image");
//           }
//         } catch (err) {
//           console.error("Error deleting image:", err);
//           failedDeletes.push(idx);
//           // Server delete failed — keep this slide, don't compact it away.
//           deleteSet.delete(idx);
//         }
//       }

//       setDeleteProgress({ done: i + 1, total: indices.length });
//       await new Promise((resolve) => requestAnimationFrame(resolve));
//     }

//     // Compact: drop the slots that were actually deleted and shift everything
//     // left so there are no gaps — same approach as handleDragEnd.
//     const entries = selectedImages
//       .map((img, i) => ({
//         img,
//         cat: selectedCategories[i],
//         crop: selectedCrops[i],
//         i,
//       }))
//       .filter((e) => e.img !== null && !deleteSet.has(e.i));

//     const newImages = Array(MAX_CARDS).fill(null);
//     const newCategories = Array(MAX_CARDS).fill("");
//     const newCrops = Array(MAX_CARDS).fill(null);

//     entries.forEach((e, idx) => {
//       newImages[idx] = e.img;
//       newCategories[idx] = e.cat;
//       newCrops[idx] = e.crop;
//     });

//     setSelectedImages(newImages);
//     setSelectedCategories(newCategories);
//     setSelectedCrops(newCrops);

//     setSelectedGalleryIndices([]);
//     setBannerCategoryError("");
//     setIsDeletingImages(false);

//     if (failedDeletes.length > 0) {
//       setSubmitError(
//         `${failedDeletes.length} image(s) couldn't be deleted from the server and were kept — try again.`,
//       );
//     }
//   };
//   // Deletes a Tailored Experience grid image (imagegrid1-4 on propertyImages).
//   // Same idea as handleBannerImageDelete: clear locally right away, and if
//   // this was already a persisted image (a real saved URL, not a fresh
//   // not-yet-saved file), delete it from Cloudflare + null the column now
//   // instead of waiting for the next form Save — Save alone never sends this
//   // field once it's cleared, so without this call the old value would just
//   // sit in the DB untouched.
//   const handleTailoredexpimageDelete = async (index) => {
//     if (isViewMode) return;

//     const exp = tailoredExpData[index];
//     const wasPersisted = !!exp?.img && !exp?.file;

//     setTailoredExpData((prev) => {
//       const u = [...prev];
//       u[index] = { ...u[index], img: null, file: null };
//       return u;
//     });

//     if (wasPersisted && propertyid) {
//       try {
//         const res = await fetch(`${API_URL}/property/${propertyid}/image`, {
//           method: "DELETE",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({
//             fieldName: `imagegrid${index + 1}`,
//             table: "propertyImages",
//           }),
//         });
//         const data = await res.json();
//         if (!res.ok || !data.success) {
//           throw new Error(data.error || "Failed to delete image");
//         }
//       } catch (err) {
//         console.error("Error deleting tailored experience image:", err);
//         setSubmitError(
//           "That image was removed here but couldn't be deleted on the server. It may reappear if you don't save.",
//         );
//       }
//     }
//   };

//   const handleBannerCategoryChange = (index, categoryValue) => {
//     setSelectedCategories((prev) => {
//       const u = [...prev];
//       u[index] = categoryValue;
//       return u;
//     });
//     if (categoryValue) {
//       setBannerCategoryError("");
//     }
//   };

//   // Builds the ordered category list, with Bedroom/Bathroom expanded dynamically
//   // based on the "No. of Rooms" (bookingRooms/maxbed) and "Max Bathroom" (maxbath) inputs.
//   const getBannerCategoryOptions = () => {
//     const bedroomCount = parseInt(bookingRooms) || 0;
//     const bathroomCount = parseInt(maxbath) || 0;

//     const bedroomOptions =
//       bedroomCount > 0
//         ? Array.from({ length: bedroomCount }, (_, i) => `Bedroom ${i + 1}`)
//         : ["Bedroom"];

//     const bathroomOptions =
//       bathroomCount > 0
//         ? Array.from({ length: bathroomCount }, (_, i) => `Bathroom ${i + 1}`)
//         : ["Bathroom"];

//     return [
//       "Living Room",
//       "Dining Area",
//       ...bedroomOptions,
//       ...bathroomOptions,
//       "Pool",
//       "Courtyard",
//       "Games & Activity Area",
//       "Exterior",
//       "Jacuzzi",
//       "Theatre Room",
//       "Gym",
//       "Garden",
//       "Kitchen",
//       "Additional Photos",
//       "Cover Photo",
//     ];
//   };

//   // Groups every selected banner image by its assigned category, for the "View All" gallery.
//   // Categories with no images are omitted.
//   const getGroupedBannerGallery = () => {
//     const grouped = {};
//     getBannerCategoryOptions().forEach((cat) => {
//       grouped[cat] = [];
//     });
//     selectedImages.forEach((img, index) => {
//       const cat = selectedCategories[index];
//       if (img && cat) {
//         if (!grouped[cat]) grouped[cat] = [];
//         grouped[cat].push({ ...img, index });
//       }
//     });
//     return Object.entries(grouped).filter(([, imgs]) => imgs.length > 0);
//   };

//   // Pulls the images assigned to a given bedroom directly from the banner's
//   // selectedImages/selectedCategories (category === "Bedroom {roomIndex + 1}").
//   // This keeps the Bedroom Carousel section below in sync with the banner —
//   // there is no separate upload step here anymore.
//   const getBedroomImages = (roomIndex) => {
//     const categoryLabel = `Bedroom ${roomIndex + 1}`;
//     const images = [];
//     selectedImages.forEach((img, idx) => {
//       if (img && selectedCategories[idx] === categoryLabel) {
//         images.push({ ...img, bannerIndex: idx });
//       }
//     });
//     return images;
//   };

//   const TailoredExpCarousel = [
//     {
//       img: "https://mpvstorage.blob.core.windows.net/villapics-new/1592/77613/villa-barbados-caribbean-luxury-the-great-house-boat_FullL.jpg?v=302558110212",
//       title: "For Adventure Seekers",
//       content:
//         "Experience the thrill of Goa with our adventure package! Enjoy cozy stays, a hearty breakfast, and activities like water sports, trekking, and more.",
//     },
//     {
//       img: "https://mpvstorage.blob.core.windows.net/villapics-new/1592/66699/villa-barbados-caribbean-luxury-the-great-house-ext-9_FullL.jpg?v=302558110212",
//       title: "For Leisure Lovers",
//       content:
//         "Unwind with our leisure package in Goa. Relax in serene stays, savor a delicious breakfast, and enjoy peaceful beaches, spa sessions, and local attractions.",
//     },
//   ];

//   const responsive_TailoredExpCarousel = {
//     xl: {
//       breakpoint: { max: 4000, min: 1280 },
//       items: 2,
//       partialVisibilityGutter: 20,
//     },
//     lg: {
//       breakpoint: { max: 1280, min: 1024 },
//       items: 2,
//       partialVisibilityGutter: 20,
//     },
//     md: {
//       breakpoint: { max: 1024, min: 768 },
//       items: 2,
//       partialVisibilityGutter: 15,
//     },
//     sm: {
//       breakpoint: { max: 768, min: 640 },
//       items: 1,
//       partialVisibilityGutter: 15,
//     },
//     xs: {
//       breakpoint: { max: 640, min: 0 },
//       items: 1,
//       partialVisibilityGutter: 10,
//     },
//   };

//   const responsive_OuterCarousel = {
//     xl: {
//       breakpoint: { max: 4000, min: 1280 },
//       items: 3,
//       partialVisibilityGutter: 20,
//     },
//     lg: {
//       breakpoint: { max: 1280, min: 1024 },
//       items: 3,
//       partialVisibilityGutter: 20,
//     },
//     md: {
//       breakpoint: { max: 1024, min: 768 },
//       items: 2,
//       partialVisibilityGutter: 15,
//     },
//     sm: {
//       breakpoint: { max: 768, min: 640 },
//       items: 2,
//       partialVisibilityGutter: 15,
//     },
//     xs: {
//       breakpoint: { max: 640, min: 0 },
//       items: 1,
//       partialVisibilityGutter: 10,
//     },
//   };

//   const responsive_BannerCarousel = {
//     xl: {
//       breakpoint: { max: 4000, min: 1280 },
//       items: 1,
//       partialVisibilityGutter: 20,
//     },
//     lg: {
//       breakpoint: { max: 1280, min: 1024 },
//       items: 1,
//       partialVisibilityGutter: 20,
//     },
//     md: {
//       breakpoint: { max: 1024, min: 768 },
//       items: 1,
//       partialVisibilityGutter: 15,
//     },
//     sm: {
//       breakpoint: { max: 768, min: 640 },
//       items: 1,
//       partialVisibilityGutter: 15,
//     },
//     xs: {
//       breakpoint: { max: 640, min: 0 },
//       items: 1,
//       partialVisibilityGutter: 10,
//     },
//   };

//   const [showDots, setShowDots] = useState(false);

//   useEffect(() => {
//     const updateDotsVisibility = () => {
//       setShowDots(window.innerWidth < 768);
//     };

//     updateDotsVisibility();
//     window.addEventListener("resize", updateDotsVisibility);
//     return () => window.removeEventListener("resize", updateDotsVisibility);
//   }, []);

//   const responsive_ImageCarouselOne = {
//     xl: {
//       breakpoint: { max: 4000, min: 1280 },
//       items: 1,
//       partialVisibilityGutter: 20,
//     },
//     lg: {
//       breakpoint: { max: 1280, min: 1024 },
//       items: 1,
//       partialVisibilityGutter: 20,
//     },
//     md: {
//       breakpoint: { max: 1024, min: 768 },
//       items: 1,
//       partialVisibilityGutter: 15,
//     },
//     sm: {
//       breakpoint: { max: 768, min: 640 },
//       items: 1,
//       partialVisibilityGutter: 15,
//     },
//     xs: {
//       breakpoint: { max: 640, min: 0 },
//       items: 1,
//       partialVisibilityGutter: 10,
//     },
//   };

//   const CustomLeftArrowTailoredExpCarousel = ({ onClick }) => {
//     return (
//       <button
//         onClick={onClick}
//         className="hidden md:block absolute left-3 top-44 transform -translate-y-1/2 shadow-custom bg-white bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
//         name="previous_btn"
//       >
//         <FaChevronLeft />
//       </button>
//     );
//   };

//   const CustomRightArrowTailoredExpCarousel = ({ onClick }) => {
//     return (
//       <button
//         onClick={onClick}
//         className="hidden md:block absolute right-3 top-44 transform -translate-y-1/2 shadow-custom bg-white bg-opacity-90 hover:bg-opacity-100 text-sm hover:text-green-600 rounded-full p-3 z-0 scale-75"
//         name="next_btn"
//       >
//         <FaChevronRight />
//       </button>
//     );
//   };

//   const [activeCardIndex, setActiveCardIndex] = useState(0);
//   const carouselRef = useRef(null);

//   const handlePrevSlide = () => {
//     if (carouselRef.current) {
//       carouselRef.current.previous();
//     }
//   };

//   const handleNextSlide = () => {
//     if (carouselRef.current) {
//       carouselRef.current.next();
//     }
//   };

//   const [, setIsOpen_dropdownExp] = useState(false);
//   const dropdownRef_dropdownExp = useRef(null);

//   useEffect(() => {
//     const handleClickOutside_dropdownExp = (event) => {
//       if (
//         dropdownRef_dropdownExp.current &&
//         !dropdownRef_dropdownExp.current.contains(event.target)
//       ) {
//         setIsOpen_dropdownExp(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside_dropdownExp);
//     return () =>
//       document.removeEventListener("mousedown", handleClickOutside_dropdownExp);
//   }, []);
//   const [tailoredExpData, setTailoredExpData] = useState([
//     { img: null, title: "", content: "" },
//     { img: null, title: "", content: "" },
//     { img: null, title: "", content: "" },
//     { img: null, title: "", content: "" },
//   ]);
//   const MAX_CARDS_Exp = tailoredExpData.length;

//   // Booking Widget States
//   const [bookingPrice, setBookingPrice] = useState("");
//   const [bookingPriceDetails, setBookingPriceDetails] = useState(
//     "(for 10 rooms) Per Night + Taxes",
//   );
//   const [bookingcheckinDate, setBookingcheckinDate] = useState("");
//   const [bookingcheckintime, setBookingcheckintime] = useState("");
//   const [bookingcheckoutDate, setBookingcheckoutDate] = useState("");
//   const [bookingcheckouttime, setBookingcheckouttime] = useState("");
//   const [bookingAdults, setBookingAdults] = useState("2");
//   const [bookingChildren, setBookingChildren] = useState("0");
//   const [bookingRooms, setBookingRooms] = useState("3");
//   const [bookingTotal, setBookingTotal] = useState("");

//   const [propertyname, setpropertyname] = useState("");
//   const [description, setDescription] = useState("");
//   const [heading, setHeading] = useState("");
//   const [content, setContent] = useState("");
//   const [catchyQuote, setCatchyQuote] = useState("");
//   const [maptitle, setmaptitle] = useState("");
//   const [mapcontent, setmapcontent] = useState("");
//   const [tailoredExperience, setTailoredExperience] = useState("");
//   const [tailoredExperienceContent, setTailoredExperienceContent] =
//     useState("");

//   const [maplang, setmaplang] = useState("");
//   const [maplong, setmaplong] = useState("");

//   // Additional required data (not shown on website)
//   const [propertycode, setpropertycode] = useState("");
//   const [propertyowner, setpropertyowner] = useState("");
//   const [airid, setairid] = useState("");
//   const [instagramreellink, setinstagramreellink] = useState("");
//   const [propertybrochure, setPropertyBrochure] = useState("");
//   const [defaultprice, setdefaultprice] = useState("");
//   const [deposit, setDeposit] = useState("");
//   const [maxbath, setmaxbath] = useState("");
//   const [maplink, setMaplink] = useState("");
//   const [youtubelink, setYoutubelink] = useState("");
//   const [conserlink, setconserlink] = useState("");
//   const [seasontype, setseasontype] = useState([]);
//   const [isOpen_seasontype, setIsOpen_seasontype] = useState(false);
//   const containerRef_seasontype = useRef(null);
//   const [propertytype, setpropertytype] = useState("Villa");
//   const [location, setLocation] = useState("");
//   const [rating, setRating] = useState("");

//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [submitError, setSubmitError] = useState("");
//   // Upload progress for the saving overlay. "uploading" = bytes still
//   // leaving the browser (tracked via axios onUploadProgress); "finalizing"
//   // = browser has sent everything, server is now uploading to Cloudflare
//   // and writing to the DB — no byte-level signal exists for that part.
//   const [uploadProgress, setUploadProgress] = useState(0);
//   const [uploadPhase, setUploadPhase] = useState("idle"); // idle | uploading | finalizing

//   const [amenitiesList, setAmenitiesList] = useState([]);
//   const [selectedAmenities, setSelectedAmenities] = useState([]); // stores ids: [1, 4, 5]

//   const [searchTerm, setSearchTerm] = useState("");
//   const [isOpen_amenities, setIsOpen_amenities] = useState(false);

//   useEffect(() => {
//     fetch(`${API_URL}/getAmenities`)
//       .then((res) => res.json())
//       .then((data) => {
//         if (data.success) setAmenitiesList(data.data);
//       })
//       .catch((err) => console.error("Failed to load amenities", err));
//   }, []);

//   // Toggle stores/removes the numeric id
//   const toggleAmenity = (id) => {
//     if (isViewMode) return;
//     setSelectedAmenities((prev) =>
//       prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
//     );
//   };
//   const toggleseasontype = (season) => {
//     setseasontype((prev) =>
//       prev.includes(season)
//         ? prev.filter((s) => s !== season)
//         : [...prev, season],
//     );
//   };

//   useEffect(() => {
//     function handleClickOutside(event) {
//       if (
//         containerRef_seasontype.current &&
//         !containerRef_seasontype.current.contains(event.target)
//       ) {
//         setIsOpen_seasontype(false);
//       }
//     }
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   const filteredAmenities = amenitiesList.filter((amenity) =>
//     amenity.name.toLowerCase().includes(searchTerm.toLowerCase()),
//   );

//   const containerRef_amenities = useRef(null);

//   useEffect(() => {
//     function handleClickOutside(event) {
//       if (
//         containerRef_amenities.current &&
//         !containerRef_amenities.current.contains(event.target)
//       ) {
//         setIsOpen_amenities(false);
//       }
//     }

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   // ─── Fetch existing property + autofill the form ───
//   useEffect(() => {
//     if (!propertyid) {
//       setIsLoadingProperty(false);
//       setLoadError(
//         "No property selected to edit — missing propertyid (check the navigate() call that opened this page).",
//       );
//       return;
//     }

//     const loadProperty = async () => {
//       setIsLoadingProperty(true);
//       setLoadError("");
//       try {
//         const res = await fetch(`${API_URL}/admin/properties/${propertyid}`);
//         if (!res.ok) throw new Error("Failed to load property");
//         const p = await res.json();

//         // ── Simple fields ──
//         setpropertyname(p.propertyname || "");
//         setDescription(p.description || "");
//         setHeading(p.heading || "");
//         setContent(p.content || "");
//         setCatchyQuote(p.catchquote || "");
//         setmaptitle(p.maptitle || "");
//         setmapcontent(p.mapcontent || "");
//         setmaplang(p.maplang ?? "");
//         setmaplong(p.maplong ?? "");
//         setpropertycode(p.propertycode || "");
//         setpropertyowner(p.propertyowner || "");
//         setairid(p.airid || "");
//         setinstagramreellink(p.instagramreellink || "");
//         setPropertyBrochure(p.propertybrochure || "");
//         setBookingPrice(p.currentprice ?? "");
//         setdefaultprice(p.defaultprice ?? "");
//         setDeposit(p.deposit ?? "");
//         setBookingAdults(String(p.maxadult ?? "2"));
//         setBookingChildren(String(p.maxchild ?? "0"));
//         setBookingRooms(String(p.maxbed ?? "3"));
//         setmaxbath(String(p.maxbath ?? ""));
//         setMaplink(p.maplink || "");
//         setYoutubelink(p.youtubelink || "");
//         setseasontype(
//           p.seasontype
//             ? String(p.seasontype)
//                 .split(",")
//                 .map((s) => s.trim())
//                 .filter(Boolean)
//             : [],
//         );
//         setpropertytype(p.propertytype || "Villa");
//         setSelectedState(p.destination || "");
//         setLocation(p.location || "");
//         setRating(p.rating ?? "");
//         setconserlink(p.conserlink || "");
//         setpropertycategory(p.propertycategory || "");
//         setpropertytag(p.propertytag || "");

//         // ── Amenities (stored as comma-joined ids string) ──
//         if (p.addons) {
//           setSelectedAmenities(
//             String(p.addons)
//               .split(",")
//               .map((v) => parseInt(v, 10))
//               .filter((v) => !isNaN(v)),
//           );
//         }

//         // ── Tailored experience ──
//         setTailoredExperience(p.expnamemain || "");
//         setTailoredExperienceContent(p.expcontentmain || "");
//         setTailoredExpData([
//           { img: p.imagegrid1 || null, title: "", content: "" },
//           { img: p.imagegrid2 || null, title: "", content: "" },
//           { img: p.imagegrid3 || null, title: "", content: "" },
//           { img: p.imagegrid4 || null, title: "", content: "" },
//         ]);

//         // ── Merged top carousel ──
//         // Every property's images now live in allPropertyImages, so this is
//         // the same simple fill-in for every property, old or new.
//         const images = Array.isArray(p.images) ? p.images : [];
//         const filledImages = Array(MAX_CARDS).fill(null);
//         const filledCategories = Array(MAX_CARDS).fill("");
//         const filledCrops = Array(MAX_CARDS).fill(null); // ADD THIS

//         images.forEach((img, i) => {
//           if (i >= MAX_CARDS) return;
//           filledImages[i] = { url: img.imageurl, file: null };
//           filledCategories[i] = img.category || "";

//           if (img.cropdata) {
//             try {
//               filledCrops[i] =
//                 typeof img.cropdata === "string"
//                   ? JSON.parse(img.cropdata)
//                   : img.cropdata;
//             } catch (_) {}
//           }
//         });

//         setSelectedImages(filledImages);
//         setSelectedCategories(filledCategories);
//         setSelectedCrops(filledCrops);
//       } catch (err) {
//         console.error("Error loading property:", err);
//         setLoadError(err.message || "Failed to load property");
//       } finally {
//         setIsLoadingProperty(false);
//       }
//     };

//     loadProperty();
//   }, [propertyid]);
//   // Auto-populate cities when state is set (for edit mode)
//   useEffect(() => {
//     if (selectedState && indiaStates.length > 0) {
//       handleStateChange({ target: { value: selectedState } });
//     }
//   }, [selectedState, indiaStates.length]);
//   const handleSubmit = async () => {
//     if (isViewMode) return;
//     setSubmitError("");

//     const missingFields = [];
//     if (!propertyname) missingFields.push("Property Name");
//     if (!propertycode) missingFields.push("Property Code");
//     if (!propertytype) missingFields.push("Property Type");
//     if (!selectedState) missingFields.push("destination (State)");
//     if (!location) missingFields.push("Location (City)");

//     if (missingFields.length > 0) {
//       const fieldList = missingFields.join(", ");
//       toast.error(`Please fill: ${fieldList}`, {
//         // position: "top-center",
//         autoClose: 4000,
//       });
//       return;
//     }

//     const uncategorizedBannerIndex = selectedImages.findIndex(
//       (img, index) => img?.file && !selectedCategories[index],
//     );
//     // if (uncategorizedBannerIndex !== -1) {
//     //   setSubmitError(
//     //     `Please select a category for banner slide ${uncategorizedBannerIndex + 1} before saving.`,
//     //   );
//     //   return;
//     // }

//     setIsSubmitting(true);
//     setUploadProgress(0);
//     setUploadPhase("uploading");
//     try {
//       const formData = new FormData();

//       // ── Simple text fields -> `properties` table ──
//       formData.append("propertyname", propertyname);
//       formData.append("description", description);
//       formData.append("heading", heading);
//       formData.append("content", content);
//       formData.append("catchquote", catchyQuote);
//       formData.append("maplang", maplang);
//       formData.append("maplong", maplong);
//       formData.append("propertycode", propertycode);
//       formData.append("propertyowner", propertyowner);
//       formData.append("airid", airid);
//       formData.append("instagramreellink", instagramreellink);
//       formData.append("propertybrochure", propertybrochure);
//       formData.append("currentprice", bookingPrice);
//       formData.append("defaultprice", defaultprice);
//       formData.append("deposit", deposit);
//       formData.append("maxadult", bookingAdults);
//       formData.append("maxchild", bookingChildren);
//       formData.append("maxbed", bookingRooms);
//       formData.append("maxbath", maxbath);
//       formData.append("maplink", maplink);
//       formData.append("youtubelink", youtubelink);
//       formData.append("conserlink", conserlink);
//       formData.append("seasontype", seasontype.join(","));
//       formData.append("propertytype", propertytype);
//       formData.append("destination", selectedState);
//       formData.append("location", location);
//       formData.append("rating", rating);
//       formData.append("propertycategory", propertycategory);
//       formData.append("propertytag", propertytag);
//       // ── Map / propertyImages text fields ──
//       formData.append("maptitle", maptitle);
//       formData.append("mapcontent", mapcontent);

//       // ── Amenities (ids) ──
//       formData.append("amenities", JSON.stringify(selectedAmenities));

//       // ── Tailored experience ──
//       formData.append("expnamemain", tailoredExperience);
//       formData.append("expcontentmain", tailoredExperienceContent);

//       // ── Merged top carousel ──
//       // allPropertyImages is the source of truth for every property now.
//       // Slides with a fresh upload go through banner_<position>(+_category);
//       // slides that are unchanged (already-saved image, no new file) are
//       // passed back as `existingimages` JSON so the backend keeps them
//       // instead of deleting them when it rebuilds the table.
//       const existingimages = [];
//       selectedImages.forEach((img, index) => {
//         if (!img) return;
//         const position = index + 1;
//         if (img.file) {
//           formData.append(`banner_${position}`, img.file);
//           formData.append(
//             `banner_${position}_category`,
//             selectedCategories[index] || "",
//           );
//           formData.append(
//             `banner_${position}_crop`,
//             selectedCrops[index] ? JSON.stringify(selectedCrops[index]) : "",
//           ); // ADD THIS
//         } else {
//           existingimages.push({
//             imageurl: img.url,
//             position,
//             category: selectedCategories[index] || "",
//             cropdata: selectedCrops[index]
//               ? JSON.stringify(selectedCrops[index])
//               : null,
//           });
//         }
//       });
//       formData.append("existingimages", JSON.stringify(existingimages));

//       // ── Tailored experience images -> `experiences` (imagegrid1..4) ──
//       // New uploads go as files; unchanged ones are passed back as plain URL
//       // strings; cleared ones are sent as an explicit empty string so the
//       // backend can tell "untouched" apart from "deleted" and null the
//       // column on save too (belt-and-suspenders alongside the instant
//       // delete call in handleTailoredexpimageDelete).
//       tailoredExpData.forEach((exp, index) => {
//         if (exp?.file) {
//           formData.append(`expimage${index + 1}`, exp.file);
//         } else {
//           formData.append(`imagegrid${index + 1}`, exp?.img || "");
//         }
//       });

//       const { data } = await axios.put(`${API_URL}/${propertyid}`, formData, {
//         onUploadProgress: (e) => {
//           if (!e.total) return;
//           const pct = Math.round((e.loaded / e.total) * 100);
//           setUploadProgress(pct);
//           // Browser has finished sending bytes; server is now uploading
//           // to Cloudflare and writing to the DB, with no progress signal.
//           if (pct >= 100) setUploadPhase("finalizing");
//         },
//       });

//       if (!data.success) {
//         throw new Error(data.error || "Failed to update property");
//       }

//       setIsSubmitting(false);
//       setUploadPhase("idle");
//       navigate("/Properties");
//     } catch (err) {
//       console.error("Error saving property:", err);
//       const message =
//         err.response?.data?.error ||
//         err.message ||
//         "Something went wrong while saving.";
//       setSubmitError(message);
//       setIsSubmitting(false);
//       setUploadPhase("idle");
//     }
//   };
//   const sensors = useSensors(
//     useSensor(PointerSensor, {
//       activationConstraint: { distance: 5 },
//     }),
//   );

//   const handleDragEnd = (event) => {
//     const { active, over } = event;
//     if (!over || active.id === over.id) return;

//     const oldIndex = parseInt(active.id);
//     const newIndex = parseInt(over.id);

//     setSelectedImages((prev) => {
//       const entries = prev
//         .map((img, i) => ({ img, originalIndex: i }))
//         .filter((e) => e.img !== null);

//       const moved = arrayMove(entries, oldIndex, newIndex);

//       const result = Array(MAX_CARDS).fill(null);
//       moved.forEach((entry, i) => {
//         result[i] = entry.img;
//       });

//       return result;
//     });

//     setSelectedCategories((prev) => {
//       const entries = prev
//         .map((cat, i) => ({ cat, i }))
//         .filter((_, i) => selectedImages[i] !== null);
//       const moved = arrayMove(entries, oldIndex, newIndex);

//       const result = Array(MAX_CARDS).fill("");
//       moved.forEach((entry, i) => {
//         result[i] = entry.cat;
//       });
//       return result;
//     });

//     setSelectedCrops((prev) => {
//       const entries = prev
//         .map((crop, i) => ({ crop, i }))
//         .filter((_, i) => selectedImages[i] !== null);
//       const moved = arrayMove(entries, oldIndex, newIndex);

//       const result = Array(MAX_CARDS).fill(null);
//       moved.forEach((entry, i) => {
//         result[i] = entry.crop;
//       });
//       return result;
//     });
//   };
//   if (isLoadingProperty) {
//     return (
//       <div className="flex items-center justify-center w-full h-screen text-gray-500">
//         Loading property…
//       </div>
//     );
//   }

//   if (loadError) {
//     return (
//       <div className="flex flex-col items-center justify-center w-full h-screen text-red-600 gap-3">
//         <p>{loadError}</p>
//         <button
//           onClick={() => navigate("/Properties")}
//           className="px-4 py-2 bg-gray-800 text-white rounded-lg text-sm"
//         >
//           Back to Properties
//         </button>
//       </div>
//     );
//   }

//   // Every image with a `.file` is a fresh upload (new or replaced) that
//   // was converted to webp at selection time (see toWebpFile) — unchanged
//   // images only carry a `.url` and aren't re-uploaded on save.
//   const totalImagesToUpload =
//     selectedImages.filter((img) => img?.file).length +
//     tailoredExpData.filter((exp) => exp?.file).length;

//   return (
//     <div
//       className={`flex bg-[#F8F9FA] transition-all duration-500 ${
//         isMobile ? "ml-0" : isSidebarOpen ? "w-[82vw]" : "w-[95vw]"
//       }`}
//     >
//       {isSubmitting && (
//         <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
//           <div className="bg-white rounded-xl shadow-lg w-80 p-6 text-center">
//             <svg
//               className="animate-spin h-8 w-8 mx-auto text-green-800"
//               xmlns="http://www.w3.org/2000/svg"
//               fill="none"
//               viewBox="0 0 24 24"
//             >
//               <circle
//                 className="opacity-25"
//                 cx="12"
//                 cy="12"
//                 r="10"
//                 stroke="currentColor"
//                 strokeWidth="4"
//               ></circle>
//               <path
//                 className="opacity-75"
//                 fill="currentColor"
//                 d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//               ></path>
//             </svg>

//             <h3 className="mt-4 font-semibold text-gray-800">
//               {uploadPhase === "finalizing"
//                 ? "Finalizing on server..."
//                 : "Saving property..."}
//             </h3>

//             <p className="text-sm text-gray-500 mt-1">
//               {totalImagesToUpload === 0
//                 ? "No new images to upload"
//                 : `${totalImagesToUpload} image${totalImagesToUpload === 1 ? "" : "s"} ready (converted to webp)`}
//             </p>

//             <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
//               <div
//                 className="bg-green-800 h-2.5 rounded-full transition-all duration-200"
//                 style={{
//                   width: `${uploadPhase === "finalizing" ? 100 : uploadProgress}%`,
//                 }}
//               />
//             </div>

//             <p className="text-xs text-gray-500 mt-2">
//               {uploadPhase === "finalizing"
//                 ? "Uploading to Cloudflare & saving..."
//                 : `Uploading... ${uploadProgress}%`}
//             </p>
//           </div>
//         </div>
//       )}

//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`p-5 lg:p-6 transition-all duration-500 w-full ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <Outlet />

//         {/* Header */}
//         <div className="flex justify-between gap-5 items-start lg:items-center">
//           <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center">
//             <p className="font-semibold text-xl text-gray-700 ml-10 lg:ml-0">
//               Add New Property
//             </p>
//           </div>
//         </div>

//         {/* Data not to be shown in website */}
//         <div className="mt-10">
//           <p className="text-lg text-center mb-6">
//             Required Data <i>(not to be displayed on the website)</i>
//           </p>

//           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 items-center gap-5">
//             <div className="flex flex-col w-full">
//               {/* goes to table properties as propertycode */}
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Property Code <sub className="text-red-500 text-base">*</sub>
//               </label>
//               <input
//                 placeholder="Enter property code"
//                 name="propertycode"
//                 required
//                 value={propertycode}
//                 disabled={isViewMode}
//                 onChange={(e) => setpropertycode(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as propertyowner */}
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Property's Owner name
//               </label>
//               <input
//                 placeholder="Enter Owner's name"
//                 name="propertyowner"
//                 value={propertyowner}
//                 disabled={isViewMode}
//                 onChange={(e) => setpropertyowner(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>
//             <div className="flex flex-col w-full">
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Property ID
//               </label>
//               <input
//                 placeholder="Enter Airbnb ID"
//                 name="airid"
//                 value={airid}
//                 disabled={isViewMode}
//                 onChange={(e) => setairid(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>
//             <div className="flex flex-col w-full">
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Instagram Reel Link
//               </label>
//               <input
//                 placeholder="Enter Instagram Reel Link"
//                 name="instagramreellink"
//                 value={instagramreellink}
//                 disabled={isViewMode}
//                 onChange={(e) => setinstagramreellink(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>
//             <div className="flex flex-col w-full">
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Property Brochure
//               </label>
//               <input
//                 placeholder="Enter Drive Link"
//                 name="propertybrochure"
//                 value={propertybrochure}
//                 disabled={isViewMode}
//                 onChange={(e) => setPropertyBrochure(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as defaultprice */}
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Default Price
//               </label>
//               <input
//                 placeholder="Enter default price"
//                 name="defaultprice"
//                 value={defaultprice}
//                 disabled={isViewMode}
//                 onChange={(e) => setdefaultprice(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as deposit */}

//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Security Deposit
//               </label>
//               <input
//                 placeholder="Enter security deposit"
//                 name="deposit"
//                 value={deposit}
//                 disabled={isViewMode}
//                 onChange={(e) => setDeposit(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as maxadult
//                   Synced with "Guests" (Adults) in the Booking Widget below —
//                   both read/write the same `bookingAdults` state. */}

//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Max Adults
//               </label>
//               <input
//                 type="number"
//                 placeholder="Enter max adults"
//                 name="maxadult"
//                 value={bookingAdults}
//                 disabled={isViewMode}
//                 onChange={(e) => setBookingAdults(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as maxchild
//                   Synced with "Guests" (Children) in the Booking Widget below —
//                   both read/write the same `bookingChildren` state. */}

//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Max Children
//               </label>
//               <input
//                 type="number"
//                 placeholder="Enter max children"
//                 name="maxchild"
//                 value={bookingChildren}
//                 disabled={isViewMode}
//                 onChange={(e) => setBookingChildren(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as maxbed
//                   Synced with "No. of Rooms" in the Booking Widget below —
//                   both read/write the same `bookingRooms` state, so changing
//                   either one updates the other automatically. */}

//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Max Bedroom
//               </label>
//               <input
//                 type="number"
//                 placeholder="Enter max bedroom"
//                 name="maxbed"
//                 value={bookingRooms}
//                 disabled={isViewMode}
//                 onChange={(e) => setBookingRooms(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as maxbath */}

//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Max Bathroom
//               </label>
//               <input
//                 placeholder="Enter max bathroom"
//                 name="maxbath"
//                 value={maxbath}
//                 disabled={isViewMode}
//                 onChange={(e) => setmaxbath(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as maplink */}
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Map Location
//               </label>
//               <input
//                 placeholder="Enter map location"
//                 name="maplink"
//                 value={maplink}
//                 disabled={isViewMode}
//                 onChange={(e) => setMaplink(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div className="flex flex-col w-full">
//               {/* goes to table properties as youtubelink */}
//               <label
//                 for="input"
//                 className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit"
//               >
//                 Youtube Link
//               </label>
//               <input
//                 placeholder="Enter youtube link"
//                 name="youtubelink"
//                 value={youtubelink}
//                 disabled={isViewMode}
//                 onChange={(e) => setYoutubelink(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               />
//             </div>

//             <div
//               className="flex flex-col w-full relative"
//               ref={containerRef_seasontype}
//             >
//               {/* goes to table properties as seasontype */}
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Season Type
//               </label>

//               <button
//                 type="button"
//                 onClick={() => {
//                   if (!isViewMode) {
//                     setIsOpen_seasontype((prev) => !prev);
//                   }
//                 }}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full text-left focus:outline-none focus:ring-0 focus:border-black focus:shadow-md flex justify-between items-center"
//               >
//                 <span
//                   className={seasontype.length ? "text-black" : "text-black/25"}
//                 >
//                   {seasontype.length
//                     ? seasontype.join(", ")
//                     : "Select season type"}
//                 </span>
//                 <span className="text-gray-800">
//                   <IoIosArrowDown />
//                 </span>
//               </button>

//               {isOpen_seasontype && (
//                 <div className="absolute top-full mt-1 left-0 w-full bg-white border border-gray-300 rounded-lg shadow-md z-30 p-2">
//                   {["Summer", "Winter", "Monsoon"].map((season) => (
//                     <label
//                       key={season}
//                       className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-50 cursor-pointer select-none text-sm"
//                     >
//                       <input
//                         type="checkbox"
//                         disabled={isViewMode}
//                         checked={seasontype.includes(season)}
//                         onChange={() => toggleseasontype(season)}
//                         className="accent-black w-4 h-4 cursor-pointer"
//                       />
//                       {season}
//                     </label>
//                   ))}
//                 </div>
//               )}
//             </div>

//             {/* destination (State) */}
//             <div className="flex flex-col w-full">
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Destination<sub className="text-red-500 text-base">*</sub>
//               </label>
//               <select
//                 name="destination"
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                 value={selectedState}
//                 onChange={handleStateChange}
//                 disabled={isViewMode}
//                 required
//               >
//                 <option value="">Select your destination</option>
//                 {indiaStates.map((state) => (
//                   <option key={state.id} value={state.name}>
//                     {state.name}
//                   </option>
//                 ))}
//               </select>
//             </div>

//             {/* Location (City) */}
//             <div className="flex flex-col w-full">
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Select Location <sub className="text-red-500 text-base">*</sub>
//               </label>
//               <select
//                 name="location"
//                 required
//                 value={location}
//                 onChange={(e) => setLocation(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//                 disabled={isViewMode}
//               >
//                 <option value="">Select your location</option>
//                 {cities.map((city) => (
//                   <option key={city.id} value={city.name}>
//                     {city.name}
//                   </option>
//                 ))}
//               </select>
//             </div>
//             {/* Property Type */}
//             <div className="flex flex-col w-full">
//               {/* goes to table properties as propertytype */}
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Property Type<sub className="text-red-500 text-base">*</sub>
//               </label>

//               <select
//                 name="propertytype"
//                 required
//                 value={propertytype}
//                 disabled={isViewMode}
//                 onChange={(e) => setpropertytype(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               >
//                 <option value="Villa">Villa</option>
//                 <option value="Apartment">Apartment</option>
//               </select>
//             </div>

//             {/* Property Category */}
//             <div className="flex flex-col w-full">
//               {/* goes to table properties as propertytype */}
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Property Category
//               </label>

//               <select
//                 name="propertycategory"
//                 value={propertycategory}
//                 disabled={isViewMode}
//                 onChange={(e) => setpropertycategory(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               >
//                 <option value="">Select Category</option>
//                 <option value="Budget Friendly">Budget Friendly</option>
//                 <option value="Luxury">Luxury</option>
//                 <option value="Zorba Select">Zorba Select</option>
//                 {/* <option value="Apartments">Apartments</option> */}
//               </select>
//             </div>

//             {/* Tags Property */}
//             <div className="flex flex-col w-full">
//               {/* goes to table properties as propertytype */}
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Property Tag
//               </label>

//               <select
//                 name="propertytag"
//                 value={propertytag}
//                 disabled={isViewMode}
//                 onChange={(e) => setpropertytag(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               >
//                 <option value="">Select Tag</option>
//                 <option value="Guest Favourite">Guest Favourite</option>
//               </select>
//             </div>

//             {/* Rating */}
//             <div className="flex flex-col w-full">
//               <label className="text-gray-400 text-xs font-semibold relative top-2 ml-2 px-1 bg-[#F8F9FA] w-fit">
//                 Property Rating
//               </label>

//               <select
//                 name="rating"
//                 required
//                 value={rating}
//                 disabled={isViewMode}
//                 onChange={(e) => setRating(e.target.value)}
//                 className="bg-[#F8F9FA] border-gray-400 p-3 text-sm border rounded-lg w-full focus:outline-none placeholder:text-black/25 focus:ring-0 focus:border-black focus:shadow-md"
//               >
//                 <option value="0">Enter Rating</option>
//                 <option value="5">5</option>
//                 <option value="4">4</option>
//                 <option value="3">3</option>
//                 <option value="2">2</option>
//                 <option value="1">1</option>
//                 <option value="0">0</option>
//               </select>
//             </div>
//           </div>
//         </div>

//         <div className="flex gap-3 items-center mx-[10%] md:mx-[25%] my-10 md:my-16 justify-center">
//           <div className="h-[1px] w-full bg-gray-300" />
//           <img
//             src="../assets/zorba_logo.webp"
//             alt="Img"
//             className="h-4 w-auto"
//           />
//           <div className="h-[1px] w-full bg-gray-300" />
//         </div>

//         <div className="my-10">
//           <div className="">
//             <div className="flex flex-col items-center justify-center w-full max-w-3xl mx-auto text-center mt-8">
//               <div className="w-full">
//                 {/* goes to table "properties" as "propertyname" */}
//                 <input
//                   type="text"
//                   name="propertyname"
//                   required
//                   maxLength={50}
//                   value={propertyname}
//                   onChange={(e) => setpropertyname(e.target.value)}
//                   disabled={isViewMode}
//                   className="w-full text-center text-2xl sm:text-3xl font-bold border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                   placeholder="Enter Property Name"
//                 />
//                 <p className="text-xs mt-1 text-center">
//                   {propertyname.length.toString().padStart(2, "0")}/50
//                 </p>
//                 {/* goes to table "properties" as "description" */}
//                 <div className="my-4">
//                   <textarea
//                     rows="2"
//                     name="description"
//                     maxLength={250}
//                     value={description}
//                     onChange={(e) => setDescription(e.target.value)}
//                     disabled={isViewMode}
//                     className="w-full text-center text-gray-700 text-lg border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                     placeholder="Enter Property Description..."
//                   />
//                   <p className="text-xs text-center">
//                     {description.length.toString().padStart(2, "0")}/250
//                   </p>
//                 </div>
//               </div>
//             </div>

//             {/* Banner carousel */}
//             <div className="mt-5 -mx-3 relative" ref={bannerWrapperRef}>
//               {/* Images are stored in the `allPropertyImages` table with:
//     propertyid, imageurl, and position (1, 2, 3, ...)
// */}

//               <Carousel
//                 ref={bannerCarouselRef}
//                 responsive={responsive_BannerCarousel}
//                 autoPlay={false}
//                 autoPlaySpeed={3000}
//                 infinite={false}
//                 keyBoardControl
//                 showDots={false}
//                 customLeftArrow={<CustomLeftArrowBannerCarousel />}
//                 customRightArrow={<CustomRightArrowBannerCarousel />}
//                 afterChange={handleBannerAfterChange}
//               >
//                 {[...Array(MAX_CARDS)].map((_, index) => (
//                   <div key={index} className="mx-3">
//                     <div className="flex flex-col items-center justify-between w-full h-[58vh] bg-white border-dashed border-2 border-gray-400 relative overflow-hidden">
//                       {selectedImages[index] ? (
//                         <>
//                           <div className="absolute inset-0">
//                             <CroppedBanner
//                               imageurl={selectedImages[index]?.url}
//                               cropdata={selectedCrops[index]}
//                             />
//                           </div>
//                           {!isViewMode && (
//                             <div
//                               onClick={() => handleBannerImageDelete(index)}
//                               className="absolute top-1 right-1 bg-white/60 rounded-full p-1 cursor-pointer hover:bg-white text-gray-600 hover:text-red-600 transition"
//                             >
//                               <MdDelete size={18} />
//                             </div>
//                           )}

//                           {!isViewMode && (
//                             <label className="absolute bottom-1 left-2 text-xs font-medium text-gray-600 bg-white/70 px-2 py-0.5 rounded cursor-pointer hover:underline">
//                               <div className="flex gap-1 items-center">
//                                 Replace <FcAddImage size={20} />
//                               </div>
//                               <input
//                                 type="file"
//                                 accept="image/*"
//                                 onChange={(e) => handleImageUpload(e, index)}
//                                 disabled={isViewMode}
//                                 className="hidden"
//                               />
//                             </label>
//                           )}
//                           {!isViewMode && (
//                             <button
//                               type="button"
//                               onClick={() => openCropModal(index)}
//                               className="absolute bottom-1 left-[6.5rem] flex items-center h-6 px-2 text-xs font-medium text-gray-600 bg-white/70 px-2 py-0.5 rounded hover:underline"
//                             >
//                               Adjust
//                             </button>
//                           )}
//                           {/* Per-slide category dropdown (mandatory once this slide has an image) */}
//                           <div className="absolute bottom-14 right-3 z-10 flex flex-col items-end">
//                             <select
//                               name={`bannerCategory_${index}`}
//                               value={selectedCategories[index] || ""}
//                               onChange={(e) =>
//                                 handleBannerCategoryChange(
//                                   index,
//                                   e.target.value,
//                                 )
//                               }
//                               disabled={isViewMode}
//                               className={`text-xs font-medium rounded-md px-2 py-1.5 max-w-[150px] focus:outline-none focus:ring-0 shadow-sm ${
//                                 selectedCategories[index]
//                                   ? "bg-white/90 border border-gray-300 text-gray-800"
//                                   : "bg-red-50/95 border border-red-500 text-red-700"
//                               }`}
//                             >
//                               <option value="">Select category*</option>
//                               {getBannerCategoryOptions().map((cat) => (
//                                 <option key={cat} value={cat}>
//                                   {cat}
//                                 </option>
//                               ))}
//                             </select>
//                           </div>
//                         </>
//                       ) : (
//                         <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
//                           <FcAddImage size={50} />
//                           <input
//                             type="file"
//                             accept="image/*"
//                             onChange={(e) => handleImageUpload(e, index)}
//                             disabled={isViewMode}
//                             className="hidden"
//                           />
//                         </label>
//                       )}

//                       <div className="absolute top-1 left-2 text-sm font-semibold text-black/60 bg-white/70 rounded px-2 py-0.5">
//                         {index + 1}
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </Carousel>

//               <div className="absolute z-20 bottom-2.5 right-6 flex gap-2">
//                 {!isViewMode && (
//                   <label className="px-4 py-[7.5px] bg-green-800 hover:bg-green-700 rounded-md text-white text-xs font-medium shadow-sm transition-colors cursor-pointer whitespace-nowrap">
//                     Upload Multiple
//                     <input
//                       type="file"
//                       accept="image/*"
//                       multiple
//                       onChange={handleBulkImageUpload}
//                       disabled={isViewMode}
//                       className="hidden"
//                     />
//                   </label>
//                 )}
//                 <button
//                   type="button"
//                   onClick={() => setShowGalleryModal(true)}
//                   className="px-4 py-[7.5px] bg-green-800 hover:bg-green-700 rounded-md text-white text-xs font-medium shadow-sm transition-colors whitespace-nowrap"
//                 >
//                   View All
//                 </button>
//               </div>

//               {bannerCategoryError && (
//                 <p className="absolute z-20 bottom-14 right-3 bg-red-600 text-white text-xs px-3 py-1.5 rounded shadow-sm max-w-xs text-right">
//                   {bannerCategoryError}
//                 </p>
//               )}
//             </div>
//           </div>

//           {/* View All Gallery Modal */}
//           {/* {showGalleryModal && (
//             <div
//               className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
//               onClick={() => setShowGalleryModal(false)}
//             >
//               <div
//                 className="bg-white rounded-xl w-full max-w-5xl max-h-[85vh] overflow-y-auto pt-0 p-6 relative"
//                 onClick={(e) => e.stopPropagation()}
//               >
//                 <button
//                   type="button"
//                   onClick={() => setShowGalleryModal(false)}
//                   className="sticky top-3 ml-auto block z-50 text-gray-500 hover:text-black text-xl font-semibold px-2"
//                 >
//                   ✕
//                 </button>

//                 <div className="mb-8">
//                   <h3 className="text-lg font-semibold text-gray-800 mb-1">
//                     All Photos{" "}
//                     <span className="text-sm font-normal text-gray-400">
//                       ({selectedImages.filter(Boolean).length})
//                     </span>
//                   </h3>
//                   <p className="text-xs text-gray-400 mb-3">
//                     Select photos below, choose a category, then click Apply.
//                   </p>

//                   {selectedImages.filter(Boolean).length === 0 ? (
//                     <p className="text-gray-500 text-sm">
//                       No photos added to the carousel yet.
//                     </p>
//                   ) : (
//                     <>
//                       <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
//                         {selectedImages.map((img, index) =>
//                           img ? (
//                             <div
//                               key={index}
//                               onClick={() => toggleGallerySelection(index)}
//                               className={`relative aspect-square rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
//                                 selectedGalleryIndices.includes(index)
//                                   ? "border-green-900 ring-2 ring-green-900/40"
//                                   : "border-gray-200 hover:border-gray-300"
//                               }`}
//                             >
//                               <img
//                                 src={img.url}
//                                 alt={`Photo ${index + 1}`}
//                                 className="w-full h-full object-cover"
//                               />
//                               {selectedCategories[index] && (
//                                 <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">
//                                   {selectedCategories[index]}
//                                 </span>
//                               )}
//                               <div
//                                 className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center border ${
//                                   selectedGalleryIndices.includes(index)
//                                     ? "bg-green-500 border-green-900 text-white"
//                                     : "bg-white/80 border-gray-300"
//                                 }`}
//                               >
//                                 {selectedGalleryIndices.includes(index) && (
//                                   <Check size={12} strokeWidth={3} />
//                                 )}
//                               </div>
//                             </div>
//                           ) : null,
//                         )}
//                       </div>

//                       {selectedGalleryIndices.length > 0 && (
//                         <div className="flex flex-wrap items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3">
//                           <span className="text-sm text-gray-600">
//                             {selectedGalleryIndices.length} selected
//                           </span>
//                           <select
//                             value={bulkCategoryValue}
//                             onChange={(e) =>
//                               setBulkCategoryValue(e.target.value)
//                             }
//                             className="text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:border-black"
//                           >
//                             <option value="">Select category</option>
//                             {getBannerCategoryOptions().map((cat) => (
//                               <option key={cat} value={cat}>
//                                 {cat}
//                               </option>
//                             ))}
//                           </select>
//                           <button
//                             type="button"
//                             onClick={applyBulkCategory}
//                             disabled={!bulkCategoryValue}
//                             className="text-sm bg-green-900 text-white rounded-md px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-700"
//                           >
//                             Apply
//                           </button>
//                           <button
//                             type="button"
//                             onClick={() => setSelectedGalleryIndices([])}
//                             className="text-sm text-gray-500 hover:text-black px-2"
//                           >
//                             Clear
//                           </button>
//                         </div>
//                       )}
//                     </>
//                   )}
//                 </div>

//                 <hr className="border-gray-200 mb-6" />

//                 {getGroupedBannerGallery().length === 0 ? (
//                   <p className="text-gray-500 text-sm">
//                     No images have been assigned a category yet.
//                   </p>
//                 ) : (
//                   getGroupedBannerGallery().map(([category, images]) => (
//                     <div key={category} className="mb-8">
//                       <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
//                         {category}{" "}
//                         <span className="text-sm font-normal text-gray-400">
//                           ({images.length})
//                         </span>
//                       </h3>
//                       <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
//                         {images.map((img) => (
//                           <div
//                             key={img.index}
//                             className="aspect-square rounded-lg overflow-hidden border border-gray-200"
//                           >
//                             <img
//                               src={img.url}
//                               alt={category}
//                               className="w-full h-full object-cover"
//                             />
//                           </div>
//                         ))}
//                       </div>
//                     </div>
//                   ))
//                 )}
//               </div>
//             </div>
//           )} */}
//           {/* View All Gallery Modal */}
//           {showGalleryModal && (
//             <div
//               className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
//               onClick={() => setShowGalleryModal(false)}
//             >
//               <div
//                 className="bg-white rounded-xl w-full max-w-5xl max-h-[85vh] flex flex-col relative overflow-hidden"
//                 onClick={(e) => e.stopPropagation()}
//               >
//                 {/* Scrollable content area */}
//                 <div className="overflow-y-auto p-6 pt-0 flex-1">
//                   <button
//                     type="button"
//                     onClick={() => setShowGalleryModal(false)}
//                     className="sticky top-3 ml-auto block z-50 text-gray-500 hover:text-black text-xl font-semibold px-2"
//                   >
//                     ✕
//                   </button>

//                   {/* ── All Photos: select + bulk-assign category ── */}
//                   <div className="mb-8">
//                     <div className="flex items-center justify-between mb-1">
//                       <h3 className="text-lg font-semibold text-gray-800">
//                         All Photos{" "}
//                         <span className="text-sm font-normal text-gray-400">
//                           ({selectedImages.filter(Boolean).length})
//                         </span>
//                       </h3>
//                     </div>
//                     <p className="text-xs text-gray-400 mb-3">
//                       Select photos below, choose a category, then click Apply.
//                     </p>

//                     {/* Sticky bulk-action footer — only takes up space when something is selected */}
//                     {selectedGalleryIndices.length > 0 && (
//                       <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 bg-gray-50 border-t border-gray-200 w-full px-5 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)] flex-shrink-0">
//                         <span className="text-sm text-gray-600">
//                           {selectedGalleryIndices.length} selected
//                         </span>
//                         <select
//                           value={bulkCategoryValue}
//                           disabled={isViewMode}
//                           onChange={(e) => setBulkCategoryValue(e.target.value)}
//                           className="text-sm border border-gray-300 rounded-md p-2 focus:outline-none focus:border-black"
//                         >
//                           <option value="">Select category</option>
//                           {getBannerCategoryOptions().map((cat) => (
//                             <option key={cat} value={cat}>
//                               {cat}
//                             </option>
//                           ))}
//                         </select>
//                         <button
//                           type="button"
//                           onClick={applyBulkCategory}
//                           disabled={!bulkCategoryValue}
//                           className="text-sm bg-green-900 text-white rounded-md px-3 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-700"
//                         >
//                           Apply
//                         </button>
//                         <button
//                           type="button"
//                           onClick={() => setShowDeleteConfirm(true)}
//                           className="text-sm bg-red-600 text-white rounded-md px-3 py-2 hover:bg-red-700 flex items-center gap-1"
//                         >
//                           <MdDelete size={14} /> Delete Selected
//                         </button>

//                         {allGalleryIndices.length > 0 && (
//                           <button
//                             type="button"
//                             onClick={toggleSelectAllGallery}
//                             className="ml-auto text-sm text-gray-500 hover:text-black px-2"
//                           >
//                             {isAllGallerySelected
//                               ? "Deselect All"
//                               : "Select All"}
//                           </button>
//                         )}
//                         <button
//                           type="button"
//                           onClick={() => setSelectedGalleryIndices([])}
//                           className="text-sm text-gray-500 hover:text-black px-2 mr-3"
//                         >
//                           Clear
//                         </button>
//                       </div>
//                     )}

//                     {selectedImages.filter(Boolean).length === 0 ? (
//                       <p className="text-gray-500 text-sm">
//                         No photos added to the carousel yet.
//                       </p>
//                     ) : (
//                       <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
//                         <DndContext
//                           sensors={sensors}
//                           collisionDetection={closestCenter}
//                           onDragEnd={handleDragEnd}
//                         >
//                           <SortableContext
//                             items={selectedImages
//                               .map((_, idx) => idx.toString())
//                               .filter((_, i) => selectedImages[i] !== null)}
//                             strategy={rectSortingStrategy}
//                           >
//                             {selectedImages.map((img, index) =>
//                               img ? (
//                                 <SortableImage
//                                   key={index}
//                                   img={img}
//                                   index={index}
//                                   isSelected={selectedGalleryIndices.includes(
//                                     index,
//                                   )}
//                                   onToggle={toggleGallerySelection}
//                                   onView={setViewImage}
//                                   category={selectedCategories[index]}
//                                 />
//                               ) : null,
//                             )}
//                           </SortableContext>
//                         </DndContext>
//                       </div>
//                     )}
//                   </div>

//                   <hr className="border-gray-200 mb-6" />

//                   {/* ── Existing grouped-by-category view ── */}
//                   {getGroupedBannerGallery().length === 0 ? (
//                     <p className="text-gray-500 text-sm">
//                       No images have been assigned a category yet.
//                     </p>
//                   ) : (
//                     getGroupedBannerGallery().map(([category, images]) => (
//                       <div key={category} className="mb-8">
//                         <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b border-gray-100 pb-2">
//                           {category}{" "}
//                           <span className="text-sm font-normal text-gray-400">
//                             ({images.length})
//                           </span>
//                         </h3>
//                         <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
//                           {images.map((img) => (
//                             <div
//                               key={img.index}
//                               className="aspect-square rounded-lg overflow-hidden border border-gray-200 relative group"
//                             >
//                               <img
//                                 src={img.url}
//                                 alt={category}
//                                 className="w-full h-full object-cover"
//                               />
//                               <button
//                                 type="button"
//                                 onClick={() =>
//                                   setViewImage({
//                                     url: img.url,
//                                     label: category,
//                                   })
//                                 }
//                                 className="absolute inset-0 bg-black/0 flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:bg-black/30 transition-all"
//                               >
//                                 <Eye size={20} className="text-white" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     ))
//                   )}
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* delete-selected confirmation modal */}
//           {showDeleteConfirm && (
//             <div
//               className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4"
//               onClick={() => setShowDeleteConfirm(false)}
//             >
//               <div
//                 className="bg-white rounded-xl w-full max-w-sm p-6 text-center"
//                 onClick={(e) => e.stopPropagation()}
//               >
//                 <h3 className="text-lg font-semibold text-gray-800 mb-2">
//                   Delete {selectedGalleryIndices.length} image
//                   {selectedGalleryIndices.length === 1 ? "" : "s"}?
//                 </h3>
//                 <p className="text-sm text-gray-500 mb-6">
//                   Any of these already saved to this property will be removed
//                   from Cloudflare and the database right away — this can't be
//                   undone. Freshly-added photos that haven't been saved yet are
//                   just cleared from this form.
//                 </p>
//                 <div className="flex justify-center gap-3">
//                   <button
//                     type="button"
//                     onClick={() => setShowDeleteConfirm(false)}
//                     className="px-5 py-2 rounded-md bg-gray-800 text-white text-sm hover:bg-gray-700"
//                   >
//                     Keep them
//                   </button>
//                   <button
//                     type="button"
//                     onClick={handleDeleteSelectedImages}
//                     className="px-5 py-2 flex items-center gap-2 rounded-md bg-red-600 text-white text-sm hover:bg-black transition"
//                   >
//                     <MdDelete size={14} /> Delete
//                   </button>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* delete-in-progress overlay */}
//           {isDeletingImages && (
//             <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
//               <div className="bg-white rounded-xl shadow-lg w-80 p-6 text-center">
//                 <svg
//                   className="animate-spin h-8 w-8 mx-auto text-red-600"
//                   xmlns="http://www.w3.org/2000/svg"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                 >
//                   <circle
//                     className="opacity-25"
//                     cx="12"
//                     cy="12"
//                     r="10"
//                     stroke="currentColor"
//                     strokeWidth="4"
//                   ></circle>
//                   <path
//                     className="opacity-75"
//                     fill="currentColor"
//                     d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                   ></path>
//                 </svg>
//                 <h3 className="mt-4 font-semibold text-gray-800">
//                   Deleting images...
//                 </h3>
//                 <div className="mt-4 w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
//                   <div
//                     className="bg-red-600 h-2.5 rounded-full transition-all duration-150"
//                     style={{
//                       width: `${
//                         deleteProgress.total
//                           ? (deleteProgress.done / deleteProgress.total) * 100
//                           : 0
//                       }%`,
//                     }}
//                   />
//                 </div>
//                 <p className="text-xs text-gray-500 mt-2">
//                   {deleteProgress.done} of {deleteProgress.total} deleted
//                 </p>
//               </div>
//             </div>
//           )}

//           {/* crop modal */}
//           {cropModalIndex !== null && (
//             <div
//               className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
//               onClick={() => setCropModalIndex(null)}
//             >
//               <div
//                 className="bg-white rounded-xl p-5"
//                 style={{ width: cropBoxSize.width + 40 }}
//                 onClick={(e) => e.stopPropagation()}
//               >
//                 <h3 className="text-sm font-semibold text-gray-800 mb-3">
//                   Adjust image — slide {cropModalIndex + 1}
//                 </h3>

//                 <div
//                   className="relative bg-gray-100 rounded-lg overflow-hidden mx-auto"
//                   style={{
//                     width: cropBoxSize.width,
//                     height: cropBoxSize.height,
//                   }}
//                 >
//                   <Cropper
//                     image={selectedImages[cropModalIndex]?.url}
//                     crop={cropPosition}
//                     zoom={cropZoom}
//                     aspect={bannerAspect}
//                     onCropChange={setCropPosition}
//                     onZoomChange={setCropZoom}
//                     onCropComplete={handleCropComplete}
//                   />
//                 </div>

//                 <div className="flex items-center gap-3 mt-4">
//                   <span className="text-xs text-gray-500">Zoom</span>
//                   <input
//                     type="range"
//                     min={1}
//                     max={3}
//                     step={0.05}
//                     value={cropZoom}
//                     onChange={(e) => setCropZoom(parseFloat(e.target.value))}
//                     disabled={isViewMode}
//                     className="flex-1"
//                   />
//                 </div>

//                 <div className="flex justify-end gap-2 mt-5">
//                   <button
//                     type="button"
//                     onClick={() => setCropModalIndex(null)}
//                     className="text-sm px-4 py-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
//                   >
//                     Cancel
//                   </button>
//                   <button
//                     type="button"
//                     onClick={saveCrop}
//                     className="text-sm px-4 py-2 rounded-md bg-green-900 text-white hover:bg-green-700"
//                   >
//                     Save crop position
//                   </button>
//                 </div>
//               </div>
//             </div>
//           )}

//           {/* View Image Modal */}
//           {viewImage && (
//             <div
//               className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
//               onClick={() => setViewImage(null)}
//             >
//               <div
//                 className="relative max-w-4xl max-h-[90vh]"
//                 onClick={(e) => e.stopPropagation()}
//               >
//                 <button
//                   type="button"
//                   onClick={() => setViewImage(null)}
//                   className="absolute -top-9 right-0 text-white/80 hover:text-white text-xl font-semibold px-2"
//                 >
//                   ✕
//                 </button>
//                 <img
//                   src={viewImage.url}
//                   alt={viewImage.label}
//                   className="max-w-full max-h-[90vh] object-contain rounded-lg"
//                 />
//                 {viewImage.label && (
//                   <p className="text-white/70 text-xs text-center mt-2">
//                     {viewImage.label}
//                   </p>
//                 )}
//               </div>
//             </div>
//           )}
//           <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
//             <div className="lg:col-span-2">
//               <div className="grid grid-cols-1">
//                 <div className="order-1 lg:order-2 mt-5 lg:mt-10 text-left">
//                   {/* goes to table "properties" as "heading" */}
//                   <input
//                     type="text"
//                     maxLength={100}
//                     value={heading}
//                     onChange={(e) => setHeading(e.target.value)}
//                     className="fontPD flex w-full text-left text-2xl md:text-3xl lg:text-4xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                     style={{
//                       letterSpacing: "1px",
//                       wordSpacing: "2px",
//                       lineHeight: 1.3,
//                     }}
//                     disabled={isViewMode}
//                     placeholder="Heading"
//                   />
//                   <p className="text-xs text-left block mt-1">
//                     {heading.length.toString().padStart(2, "0")}/100
//                   </p>

//                   {/* goes to table "properties" as "content" */}
//                   <div className="my-5 lg:my-10">
//                     <textarea
//                       type="text"
//                       rows="3"
//                       maxLength={400}
//                       value={content}
//                       onChange={(e) => setContent(e.target.value)}
//                       disabled={isViewMode}
//                       className="flex w-full text-left border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                       placeholder="Content..."
//                     />
//                     <span className="text-xs block mt-1">
//                       {content.length.toString().padStart(2, "0")}/400
//                     </span>
//                   </div>
//                 </div>
//                 {/* goes to table "properties" as "catchquote" */}

//                 <div className="mt-5 lg:mt-0 order-3">
//                   <input
//                     type="text"
//                     maxLength={100}
//                     value={catchyQuote}
//                     onChange={(e) => setCatchyQuote(e.target.value)}
//                     disabled={isViewMode}
//                     className="flex w-full md:text-xl text-left border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                     placeholder="Catchy Quote"
//                   />
//                   <p className="text-xs text-left block mt-1">
//                     {catchyQuote.length.toString().padStart(2, "0")}/100
//                   </p>

//                   <div className="flex gap-3 items-center mx-[10%] md:mx-[25%] my-5 justify-center">
//                     <div className="h-[1px] w-full bg-gray-300" />
//                     <img
//                       src="../assets/zorba_logo.webp"
//                       alt="Img"
//                       className="h-4 w-auto"
//                     />
//                     <div className="h-[1px] w-full bg-gray-300" />
//                   </div>
//                 </div>
//               </div>
//               {/* Carousel of Amenities Experience */}
//               <div className="mt-5 mb-10" ref={containerRef_amenities}>
//                 <div className="text-center mb-4">
//                   <h2 className="text-2xl font-semibold">Amenities</h2>
//                   <p className="text-gray-500 mt-1">
//                     {selectedAmenities.length} selected
//                   </p>
//                 </div>

//                 <input
//                   type="text"
//                   placeholder="Search amenities..."
//                   className="w-full mb-4 px-4 py-3 border rounded-lg focus:outline-none focus:ring-0 focus:border-[1.5px] focus:border-black"
//                   value={searchTerm}
//                   onFocus={() => setIsOpen_amenities(true)}
//                   onChange={(e) => setSearchTerm(e.target.value)}
//                 />

//                 {isOpen_amenities && (
//                   <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 overflow-y-auto max-h-96">
//                     {filteredAmenities.map((amenity, index) => {
//                       const isSelected = selectedAmenities.includes(amenity.id);

//                       return (
//                         <div
//                           key={index}
//                           onClick={() => {
//                             if (!isViewMode) {
//                               toggleAmenity(amenity.id);
//                             }
//                           }}
//                           className={`relative rounded-xl border px-3 py-5 transition-all duration-200 ${
//                             isViewMode ? "cursor-default" : "cursor-pointer"
//                           } ${
//                             isSelected
//                               ? "border-black shadow-md"
//                               : "border-gray-200 hover:border-gray-400"
//                           }`}
//                         >
//                           {/* Tick */}
//                           {isSelected && (
//                             <div className="absolute top-3 right-3 bg-green-800 text-white rounded-full p-1">
//                               <Check size={16} />
//                             </div>
//                           )}

//                           {/* Icon */}
//                           <img
//                             src={amenity.url}
//                             alt={amenity.name}
//                             className="w-auto h-20 mb-3 object-contain"
//                           />

//                           {/* Text */}
//                           <p className="text-base font-medium">
//                             {amenity.name}
//                           </p>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 )}
//               </div>
//               {/* Wrapping Carousel for all 5 image categories */}
//               {/* Bedroom Carousel — sourced from the banner images assigned to
//                   "Bedroom 1", "Bedroom 2", ... "Bedroom N" categories above. */}
//               <div className="mt-8 mb-5 border border-gray-200 p-4 rounded-xl shadow-sm bg-gray-50 -mx-3 md:mx-0">
//                 {/*
//   Bedroom structure:
//   - One row per bedroom in `allPropertyBedrooms`
//   • propertyid
//   • position = roomIndex + 1

//   - One row per image in `allPropertyBedroomImages`, sourced from the banner
//     images tagged with category "Bedroom {position}"
//   • bedroomID (FK -> allPropertyBedrooms.id)
//   • imageurl
//   • position = imgIndex + 1
//   */}
//                 <Carousel
//                   key={`bedroom-outer-carousel-${parseInt(bookingRooms) || 1}`}
//                   responsive={responsive_OuterCarousel}
//                   autoPlay={false}
//                   infinite={true}
//                   showDots={true}
//                   keyBoardControl={true}
//                   arrows={false}
//                 >
//                   {Array.from({ length: parseInt(bookingRooms) || 1 }).map(
//                     (_, roomIndex) => {
//                       const images = getBedroomImages(roomIndex);
//                       return (
//                         <div key={roomIndex} className="px-4 pb-8">
//                           <h3 className="text-center font-bold text-lg mb-4">
//                             Bedroom {roomIndex + 1} Images{" "}
//                             <span className="text-sm font-normal text-gray-400">
//                               ({images.length})
//                             </span>
//                           </h3>

//                           {images.length === 0 ? (
//                             <div className="flex flex-col items-center justify-center w-full aspect-[4/3] max-h-[350px] bg-white border-dashed border-2 border-gray-300 rounded-lg text-center px-4">
//                               <p className="text-sm text-gray-400">
//                                 No images assigned yet. Add images in the banner
//                                 above and set their category to "Bedroom{" "}
//                                 {roomIndex + 1}".
//                               </p>
//                             </div>
//                           ) : (
//                             <Carousel
//                               key={`bedroom-inner-carousel-${roomIndex}-${images.length}`}
//                               responsive={responsive_ImageCarouselOne}
//                               autoPlay={false}
//                               autoPlaySpeed={3000}
//                               infinite
//                               keyBoardControl
//                               showDots={showDots}
//                               customLeftArrow={<CustomLeftArrowPlain />}
//                               customRightArrow={<CustomRightArrowPlain />}
//                             >
//                               {images.map((img, imgIndex) => (
//                                 <div key={img.bannerIndex} className="mx-3">
//                                   <div className="flex flex-col items-center justify-between w-full aspect-[4/3] max-h-[350px] bg-white border-dashed border-2 border-gray-400 relative overflow-hidden">
//                                     <img
//                                       src={img.url}
//                                       alt={`Bedroom ${roomIndex + 1} - ${imgIndex + 1}`}
//                                       className="w-full h-full object-cover"
//                                     />
//                                     {!isViewMode && (
//                                       <div
//                                         onClick={() =>
//                                           handleBannerImageDelete(
//                                             img.bannerIndex,
//                                           )
//                                         }
//                                         title="Remove this image from the banner"
//                                         className="absolute top-1 right-1 bg-white/60 rounded-full p-1 cursor-pointer hover:bg-white text-gray-600 hover:text-red-600 transition"
//                                       >
//                                         <MdDelete size={18} />
//                                       </div>
//                                     )}
//                                     <div className="absolute bottom-1 right-2 text-sm font-semibold text-black/60 bg-white/70 rounded px-2 py-0.5">
//                                       {imgIndex + 1}
//                                     </div>
//                                   </div>
//                                 </div>
//                               ))}
//                             </Carousel>
//                           )}
//                         </div>
//                       );
//                     },
//                   )}
//                 </Carousel>
//               </div>
//               {/* Map */}
//               <div className="mt-12">
//                 <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-5">
//                   <div>
//                     {/* goes to table "propertyImages" as maptitle */}
//                     <textarea
//                       type="text"
//                       maxLength={50}
//                       value={maptitle}
//                       onChange={(e) => setmaptitle(e.target.value)}
//                       rows={2}
//                       className="w-full text-2xl md:text-3xl lg:text-4xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                       style={{
//                         letterSpacing: "1px",
//                         wordSpacing: "2px",
//                         lineHeight: 1.3,
//                       }}
//                       disabled={isViewMode}
//                       placeholder="Map Title"
//                     />
//                     <p className="text-xs block">
//                       {maptitle.length.toString().padStart(2, "0")}/50
//                     </p>
//                   </div>

//                   <div>
//                     {/* goes to table "propertyImages" as mapcontent */}
//                     <textarea
//                       type="text"
//                       maxLength={150}
//                       value={mapcontent}
//                       onChange={(e) => setmapcontent(e.target.value)}
//                       rows={4}
//                       className="w-full border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                       placeholder="Map Content..."
//                       disabled={isViewMode}
//                     />
//                     <p className="text-xs block">
//                       {mapcontent.length.toString().padStart(2, "0")}/150
//                     </p>
//                   </div>
//                 </div>
//                 {/* goes to table "properties" as maplang  */}
//                 <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       Map Latitude (maplang)
//                     </label>
//                     <input
//                       type="number"
//                       step="0.00000001"
//                       name="maplang"
//                       value={maplang}
//                       onChange={(e) => setmaplang(e.target.value)}
//                       disabled={isViewMode}
//                       placeholder="Enter Latitude"
//                       className="w-full text-2xl md:text-3xl lg:text-xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                     />
//                   </div>
//                   {/* goes to table "properties" as  maplong */}
//                   <div>
//                     <label className="block text-sm font-medium text-gray-700 mb-1">
//                       Map Longitude (maplong)
//                     </label>
//                     <input
//                       type="number"
//                       step="0.00000001"
//                       name="maplong"
//                       value={maplong}
//                       onChange={(e) => setmaplong(e.target.value)}
//                       disabled={isViewMode}
//                       placeholder="Enter Longitude"
//                       className="w-full text-2xl md:text-3xl lg:text-xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>
//             {/* Side StickyCard */}
//             <div className="lg:col-span-1 relative">
//               <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 pb-4 sticky top-10">
//                 <div className="mb-4">
//                   <div className="flex items-center text-4xl font-bold text-[#A23321]">
//                     ₹ {/* goes to table properties as currentprice */}
//                     <input
//                       type="text"
//                       value={bookingPrice}
//                       placeholder="Enter Current Price"
//                       onChange={(e) => setBookingPrice(e.target.value)}
//                       disabled={isViewMode}
//                       className="text-xl w-full bg-transparent border-b border-gray-300 focus:border-black focus:ring-0 px-1 py-0"
//                     />
//                   </div>
//                   {/* <input
//                     type="text"
//                     value={bookingPriceDetails}
//                     onChange={(e) => setBookingPriceDetails(e.target.value)}
//                     className="text-base text-gray-400 w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 px-1 py-0 mt-1"
//                   /> */}
//                 </div>

//                 {/* <div className="grid grid-cols-2 gap-4 mb-4">
//                   <div>
//                     <label className="text-sm text-gray-500 font-medium block mb-1">
//                       Check-in ▾
//                     </label>
//                     <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
//                       <input
//                         type="date"
//                         value={bookingcheckinDate}
//                         onChange={(e) => setBookingcheckinDate(e.target.value)}
//                         className="text-base text-gray-800 font-medium w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0"
//                       />
//                       <input
//                         type="time"
//                         value={bookingcheckintime}
//                         onChange={(e) => setBookingcheckintime(e.target.value)}
//                         className="text-sm text-gray-500 w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0 mt-1"
//                       />
//                     </div>
//                   </div>
//                   <div>
//                     <label className="text-sm text-gray-500 font-medium block mb-1">
//                       Check-out ▾
//                     </label>
//                     <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
//                       <input
//                         type="date"
//                         value={bookingcheckoutDate}
//                         onChange={(e) => setBookingcheckoutDate(e.target.value)}
//                         className="text-base text-gray-800 font-medium w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0"
//                       />
//                       <input
//                         type="time"
//                         value={bookingcheckouttime}
//                         onChange={(e) => setBookingcheckouttime(e.target.value)}
//                         className="text-sm text-gray-500 w-full bg-transparent border-b border-gray-200 focus:border-black focus:ring-0 p-0 mt-1"
//                       />
//                     </div>
//                   </div>
//                 </div> */}

//                 <div className="grid grid-cols-2 gap-4 mb-6">
//                   <div>
//                     <label className="text-sm text-gray-500 font-medium block mb-1">
//                       Guests ▾
//                     </label>
//                     <div className="bg-gray-50 border border-gray-200 rounded-md p-2">
//                       <div className="flex items-center border-b border-gray-200 focus-within:border-black">
//                         {/* goes to table properties as maxadult */}
//                         <input
//                           type="number"
//                           value={bookingAdults}
//                           disabled={isViewMode}
//                           onChange={(e) => setBookingAdults(e.target.value)}
//                           className="text-base text-gray-800 font-medium w-12 bg-transparent border-none focus:ring-0 p-0"
//                         />
//                         <span className="text-base text-gray-800 font-medium ml-1">
//                           Adults,
//                         </span>
//                       </div>
//                       <div className="flex items-center border-b border-gray-200 mt-1 focus-within:border-black">
//                         {/* goes to table properties as maxchild */}
//                         <input
//                           type="number"
//                           value={bookingChildren}
//                           disabled={isViewMode}
//                           onChange={(e) => setBookingChildren(e.target.value)}
//                           className="text-base text-gray-800 font-medium w-12 bg-transparent border-none focus:ring-0 p-0"
//                         />
//                         <span className="text-base text-gray-800 font-medium ml-1">
//                           Children
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                   <div>
//                     <label className="text-sm text-gray-500 font-medium block mb-1">
//                       No. of Rooms ▾
//                     </label>
//                     <div className="bg-gray-50 border border-gray-200 rounded-md p-3 text-center">
//                       <div className="flex items-center justify-center border-b border-gray-200 focus-within:border-black">
//                         {/* goes to table properties as maxbed
//                             Synced with "Max Bedroom" in the Required Data
//                             section above — same `bookingRooms` state. */}
//                         <input
//                           type="number"
//                           value={bookingRooms}
//                           disabled={isViewMode}
//                           onChange={(e) => setBookingRooms(e.target.value)}
//                           className="text-base text-gray-800 font-medium w-12 text-right bg-transparent border-none focus:ring-0 p-0"
//                         />
//                         <span className="text-base text-gray-800 font-medium ml-2">
//                           Rooms
//                         </span>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <hr className="border-gray-200 mb-4" />

//                 <div className="flex justify-between items-center mb-0">
//                   <p className="text-2xl font-bold text-gray-800">Total</p>
//                   <div className="text-right">
//                     <div className="flex items-center justify-end text-2xl font-bold text-[#A23321]">
//                       {bookingPrice && !isNaN(bookingPrice) ? (
//                         <>
//                           ₹{" "}
//                           {(parseFloat(bookingPrice) * 1.18).toLocaleString(
//                             "en-IN",
//                             {
//                               minimumFractionDigits: 2,
//                               maximumFractionDigits: 2,
//                             },
//                           )}
//                         </>
//                       ) : (
//                         "N/A"
//                       )}
//                     </div>
//                     <p className="text-sm text-gray-400 mt-1">(Incl. taxes)</p>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           {/* Break */}
//           <div className="flex gap-3 items-center mx-5 md:mx-[5%] my-8 md:my-14 justify-center">
//             <div className="h-[1px] w-full bg-gray-300" />
//             <img
//               src="../assets/zorba_logo.webp"
//               alt="Img"
//               className="h-4 w-auto"
//             />
//             <div className="h-[1px] w-full bg-gray-300" />
//           </div>
//           {/* Break over  */}

//           {/* Tailored Experience */}
//           <div className="">
//             <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-5">
//               <div>
//                 {/* goes to table "experiences" as "expnamemain" */}
//                 <input
//                   type="text"
//                   placeholder="Experience"
//                   maxLength={50}
//                   value={tailoredExperience}
//                   disabled={isViewMode}
//                   onChange={(e) => setTailoredExperience(e.target.value)}
//                   className="w-full text-2xl md:text-3xl lg:text-4xl border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                   style={{
//                     letterSpacing: "1px",
//                     wordSpacing: "2px",
//                     lineHeight: 1.3,
//                   }}
//                 />
//                 <p className="text-xs block mt-1">
//                   {tailoredExperience.length.toString().padStart(2, "0")}/50
//                 </p>

//                 {/* goes to table "experiences" as "expcontentmain" */}
//               </div>

//               <div className="space-y-5">
//                 <div>
//                   <textarea
//                     type="text"
//                     rows={3}
//                     placeholder="Content..."
//                     value={tailoredExperienceContent}
//                     onChange={(e) =>
//                       setTailoredExperienceContent(e.target.value)
//                     }
//                     maxLength={150}
//                     disabled={isViewMode}
//                     className="w-full border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                   />
//                   <p className="text-xs block">
//                     {tailoredExperienceContent.length
//                       .toString()
//                       .padStart(2, "0")}
//                     /150
//                   </p>
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Concierge Brochure
//                   </label>
//                   <input
//                     type="text"
//                     name="conserlink"
//                     placeholder="Enter PDF Link"
//                     value={conserlink}
//                     disabled={isViewMode}
//                     onChange={(e) => setconserlink(e.target.value)}
//                     className="w-full border-b border-l-0 border-r-0 border-t-0 focus:ring-0 focus:border-b-2 focus:border-black placeholder:text-gray-400"
//                   />
//                 </div>
//               </div>
//             </div>

//             {/* Carousel of Tailored Experience */}
//             <div className="mt-5 -mx-3">
//               {/* goes to table "experiences" as "imagegrid1	, imagegrid2, imagegrid3 and imagegrid4" */}
//               <Carousel
//                 ref={carouselRef}
//                 responsive={responsive_TailoredExpCarousel}
//                 autoPlay={false}
//                 autoPlaySpeed={3000}
//                 infinite
//                 keyBoardControl
//                 showDots={showDots}
//                 customLeftArrow={<CustomLeftArrowTailoredExpCarousel />}
//                 customRightArrow={<CustomRightArrowTailoredExpCarousel />}
//                 beforeChange={(nextSlide) => setActiveCardIndex(nextSlide)}
//               >
//                 {[...Array(MAX_CARDS_Exp)].map((_, index) => (
//                   <div key={index} className="mx-3">
//                     <div className="relative w-full h-[350px] bg-white border-2 border-dashed border-gray-400 overflow-hidden">
//                       {tailoredExpData[index]?.img ? (
//                         <>
//                           <img
//                             src={tailoredExpData[index].img}
//                             alt="Img"
//                             className="w-full h-full object-cover"
//                           />

//                           {!isViewMode && (
//                             <div
//                               onClick={() =>
//                                 handleTailoredexpimageDelete(index)
//                               }
//                               className="absolute top-1 right-1 bg-white/60 rounded-full p-1 cursor-pointer hover:bg-white text-gray-600 hover:text-red-600 transition"
//                             >
//                               <MdDelete size={18} />
//                             </div>
//                           )}
//                           {!isViewMode && (
//                             <label className="absolute bottom-1 left-2 text-xs font-medium text-gray-600 bg-white/70 px-2 py-0.5 rounded cursor-pointer hover:underline">
//                               <div className="flex gap-1 items-center">
//                                 Replace <FcAddImage size={20} />
//                               </div>
//                               <input
//                                 type="file"
//                                 accept="image/*"
//                                 onChange={async (e) => {
//                                   const file = e.target.files[0];
//                                   e.target.value = "";
//                                   if (!file) return;
//                                   let webpFile;
//                                   try {
//                                     webpFile = await toWebpFile(file);
//                                   } catch (err) {
//                                     return;
//                                   }
//                                   const newData = [...tailoredExpData];
//                                   newData[index].img =
//                                     URL.createObjectURL(webpFile); // preview
//                                   newData[index].file = webpFile; // actual File for backend
//                                   setTailoredExpData(newData);
//                                 }}
//                                 disabled={isViewMode}
//                                 className="hidden"
//                               />
//                             </label>
//                           )}
//                         </>
//                       ) : (
//                         <label className="flex flex-col items-center justify-center w-full h-full cursor-pointer">
//                           <FcAddImage size={50} />
//                           <input
//                             type="file"
//                             accept="image/*"
//                             onChange={async (e) => {
//                               const file = e.target.files[0];
//                               e.target.value = "";
//                               if (!file) return;
//                               let webpFile;
//                               try {
//                                 webpFile = await toWebpFile(file);
//                               } catch (err) {
//                                 return;
//                               }
//                               const newData = [...tailoredExpData];
//                               newData[index].img =
//                                 URL.createObjectURL(webpFile);
//                               newData[index].file = webpFile;
//                               setTailoredExpData(newData);
//                             }}
//                             disabled={isViewMode}
//                             className="hidden"
//                           />
//                         </label>
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </Carousel>

//               <div className="flex gap-5 items-center justify-center md:hidden text-sm text-gray-400 mt-3">
//                 <FaChevronLeft
//                   onClick={handlePrevSlide}
//                   className="cursor-pointer"
//                 />
//                 Showing {(activeCardIndex % TailoredExpCarousel.length) + 1} /
//                 {TailoredExpCarousel.length}
//                 <FaChevronRight
//                   onClick={handleNextSlide}
//                   className="cursor-pointer"
//                 />
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="mt-14 mb-5">
//           {submitError && (
//             <p className="text-red-600 text-sm mt-4">{submitError}</p>
//           )}
//           {!isViewMode && (
//             <div className="grid grid-cols-2 gap-3 mt-10 w-72 mx-auto">
//               <button
//                 type="button"
//                 onClick={() => navigate("/Properties")}
//                 className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//               >
//                 Cancel
//               </button>

//               <button
//                 type="button"
//                 onClick={handleSubmit}
//                 disabled={isSubmitting}
//                 className="px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
//               >
//                 {isSubmitting ? "Saving..." : "Save"}
//               </button>
//             </div>
//           )}
//         </div>
//       </main>
//     </div>
//   );
// };

// export default EditPropertyDetailElementor;
